import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';

// ═══════════════════════════════════════════════════════════════════════════
// NAME CONSISTENCY ENGINE — DISPLAY LAYER (FIXED)
// 1. Removed brittle regex auto-detection.
// 2. Implemented single-pass replacement regex to prevent "stuttering" error.
// 3. Relies solely on explicit candidate names from Phase 02 data and
//    known placeholder phrases.
// ═══════════════════════════════════════════════════════════════════════════

const PLACEHOLDER_PHRASES = ['this brand', 'the brand', 'our brand'];

function buildReplacer(brand, selectedName) {
  if (!selectedName) return null;
  const selLower = selectedName.toLowerCase();

  // 1. Get candidate names from Phase 02 data ONLY.
  // Filter out empty names and the currently selected name itself to avoid self-replacement.
  const candidates = (brand.phase02?.names || [])
    .map(n => n?.name)
    .filter(n => n && typeof n === 'string' && n.toLowerCase() !== selLower);

  // deduplicate
  const uniqueCandidates = [...new Set(candidates)];

  // 2. Combine candidates AND placeholder phrases into one target list
  const allTargets = [...uniqueCandidates, ...PLACEHOLDER_PHRASES];

  if (allTargets.length === 0) return null;

  // 3. Escape regex special characters in all targets
  const escaped = allTargets.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

  // 4. Create single master regex
  // \b ensures whole word matches. gi for global case-insensitive.
  // This single pass prevents "stuttering" replacements.
  return new RegExp(`\\b(${escaped.join('|')})\\b`, 'gi');
}

function fixText(text, replacer, selectedName) {
  if (!text || typeof text !== 'string') return text || '';
  // Single pass replacement using the master regex
  return replacer ? text.replace(replacer, selectedName) : text;
}

function fixObj(obj, replacer, selectedName) {
  if (!obj || !replacer) return obj;
  if (typeof obj === 'string') return fixText(obj, replacer, selectedName);
  if (Array.isArray(obj)) return obj.map(i => fixObj(i, replacer, selectedName));
  if (typeof obj === 'object' && obj !== null) {
    const out = {};
    for (const [k, v] of Object.entries(obj)) {
      // skip replacing keys, only values
      out[k] = fixObj(v, replacer, selectedName);
    }
    return out;
  }
  return obj;
}

// ═══════════════════════════════════════════════════════════════════════════
// EXPORT HELPERS
// ═══════════════════════════════════════════════════════════════════════════

function flattenObj(obj, indent = '') {
  if (obj == null) return '';
  if (['string', 'number', 'boolean'].includes(typeof obj)) return String(obj) + '\n';
  if (Array.isArray(obj)) return obj.map((item, i) =>
    typeof item === 'object' && item !== null
      ? `\n${indent}[${i + 1}]\n${flattenObj(item, indent + '  ')}`
      : `${indent}• ${item}\n`
  ).join('');
  return Object.entries(obj).map(([k, v]) => {
    // formatting camelCase keys to Title Case labels
    const label = k.replace(/([A-Z])/g, ' $1').trim().replace(/^./, s => s.toUpperCase());
    return typeof v === 'object' && v !== null
      ? `${indent}${label}:\n${flattenObj(v, indent + '  ')}`
      : `${indent}${label}: ${v}\n`;
  }).join('');
}

function triggerDownload(text, filename) {
  const blob = new Blob([text], { type: 'text/plain; charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}



// ═══════════════════════════════════════════════════════════════════════════
// PITCH DECK GENERATOR — Client-side PptxGenJS
// Loads PptxGenJS from CDN dynamically. No server needed.
// Brand colors, name, and data flow from the confirmed brand session.
// ═══════════════════════════════════════════════════════════════════════════

async function generateCompanyProfile(brand, selectedName, brief, logoUrl) {
  if (!window.PptxGenJS) {
    await new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/pptxgenjs@3.12.0/dist/pptxgen.bundle.js';
      script.onload = resolve;
      script.onerror = () => reject(new Error('PptxGenJS failed to load'));
      document.head.appendChild(script);
    });
  }

  const hx = (hex) => (hex || '#050C1A').replace('#', '');

  const p00 = brand.phase00 || {};
  const p01 = brand.phase01 || {};
  const p03 = brand.phase03 || {};
  const p04 = brand.phase04 || {};
  const p05 = brand.phase05 || {};
  const p08 = brand.phase08 || {};
  const p10 = brand.phase10 || {};

  const colorVals = Object.values(p03.colors || {});
  const C1    = hx(colorVals[0]?.hex || '#1A6AFF');
  const C2    = hx(colorVals[1]?.hex || '#06B6D4');
  const DARK  = hx(colorVals[0]?.hex ? colorVals[0].hex : '#050C1A');
  const WHITE = 'FFFFFF';
  const MUTED = '6B7280';
  const LIGHT = 'F8FAFF';
  const SMOKE = 'F3F4F6';

  const pptx = new window.PptxGenJS();
  pptx.layout = 'LAYOUT_16x9';
  pptx.title  = `${selectedName} — Company Profile`;
  pptx.author = 'Brandostro';

  // ── Helper: Section slide header ──
  const addHeader = (slide, title, dark = false) => {
    if (dark) {
      slide.background = { color: DARK };
      slide.addShape(pptx.shapes.RECTANGLE, { x:0, y:0, w:10, h:0.06, fill:{ color:C1 } });
      slide.addShape(pptx.shapes.RECTANGLE, { x:0, y:5.565, w:10, h:0.06, fill:{ color:C2 } });
      slide.addText(title, { x:0.55, y:0.3, w:9, h:0.7, fontSize:28, fontFace:'Arial', color:WHITE, bold:true });
    } else {
      slide.background = { color: WHITE };
      slide.addShape(pptx.shapes.RECTANGLE, { x:0, y:0, w:10, h:0.06, fill:{ color:C1 } });
      slide.addShape(pptx.shapes.RECTANGLE, { x:0, y:5.565, w:10, h:0.06, fill:{ color:C2 } });
      slide.addText(title, { x:0.55, y:0.3, w:7, h:0.7, fontSize:28, fontFace:'Arial', color:'111827', bold:true });
      slide.addText(selectedName, { x:8, y:0.38, w:1.8, h:0.5, fontSize:10, fontFace:'Arial', color:MUTED, align:'right' });
    }
  };

  // ── SLIDE 1: COVER ──────────────────────────────────────────────────────
  const s1 = pptx.addSlide();
  s1.background = { color: DARK };
  // Left accent bar
  s1.addShape(pptx.shapes.RECTANGLE, { x:0, y:0, w:0.06, h:5.625, fill:{ color:C1 } });
  // Bottom accent
  s1.addShape(pptx.shapes.RECTANGLE, { x:0, y:5.565, w:10, h:0.06, fill:{ color:C2 } });
  // Large brand name
  s1.addText(selectedName, {
    x:0.4, y:1.2, w:9, h:1.6,
    fontSize:64, fontFace:'Arial', color:WHITE, bold:true, valign:'bottom'
  });
  // Divider line
  s1.addShape(pptx.shapes.RECTANGLE, { x:0.4, y:3.0, w:1.2, h:0.04, fill:{ color:C2 } });
  // Tagline
  if (p04.taglines?.[0]?.line) {
    s1.addText(p04.taglines[0].line, {
      x:0.4, y:3.2, w:8, h:0.7,
      fontSize:20, fontFace:'Arial', color:C2, italic:true
    });
  }
  // Company profile label
  s1.addText('COMPANY PROFILE', {
    x:0.4, y:4.2, w:4, h:0.35,
    fontSize:10, fontFace:'Arial', color:MUTED, charSpacing:4
  });
  // Powered by
  s1.addText('Powered by Brandostro', {
    x:0.4, y:5.2, w:5, h:0.25,
    fontSize:8, fontFace:'Arial', color:'374151'
  });

  // ── SLIDE 2: WHO WE ARE ─────────────────────────────────────────────────
  const s2 = pptx.addSlide();
  addHeader(s2, '01  Who We Are');
  // Sub label
  s2.addText('THE IDENTITY', { x:0.55, y:1.15, w:9, h:0.28, fontSize:9, fontFace:'Arial', color:C2, charSpacing:5 });
  // Manifesto as the identity statement
  s2.addText(p00.manifesto || `${selectedName} is a purpose-driven organisation built to create meaningful change.`, {
    x:0.55, y:1.55, w:8.5, h:2.4,
    fontSize:17, fontFace:'Arial', color:'111827', lineSpacingMultiple:1.7, italic:true
  });
  // Origin story
  if (p00.origin) {
    s2.addShape(pptx.shapes.RECTANGLE, { x:0.55, y:4.1, w:8.9, h:1.0, fill:{ color:C1, transparency:93 }, line:{ color:C1, width:1 } });
    s2.addText('OUR STORY', { x:0.75, y:4.18, w:3, h:0.25, fontSize:8, fontFace:'Arial', color:C1, charSpacing:4 });
    s2.addText(p00.origin, { x:0.75, y:4.45, w:8.5, h:0.6, fontSize:12, fontFace:'Arial', color:'374151', lineSpacingMultiple:1.4 });
  }

  // ── SLIDE 3: MISSION & VALUES ───────────────────────────────────────────
  const s3 = pptx.addSlide();
  addHeader(s3, '02  Mission & Values', true);
  s3.addText('THE WHY', { x:0.55, y:1.15, w:9, h:0.28, fontSize:9, fontFace:'Arial', color:C2, charSpacing:5 });
  // Mission
  s3.addText('MISSION', { x:0.55, y:1.55, w:4.2, h:0.28, fontSize:9, fontFace:'Arial', color:MUTED, charSpacing:4 });
  s3.addText(p01.mission || '', { x:0.55, y:1.9, w:4.2, h:1.1, fontSize:14, fontFace:'Arial', color:'D1D5DB', lineSpacingMultiple:1.55 });
  // Vision
  s3.addText('VISION', { x:5.3, y:1.55, w:4.2, h:0.28, fontSize:9, fontFace:'Arial', color:MUTED, charSpacing:4 });
  s3.addText(p01.vision || '', { x:5.3, y:1.9, w:4.2, h:1.1, fontSize:14, fontFace:'Arial', color:'D1D5DB', lineSpacingMultiple:1.55 });
  // Divider
  s3.addShape(pptx.shapes.RECTANGLE, { x:0.55, y:3.2, w:8.9, h:0.02, fill:{ color:'1F2937' } });
  // Values
  s3.addText('CORE VALUES', { x:0.55, y:3.35, w:9, h:0.28, fontSize:9, fontFace:'Arial', color:MUTED, charSpacing:4 });
  (p01.values || []).slice(0, 4).forEach((v, i) => {
    const vx = 0.55 + i * 2.35;
    s3.addShape(pptx.shapes.RECTANGLE, { x:vx, y:3.75, w:2.1, h:1.4, fill:{ color:'111827' }, line:{ color:'1F2937', width:1 } });
    s3.addText(v.name || '', { x:vx+0.12, y:3.85, w:1.9, h:0.4, fontSize:11, fontFace:'Arial', color:C2, bold:true });
    s3.addText(v.definition || '', { x:vx+0.12, y:4.3, w:1.9, h:0.75, fontSize:9, fontFace:'Arial', color:'9CA3AF', lineSpacingMultiple:1.3 });
  });

  // ── SLIDE 4: WHAT WE DO ─────────────────────────────────────────────────
  const s4 = pptx.addSlide();
  addHeader(s4, '03  What We Do');
  s4.addText('THE CORE', { x:0.55, y:1.15, w:9, h:0.28, fontSize:9, fontFace:'Arial', color:C2, charSpacing:5 });
  // Offerings as main content
  s4.addText(p01.offerings || '', {
    x:0.55, y:1.55, w:5.5, h:1.4,
    fontSize:16, fontFace:'Arial', color:'111827', lineSpacingMultiple:1.65
  });
  // Problem we solve
  if (p00.problem) {
    s4.addText('THE PROBLEM WE SOLVE', { x:0.55, y:3.15, w:5.5, h:0.28, fontSize:9, fontFace:'Arial', color:MUTED, charSpacing:4 });
    s4.addText(p00.problem, { x:0.55, y:3.5, w:5.5, h:1.0, fontSize:13, fontFace:'Arial', color:'374151', lineSpacingMultiple:1.5 });
  }
  // Right column: The change we create
  s4.addShape(pptx.shapes.RECTANGLE, { x:6.5, y:1.15, w:3.05, h:4.1, fill:{ color:C1 } });
  s4.addText('THE CHANGE\nWE CREATE', { x:6.65, y:1.35, w:2.75, h:0.7, fontSize:9, fontFace:'Arial', color:'93C5FD', charSpacing:3, lineSpacingMultiple:1.4 });
  s4.addText(p00.change || '', { x:6.65, y:2.15, w:2.75, h:2.5, fontSize:14, fontFace:'Arial', color:WHITE, italic:true, lineSpacingMultiple:1.6 });

  // ── SLIDE 5: WHY CHOOSE US ──────────────────────────────────────────────
  const s5 = pptx.addSlide();
  addHeader(s5, '04  Why Choose Us', true);
  s5.addText('THE EDGE', { x:0.55, y:1.15, w:9, h:0.28, fontSize:9, fontFace:'Arial', color:C2, charSpacing:5 });
  // Win statement large
  s5.addText(`"${p05.winStatement || ''}"`, {
    x:0.55, y:1.55, w:9, h:1.5,
    fontSize:19, fontFace:'Arial', color:WHITE, italic:true, lineSpacingMultiple:1.55
  });
  // Competitors as differentiation proof
  s5.addText('COMPETITIVE ADVANTAGE', { x:0.55, y:3.3, w:9, h:0.28, fontSize:9, fontFace:'Arial', color:MUTED, charSpacing:4 });
  (p05.competitors || []).slice(0, 3).forEach((c, i) => {
    s5.addShape(pptx.shapes.RECTANGLE, { x:0.55 + i*3.15, y:3.7, w:2.95, h:1.5, fill:{ color:'111827' }, line:{ color:'1F2937', width:1 } });
    s5.addText(c.name || '', { x:0.7 + i*3.15, y:3.82, w:2.65, h:0.35, fontSize:12, fontFace:'Arial', color:C1, bold:true });
    s5.addText(c.ourPosition || '', { x:0.7 + i*3.15, y:4.22, w:2.65, h:0.9, fontSize:10, fontFace:'Arial', color:'9CA3AF', lineSpacingMultiple:1.35 });
  });

  // ── SLIDE 6: OUR IMPACT ─────────────────────────────────────────────────
  const s6 = pptx.addSlide();
  addHeader(s6, '05  Our Impact');
  s6.addText('THE 2026 STANDARD', { x:0.55, y:1.15, w:9, h:0.28, fontSize:9, fontFace:'Arial', color:C2, charSpacing:5 });
  // Brand belief as impact statement
  s6.addText(p00.belief || '', {
    x:0.55, y:1.55, w:5.8, h:1.2,
    fontSize:16, fontFace:'Arial', color:'111827', lineSpacingMultiple:1.65
  });
  // Whitespace score as market impact indicator
  const score = String(p05.whitespaceScore || '—');
  s6.addShape(pptx.shapes.RECTANGLE, { x:6.8, y:1.15, w:2.75, h:2.2, fill:{ color:C1, transparency:90 }, line:{ color:C1, width:1 } });
  s6.addText(score, { x:6.8, y:1.25, w:2.75, h:1.4, fontSize:72, fontFace:'Arial', color:C1, bold:true, align:'center', valign:'middle' });
  s6.addText('MARKET WHITESPACE', { x:6.8, y:2.75, w:2.75, h:0.4, fontSize:8, fontFace:'Arial', color:MUTED, align:'center', charSpacing:2 });
  // Opportunities from SWOT
  if ((p05.swot?.opportunities || []).length > 0) {
    s6.addText('KEY OPPORTUNITIES', { x:0.55, y:3.05, w:5.8, h:0.28, fontSize:9, fontFace:'Arial', color:MUTED, charSpacing:4 });
    p05.swot.opportunities.slice(0, 3).forEach((o, i) => {
      s6.addShape(pptx.shapes.RECTANGLE, { x:0.55, y:3.45 + i*0.5, w:0.04, h:0.3, fill:{ color:C2 } });
      s6.addText(o, { x:0.75, y:3.42 + i*0.5, w:5.5, h:0.35, fontSize:12, fontFace:'Arial', color:'374151' });
    });
  }

  // ── SLIDE 7: LEADERSHIP TEAM ────────────────────────────────────────────
  const s7 = pptx.addSlide();
  addHeader(s7, '06  Leadership Team', true);
  s7.addText('THE FACES', { x:0.55, y:1.15, w:9, h:0.28, fontSize:9, fontFace:'Arial', color:C2, charSpacing:5 });
  s7.addText('The people steering', { x:0.55, y:1.5, w:9, h:0.5, fontSize:18, fontFace:'Arial', color:WHITE, italic:true });
  s7.addText(selectedName, { x:0.55, y:1.95, w:9, h:0.5, fontSize:18, fontFace:'Arial', color:C2, bold:true });
  // Placeholder leader cards
  const roles = ['Founder & CEO', 'Chief Strategy Officer', 'Chief Operations Officer'];
  roles.forEach((role, i) => {
    const cx = 0.55 + i * 3.15;
    s7.addShape(pptx.shapes.RECTANGLE, { x:cx, y:2.7, w:2.95, h:2.5, fill:{ color:'111827' }, line:{ color:'1F2937', width:1 } });
    // Avatar circle
    s7.addShape(pptx.shapes.OVAL, { x:cx+0.9, y:2.85, w:1.15, h:1.15, fill:{ color:C1, transparency:80 }, line:{ color:C1, width:1.5 } });
    s7.addText('?', { x:cx+0.9, y:2.85, w:1.15, h:1.15, fontSize:28, fontFace:'Arial', color:C1, align:'center', valign:'middle', bold:true });
    s7.addText('Your Name', { x:cx+0.12, y:4.1, w:2.7, h:0.35, fontSize:12, fontFace:'Arial', color:WHITE, bold:true, align:'center' });
    s7.addText(role, { x:cx+0.12, y:4.5, w:2.7, h:0.5, fontSize:9, fontFace:'Arial', color:MUTED, align:'center', lineSpacingMultiple:1.3 });
  });
  s7.addText('Update this slide with your actual leadership team details.', {
    x:0.55, y:5.22, w:8.9, h:0.28, fontSize:8, fontFace:'Arial', color:'374151', italic:true, align:'center'
  });

  // ── SLIDE 8: GET IN TOUCH ───────────────────────────────────────────────
  const s8 = pptx.addSlide();
  s8.background = { color: C1 };
  s8.addShape(pptx.shapes.RECTANGLE, { x:0, y:0, w:10, h:0.06, fill:{ color:'FFFFFF', transparency:80 } });
  s8.addShape(pptx.shapes.RECTANGLE, { x:0, y:5.565, w:10, h:0.06, fill:{ color:C2 } });
  s8.addText('07  Get In Touch', { x:0.55, y:0.3, w:9, h:0.6, fontSize:26, fontFace:'Arial', color:WHITE, bold:true });
  s8.addText('THE ACTION', { x:0.55, y:1.05, w:9, h:0.28, fontSize:9, fontFace:'Arial', color:'BFDBFE', charSpacing:5 });
  s8.addText(`We would love to hear from you.`, {
    x:0.55, y:1.45, w:9, h:0.7, fontSize:22, fontFace:'Arial', color:WHITE, italic:true
  });
  // Contact details
  const safeName2 = selectedName.toLowerCase().replace(/\s+/g,'');
  [
    { label:'EMAIL', value:`hello@${safeName2}.com` },
    { label:'WEBSITE', value:`${safeName2}.com` },
    { label:'ENQUIRIES', value:`Let's build something great together.` },
  ].forEach((c, i) => {
    s8.addText(c.label, { x:0.55, y:2.5 + i*0.75, w:2, h:0.28, fontSize:8, fontFace:'Arial', color:'BFDBFE', charSpacing:4 });
    s8.addText(c.value, { x:2.7, y:2.5 + i*0.75, w:6.5, h:0.35, fontSize:15, fontFace:'Arial', color:WHITE });
  });
  // Positioning as closing thought
  if (p04.positioning) {
    s8.addShape(pptx.shapes.RECTANGLE, { x:0.55, y:4.4, w:8.9, h:0.9, fill:{ color:'FFFFFF', transparency:88 } });
    s8.addText(`"${p04.positioning}"`, {
      x:0.7, y:4.45, w:8.6, h:0.8, fontSize:13, fontFace:'Arial', color:WHITE, italic:true, valign:'middle'
    });
  }
  s8.addText('Powered by Brandostro', { x:0.55, y:5.25, w:9, h:0.22, fontSize:8, fontFace:'Arial', color:'BFDBFE', align:'right' });

  await pptx.writeFile({ fileName: `${selectedName.replace(/\s+/g, '-')}-company-profile.pptx` });
}


// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

const PHASES = [
  { id:'p02', num:'00', title:'Nomenclature',          tag:'Your Name',       dataKey:'phase02' },
  { id:'p00', num:'01', title:'Brand DNA',             tag:'The Foundation',  dataKey:'phase00' },
  { id:'p01', num:'02', title:'Company Identity',      tag:'Who You Are',     dataKey:'phase01' },
  { id:'p03', num:'03', title:'Visual Architecture',   tag:'How You Look',    dataKey:'phase03' },
  { id:'p04', num:'04', title:'Brand Messaging',       tag:'How You Speak',   dataKey:'phase04' },
  { id:'p05', num:'05', title:'Strategic Positioning', tag:'Where You Stand', dataKey:'phase05' },
  { id:'p06', num:'06', title:'Customer Experience',   tag:'How You Feel',    dataKey:'phase06' },
  { id:'p07', num:'07', title:'Digital Presence',      tag:'How You Show Up', dataKey:'phase07' },
  { id:'p08', num:'08', title:'Go-To-Market',          tag:'How You Launch',  dataKey:'phase08' },
  { id:'p10', num:'09', title:'Launch and Activation', tag:'Your Entrance',   dataKey:'phase10' },
];

// Transition messages — index N shown when advancing TO phase N
// Index 0 is intentionally unused (we start on phase 0)
const TRANSITION_MSGS = [
  '',
  'Your name is chosen. Now we uncover the truth at the heart of your brand.',
  'The foundation is set. Now we define who you are and what you stand for.',
  'Your identity is clear. Now we build the visual world your brand deserves.',
  'The visual language is defined. Now we find the words that make your market stop.',
  'Your voice is clear. Now we find exactly where you stand in the market.',
  'Your position is locked. Now we design every moment your customer will remember.',
  'Every moment mapped. Now we build your presence in the digital world.',
  'Your presence is defined. Now we prepare your strategy for the market.',
  'Everything is ready. This is your entrance into the world.',
];

const LOGO_PATH = "M1738.97 674.63c90.78,-4.22 183.48,27.17 251.43,104.4 15.94,18.13 41.91,69.3 1.62,72.16 -46.02,3.27 -88.76,-125.46 -251.31,-116.94 -114.81,6.03 -237.3,86.41 -251.16,229.53 -1.93,19.95 0.62,68.54 -33.13,68.9 -29.35,0.32 -33.64,-33.32 -33.64,-56.16 -0,-174.72 155.08,-294.4 316.2,-301.89zm114.25 144.02c47.75,27.89 78.37,70.83 93.94,123.44 13.66,46.13 14.63,94.59 4.24,123.39 -10.33,28.65 -55.47,20.8 -55.57,-6.74 -0.26,-75.01 -3.67,-164.23 -88.44,-191.35 -48.39,-15.48 -100.32,1.85 -148.54,8.5 -25.39,3.5 -43.85,-31.11 -23.74,-50.24 45.47,-43.25 164.09,-38.55 218.1,-7.01zm-14.55 333.77c-7,166.5 -53.94,185.3 -78.63,170.78 -21.44,-12.62 -11.51,-37.15 -0.76,-64.82 28.36,-72.98 -15.04,-249.87 46.72,-250.26 46.83,-0.3 33.71,119.34 32.66,144.3zm-121.88 -156.07c-9.32,51.34 18.25,152.91 -38.33,154.46 -47.31,-4.36 -22.81,-48.24 -20.17,-106.83 1.84,-40.62 -6.2,-77.37 17.12,-105.02 26.9,-31.91 98.28,-42.1 104.03,0.05 6.44,47.22 -52.81,3.14 -62.65,57.33zm314.19 -566.85l-561.94 0c-159.7,0 -290.36,130.67 -290.36,290.36l0 560.29c0,159.7 130.67,290.36 290.36,290.36l561.94 0c159.7,0 290.36,-130.67 290.36,-290.36l0 -560.29c0,-159.7 -130.67,-290.36 -290.36,-290.36zm-455.62 789.69c-9.88,24.52 -22.66,37.91 -37.16,34.49 -37.44,-8.83 -26.27,-63.83 -18.13,-91.01 18.44,-61.61 17,-75.47 13.24,-125.89 -4.53,-60.75 9.89,-130.66 49.18,-127.92 23.23,1.62 28.87,15.8 28.42,28.18 -0.78,21.37 -17.34,55.06 -18.04,96.04 -1.02,59.72 8.02,122.74 -17.51,186.11zm311.8 -12.58c3.61,-30.19 1.67,-52.98 15.57,-56.77 18.56,-5.07 42.56,-2.65 44.17,19.27 1.62,21.97 -0.66,42.51 -2.24,59.5 -3.87,41.59 -12.09,67.59 -45.35,62.49 -44.91,-6.88 -14.98,-60.73 -12.15,-84.49zm-432.99 -118.89c17.43,0 31.55,14.13 31.55,31.56 0,17.43 -14.13,31.55 -31.55,31.55 -17.43,0 -31.56,-14.13 -31.56,-31.55 0,-17.43 14.13,-31.56 31.56,-31.56zm236.11 150.43c0.51,39.89 -16.95,103.01 -53.06,82.76 -24.94,-13.99 -10.91,-44.76 -4.19,-66.24 9.98,-31.89 20.3,-47.06 40.38,-42.39 14.26,3.31 16.68,10.73 16.87,25.86zm323.94 -245.56c-3.41,-35.22 -4.16,-71.05 20.79,-76.17 58.55,-15.81 63.11,247.5 -0.69,231.18 -20.13,-5.15 -24.09,-17.31 -22.95,-36.89 0.66,-11.4 3.05,-25.32 4.98,-41.82 3.4,-29.04 0.62,-47.84 -2.14,-76.29z";

function LogoMark({ size = 22, fill = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="1100 380 950 1300" xmlns="http://www.w3.org/2000/svg"
      style={{ flexShrink: 0 }} aria-hidden="true">
      <path fill={fill} d={LOGO_PATH} />
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════════════════════════

const styles = `
  @keyframes fadeUp    { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
  @keyframes fadeDown  { from{opacity:0;transform:translateY(-14px)} to{opacity:1;transform:translateY(0)} }
  @keyframes phaseOut  { from{opacity:1;transform:translateY(0)} to{opacity:0;transform:translateY(-10px)} }
  @keyframes phaseIn   { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
  @keyframes fp-spin-1 { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  @keyframes fp-spin-2 { from{transform:rotate(0deg)} to{transform:rotate(-360deg)} }
  @keyframes fp-glow   { 0%,100%{opacity:0.6} 50%{opacity:1} }
  @keyframes glassIn   { from{opacity:0;transform:scale(0.97) translateY(10px)} to{opacity:1;transform:scale(1) translateY(0)} }
  @keyframes overlayIn { from{opacity:0} to{opacity:1} }
  @keyframes endReveal { from{opacity:0;transform:scale(0.97) translateY(16px)} to{opacity:1;transform:scale(1) translateY(0)} }
  @keyframes toastIn   { from{opacity:0;transform:translateX(-50%) translateY(8px)} to{opacity:1;transform:translateX(-50%) translateY(0)} }
  @keyframes dotPulse  { 0%,60%,100%{opacity:0.25;transform:scale(0.75)} 30%{opacity:1;transform:scale(1)} }
  @keyframes doneIn    { from{opacity:0;transform:scale(0.7)} to{opacity:1;transform:scale(1)} }
  @keyframes cg-spin   { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }

  * { box-sizing:border-box; margin:0; padding:0; }

  .r-root { min-height:100vh; background:#F2F5FF; font-family:'Inter',sans-serif; color:#0A0A1A; position:relative; overflow-x:hidden; }
  .r-root *, .r-root *::before, .r-root *::after { box-sizing:border-box; max-width:100%; }
  .r-root img { height:auto; }
  .r-root p, .r-root span, .r-root div { word-break:break-word; overflow-wrap:break-word; }
  .r-bg { position:fixed; inset:0; pointer-events:none; z-index:0; overflow:hidden; }
  .r-bg-orb { position:absolute; border-radius:50%; filter:blur(100px); }
  .r-bg-orb-1 { width:700px; height:700px; top:-150px; left:-50px; background:radial-gradient(ellipse, rgba(26,106,255,0.05) 0%, transparent 70%); }
  .r-bg-orb-2 { width:500px; height:500px; bottom:-80px; right:0; background:radial-gradient(ellipse, rgba(6,182,212,0.04) 0%, transparent 70%); }

  /* ── CONCIERGE TOAST — dark pill, maximum contrast ── */
  .r-trans-toast {
    position:fixed; bottom:108px; left:50%; transform:translateX(-50%);
    z-index:80; pointer-events:none;
    background:#050C1A; color:#EDF1FF;
    font-family:'Inter',sans-serif;
    font-size:13px; font-weight:400; line-height:1.5; letter-spacing:-0.01em;
    white-space:nowrap; padding:12px 24px; border-radius:100px;
    box-shadow:0 8px 32px rgba(5,12,26,0.3);
    animation:toastIn 0.3s ease forwards;
  }

  /* ── CONCIERGE OVERLAY ── */
  .r-concierge {
    position:fixed; inset:0; z-index:150;
    background:rgba(242,245,255,0.84);
    backdrop-filter:blur(20px) saturate(1.5);
    -webkit-backdrop-filter:blur(20px) saturate(1.5);
    display:flex; flex-direction:column; align-items:center; justify-content:center;
    animation:overlayIn 0.3s ease forwards;
  }
  .r-cg-card {
    background:rgba(255,255,255,0.94);
    backdrop-filter:blur(20px); -webkit-backdrop-filter:blur(20px);
    border:1px solid rgba(255,255,255,0.98); border-radius:24px; padding:44px 52px;
    display:flex; flex-direction:column; align-items:center; gap:16px;
    box-shadow:0 24px 64px rgba(0,0,0,0.1), 0 4px 16px rgba(26,106,255,0.07);
    min-width:0; width:90vw; max-width:480px; text-align:center;
    position:relative; overflow:hidden;
  }
  .r-cg-card::before { content:''; position:absolute; top:0; left:0; right:0; height:2px; background:linear-gradient(90deg, transparent, #1A6AFF 30%, #06B6D4 70%, transparent); }
  .r-cg-badge { display:inline-flex; align-items:center; gap:7px; font-size:9px; font-weight:600; letter-spacing:0.18em; text-transform:uppercase; color:#1A6AFF; background:#EFF4FF; border:1px solid #BFDBFE; padding:5px 12px; border-radius:100px; }
  .r-cg-badge-dot { width:5px; height:5px; border-radius:50%; background:#1A6AFF; animation:fp-glow 1.2s ease-in-out infinite; }
  .r-cg-spinner { position:relative; width:60px; height:60px; display:flex; align-items:center; justify-content:center; }
  .r-cg-ring { position:absolute; border-radius:50%; border:2px solid transparent; top:50%; left:50%; }
  .r-cg-ring-1 { width:60px; height:60px; margin:-30px 0 0 -30px; border-color:rgba(26,106,255,0.15); border-top-color:#1A6AFF; animation:cg-spin 1.6s linear infinite; }
  .r-cg-ring-2 { width:42px; height:42px; margin:-21px 0 0 -21px; border-color:rgba(6,182,212,0.12); border-right-color:#06B6D4; animation:cg-spin 1.1s linear infinite reverse; }
  .r-cg-title { font-family:'Space Grotesk',sans-serif; font-size:22px; font-weight:700; letter-spacing:-0.03em; color:#0A0A1A; line-height:1.2; }
  .r-cg-sub { font-size:13px; font-weight:400; color:#6B7280; line-height:1.7; max-width:320px; }
  .r-cg-dots { display:flex; gap:8px; }
  .r-cg-dots span { width:7px; height:7px; border-radius:50%; background:#1A6AFF; animation:dotPulse 1.4s ease-in-out infinite; }
  .r-cg-dots span:nth-child(2) { animation-delay:0.2s; }
  .r-cg-dots span:nth-child(3) { animation-delay:0.4s; }
  .r-cg-done { width:60px; height:60px; background:#EFF4FF; border:2px solid #BFDBFE; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:26px; animation:doneIn 0.4s cubic-bezier(0.22,1,0.36,1) forwards; }
  .r-cg-btn { height:48px; padding:0 28px; background:#1A6AFF; border:none; border-radius:10px; font-family:'Space Grotesk',sans-serif; font-size:14px; font-weight:600; color:#fff; cursor:pointer; transition:all 0.2s; width:100%; box-shadow:0 4px 14px rgba(26,106,255,0.35); letter-spacing:0.02em; }
  .r-cg-btn:hover { background:#094BCC; transform:translateY(-1px); }
  .r-cg-error { font-size:12px; color:#EF4444; text-align:center; }

  /* ── PHASE FADE ── */
  .r-main.fading-out { animation:phaseOut 0.28s ease forwards; pointer-events:none; }
  .r-main.entering   { animation:phaseIn 0.4s cubic-bezier(0.22,1,0.36,1) forwards; }

  /* ── DIALOGS ── */
  .r-dialog-overlay { position:fixed; inset:0; z-index:100; background:rgba(10,10,26,0.2); backdrop-filter:blur(10px); -webkit-backdrop-filter:blur(10px); display:flex; align-items:center; justify-content:center; animation:overlayIn 0.2s ease forwards; padding:24px; }
  .r-dialog {
    background:rgba(255,255,255,0.94); backdrop-filter:blur(24px) saturate(1.4); -webkit-backdrop-filter:blur(24px) saturate(1.4);
    border:1px solid rgba(255,255,255,0.98); border-radius:20px; padding:36px; max-width:500px; width:100%;
    animation:glassIn 0.28s cubic-bezier(0.22,1,0.36,1) forwards;
    position:relative; overflow:hidden;
    box-shadow:0 20px 60px rgba(0,0,0,0.1), 0 4px 16px rgba(26,106,255,0.06);
  }
  .r-dialog::before { content:''; position:absolute; top:0; left:0; right:0; height:2px; background:linear-gradient(90deg, transparent, #1A6AFF 30%, #06B6D4 70%, transparent); }
  .r-dialog-badge { display:inline-flex; align-items:center; gap:8px; font-size:9px; font-weight:600; letter-spacing:0.18em; text-transform:uppercase; color:#1A6AFF; margin-bottom:18px; padding:5px 12px; background:#EFF4FF; border:1px solid #BFDBFE; border-radius:100px; }
  .r-dialog-badge::before { content:''; width:5px; height:5px; border-radius:50%; background:#1A6AFF; animation:fp-glow 2s ease-in-out infinite; }
  .r-dialog-title { font-family:'Space Grotesk',sans-serif; font-size:22px; font-weight:700; letter-spacing:-0.03em; color:#0A0A1A; margin-bottom:10px; line-height:1.2; }
  .r-dialog-sub { font-size:14px; font-weight:400; color:#374151; line-height:1.7; margin-bottom:20px; }
  .r-dialog-textarea { width:100%; min-height:100px; background:#F8FAFF; border:1.5px solid #E2E8F0; border-radius:10px; padding:14px 16px; font-family:'Inter',sans-serif; font-size:14px; color:#0A0A1A; line-height:1.65; resize:vertical; outline:none; margin-bottom:16px; transition:border-color 0.2s, box-shadow 0.2s; caret-color:#1A6AFF; }
  .r-dialog-textarea::placeholder { color:#9CA3AF; font-style:italic; }
  .r-dialog-textarea:focus { border-color:#1A6AFF; box-shadow:0 0 0 3px rgba(26,106,255,0.08); background:#fff; }
  .r-dialog-actions { display:flex; flex-direction:column; gap:10px; }
  .r-dialog-btn-primary { height:48px; width:100%; background:#1A6AFF; border:none; border-radius:10px; font-family:'Space Grotesk',sans-serif; font-size:14px; font-weight:600; color:#fff; cursor:pointer; transition:all 0.2s; letter-spacing:0.02em; }
  .r-dialog-btn-primary:hover:not(:disabled) { background:#094BCC; transform:translateY(-1px); box-shadow:0 6px 20px rgba(26,106,255,0.3); }
  .r-dialog-btn-primary:disabled { opacity:0.4; cursor:not-allowed; }
  .r-dialog-btn-secondary { height:44px; width:100%; background:transparent; border:1.5px solid #E2E8F0; border-radius:10px; font-family:'Space Grotesk',sans-serif; font-size:13px; font-weight:500; color:#374151; cursor:pointer; transition:all 0.2s; }
  .r-dialog-btn-secondary:hover { border-color:#CBD5E1; color:#0A0A1A; background:#F8FAFF; }
  .r-dialog-note { font-size:11px; color:#9CA3AF; text-align:center; margin-top:14px; }
  /* Info-toned name-locked dialog — amber for warning, not error */
  .r-dialog-info::before { background:linear-gradient(90deg, transparent, #F59E0B 30%, #F59E0B 70%, transparent); }

  /* ── READ-ONLY SECTION ── */
  .r-readonly-section { position:relative; }
  .r-readonly-badge {
    display:inline-flex; align-items:center; gap:5px;
    font-size:9px; font-weight:600; letter-spacing:0.14em; text-transform:uppercase;
    color:#9CA3AF; margin-bottom:12px; user-select:none;
  }

  /* ── END LIGHTBOX ── */
  .r-end { position:fixed; inset:0; z-index:200; background:#050C1A; display:flex; flex-direction:column; align-items:center; justify-content:center; padding:40px 24px; animation:overlayIn 0.4s ease forwards; }
  .r-end-bg { position:absolute; inset:0; background:radial-gradient(ellipse 70% 55% at 50% 55%, rgba(26,106,255,0.18) 0%, transparent 65%); pointer-events:none; }
  .r-end-wordmark { position:absolute; top:28px; left:50%; transform:translateX(-50%); display:flex; align-items:center; gap:10px; font-family:'Space Grotesk',sans-serif; font-size:11px; font-weight:600; letter-spacing:0.2em; text-transform:uppercase; color:rgba(255,255,255,0.22); white-space:nowrap; }
  .r-end-inner { position:relative; z-index:1; display:flex; flex-direction:column; align-items:center; animation:endReveal 0.7s cubic-bezier(0.22,1,0.36,1) forwards; width:100%; max-width:420px; }
  .r-end-fp { position:relative; width:72px; height:72px; display:flex; align-items:center; justify-content:center; margin-bottom:28px; }
  .r-end-fp-ring { position:absolute; border-radius:50%; border:1.5px solid transparent; top:50%; left:50%; }
  .r-end-fp-r1 { width:72px; height:72px; margin:-36px 0 0 -36px; border-color:rgba(26,106,255,0.2); border-top-color:#1A6AFF; animation:fp-spin-1 8s linear infinite; }
  .r-end-fp-r2 { width:50px; height:50px; margin:-25px 0 0 -25px; border-color:rgba(6,182,212,0.15); border-right-color:#06B6D4; animation:fp-spin-2 5s linear infinite; }
  .r-end-fp-core { width:10px; height:10px; border-radius:50%; background:#1A6AFF; animation:fp-glow 3s ease-in-out infinite; }
  .r-end-counter { font-size:10px; font-weight:600; letter-spacing:0.25em; text-transform:uppercase; color:#06B6D4; margin-bottom:16px; }
  .r-end-headline { font-family:'Space Grotesk',sans-serif; font-size:clamp(34px,5vw,60px); font-weight:700; letter-spacing:-0.04em; color:#EDF1FF; text-align:center; line-height:1.0; margin-bottom:10px; }
  .r-end-name { font-family:'Space Grotesk',sans-serif; font-size:clamp(18px,2.5vw,32px); font-weight:600; letter-spacing:-0.03em; color:#06B6D4; text-align:center; margin-bottom:32px; }
  .r-end-stats { display:flex; gap:24px; margin-bottom:36px; align-items:center; }
  .r-end-stat { text-align:center; }
  .r-end-stat-num { font-family:'Space Grotesk',sans-serif; font-size:24px; font-weight:700; letter-spacing:-0.04em; color:rgba(26,106,255,0.6); line-height:1; margin-bottom:4px; }
  .r-end-stat-label { font-size:9px; font-weight:500; letter-spacing:0.2em; text-transform:uppercase; color:rgba(255,255,255,0.22); }
  .r-end-sep { width:1px; height:28px; background:rgba(255,255,255,0.08); }
  .r-end-actions { display:flex; flex-direction:column; gap:8px; width:100%; }
  .r-end-btn-p { height:50px; width:100%; background:linear-gradient(135deg,#094BCC,#1A6AFF); border:none; border-radius:10px; font-family:'Space Grotesk',sans-serif; font-size:14px; font-weight:600; color:#fff; cursor:pointer; letter-spacing:0.02em; transition:all 0.2s; box-shadow:0 8px 32px rgba(26,106,255,0.35); }
  .r-end-btn-p:hover { transform:translateY(-1px); box-shadow:0 14px 48px rgba(26,106,255,0.45); }
  .r-end-btn-s { height:42px; width:100%; background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.1); border-radius:10px; font-family:'Space Grotesk',sans-serif; font-size:13px; font-weight:400; color:rgba(255,255,255,0.5); cursor:pointer; transition:all 0.2s; }
  .r-end-btn-s:hover { border-color:rgba(255,255,255,0.22); color:rgba(255,255,255,0.8); background:rgba(255,255,255,0.07); }
  .r-end-back { background:none; border:none; color:rgba(255,255,255,0.2); font-size:11px; cursor:pointer; margin-top:10px; transition:color 0.2s; }
  .r-end-back:hover { color:rgba(255,255,255,0.5); }
  .r-end-note { margin-top:16px; font-size:10px; color:rgba(255,255,255,0.14); text-align:center; }
  .r-end-sub-label { font-size:9px; font-weight:600; letter-spacing:0.2em; text-transform:uppercase; color:rgba(255,255,255,0.28); margin-bottom:10px; }
  .r-end-sub-title { font-family:'Space Grotesk',sans-serif; font-size:22px; font-weight:700; letter-spacing:-0.03em; color:#EDF1FF; margin-bottom:10px; }
  .r-end-sub-text { font-size:13px; color:rgba(255,255,255,0.48); line-height:1.7; margin-bottom:22px; font-weight:300; }
  .r-stars { display:flex; gap:8px; margin-bottom:16px; }
  .r-star { width:36px; height:36px; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.1); border-radius:8px; cursor:pointer; display:flex; align-items:center; justify-content:center; font-size:18px; transition:all 0.15s; }
  .r-star:hover, .r-star.lit { background:rgba(26,106,255,0.2); border-color:rgba(26,106,255,0.4); }
  .r-feedback-sent { text-align:center; padding:16px 0; }
  .r-feedback-sent-icon { font-size:32px; margin-bottom:10px; }
  .r-feedback-sent-title { font-family:'Space Grotesk',sans-serif; font-size:20px; font-weight:700; letter-spacing:-0.02em; color:#EDF1FF; margin-bottom:6px; }
  .r-feedback-sent-sub { font-size:13px; color:rgba(255,255,255,0.4); line-height:1.6; }

  /* ── LAYOUT ── */
  .r-layout { display:grid; grid-template-columns:220px minmax(0,1fr) 200px; grid-template-rows:auto 1fr; min-height:100vh; position:relative; z-index:1; }
  .r-header { grid-column:1/-1; height:60px; padding:0 32px; display:flex; align-items:center; justify-content:space-between; background:#050C1A; position:sticky; top:0; z-index:20; overflow:visible; gap:8px; }
  .r-wordmark { font-family:'Space Grotesk',sans-serif; font-size:11px; font-weight:600; letter-spacing:0.18em; text-transform:uppercase; color:rgba(255,255,255,0.38); display:flex; align-items:center; gap:10px; }
  .r-header-name { font-family:'Space Grotesk',sans-serif; font-size:13px; font-weight:600; color:#06B6D4; letter-spacing:0.02em; }
  .r-header-center { font-size:11px; font-weight:500; letter-spacing:0.12em; text-transform:uppercase; color:rgba(255,255,255,0.3); }
  .r-header-center strong { color:#1A6AFF; font-weight:600; }
  .r-header-new { height:30px; padding:0 16px; background:transparent; border:1px solid rgba(255,255,255,0.12); border-radius:6px; font-family:'Space Grotesk',sans-serif; font-size:11px; font-weight:500; color:rgba(255,255,255,0.38); cursor:pointer; transition:all 0.2s; }
  .r-header-new:hover { border-color:rgba(26,106,255,0.5); color:rgba(255,255,255,0.7); background:rgba(26,106,255,0.08); }

  .r-left { padding:28px 20px; border-right:1px solid #E2E8F0; display:flex; flex-direction:column; gap:4px; background:#FFFFFF; }
  .r-nav-item { display:flex; align-items:center; gap:10px; padding:9px 10px; border-radius:8px; cursor:pointer; transition:all 0.15s; border:1px solid transparent; }
  .r-nav-item:hover:not(.future) { background:#F8FAFF; }
  .r-nav-item.active { background:rgba(26,106,255,0.15); border-color:rgba(26,106,255,0.3); }
  .r-nav-item.future { cursor:default; pointer-events:none; }
  .r-nav-num { font-size:10px; font-weight:500; color:#9CA3AF; width:20px; flex-shrink:0; }
  .r-nav-item.active .r-nav-num { color:#1A6AFF; }
  .r-nav-item.done  .r-nav-num { color:#06B6D4; }
  .r-nav-name { font-size:12px; font-weight:400; color:#6B7280; transition:color 0.15s; }
  .r-nav-item.active .r-nav-name { color:#0A0A1A; font-weight:500; }
  .r-nav-item.done  .r-nav-name { color:#4B5563; }
  .r-nav-item.future .r-nav-name { color:#D1D5DB; }
  .r-nav-dot { margin-left:auto; width:5px; height:5px; border-radius:50%; background:rgba(255,255,255,0.15); flex-shrink:0; }
  .r-nav-item.done   .r-nav-dot { background:#06B6D4; }
  .r-nav-item.active .r-nav-dot { background:#1A6AFF; box-shadow:0 0 6px rgba(26,106,255,0.5); }
  .r-nav-item.gated { opacity:0.55; cursor:pointer; }
  .r-nav-item.gated:hover { opacity:0.8; background:rgba(26,106,255,0.04); }

  .r-main { overflow-x:hidden; padding:40px 48px 100px; overflow-y:auto; max-height:calc(100vh - 60px); background:transparent; min-width:0; width:100%; }
  .r-main::-webkit-scrollbar { width:3px; }
  .r-main::-webkit-scrollbar-thumb { background:#BFDBFE; border-radius:2px; }

  .r-right { padding:28px 20px; border-left:1px solid #E2E8F0; background:#FFFFFF; display:flex; flex-direction:column; gap:14px; }
  .r-right-label { font-size:9px; font-weight:600; letter-spacing:0.16em; text-transform:uppercase; color:#9CA3AF; margin-bottom:6px; }
  .r-brief-box { background:#EFF4FF; border:1px solid #BFDBFE; border-radius:10px; padding:12px 14px; }
  .r-brief-text { font-size:12px; font-weight:400; color:#374151; line-height:1.65; }
  .r-selected-name-val { font-family:'Space Grotesk',sans-serif; font-size:17px; font-weight:700; letter-spacing:-0.02em; color:#1A6AFF; padding:6px 0; }
  .r-refine-btn { width:100%; height:36px; background:#F8FAFF; border:1.5px solid #E2E8F0; border-radius:8px; font-family:'Space Grotesk',sans-serif; font-size:11px; font-weight:600; color:#374151; cursor:pointer; transition:all 0.2s; display:flex; align-items:center; justify-content:center; gap:6px; letter-spacing:0.02em; }
  .r-refine-btn:hover:not(:disabled) { border-color:#1A6AFF; color:#1A6AFF; background:#EFF4FF; }
  .r-refine-btn:disabled { opacity:0.4; cursor:not-allowed; }
  .r-export-btn { width:100%; height:30px; background:transparent; border:1px solid rgba(255,255,255,0.1); border-radius:8px; font-size:9px; font-weight:600; letter-spacing:0.12em; text-transform:uppercase; color:rgba(242,245,255,0.4); cursor:pointer; transition:all 0.2s; display:flex; align-items:center; justify-content:center; gap:5px; }
  .r-export-btn:hover { border-color:#BFDBFE; color:#1A6AFF; background:#EFF4FF; }
  .r-right-divider { height:1px; background:#F3F4F6; }
  .r-progress-arc { display:flex; flex-direction:column; align-items:center; gap:6px; }
  .r-progress-num { font-family:'Space Grotesk',sans-serif; font-size:40px; font-weight:700; letter-spacing:-0.04em; color:#BFDBFE; line-height:1; }
  .r-progress-of { font-size:10px; font-weight:500; color:#9CA3AF; }

  .r-phase-label { display:inline-flex; align-items:center; gap:10px; font-size:10px; font-weight:600; letter-spacing:0.2em; text-transform:uppercase; color:#1A6AFF; margin-bottom:14px; animation:fadeDown 0.35s ease forwards; }
  .r-phase-label::after { content:''; width:24px; height:1.5px; background:#BFDBFE; border-radius:1px; }

  .r-hero { font-family:'Space Grotesk',sans-serif; font-size:clamp(28px,3.5vw,48px); font-weight:700; letter-spacing:-0.04em; line-height:1.05; color:#0A0A1A; margin-bottom:32px; animation:fadeUp 0.5s cubic-bezier(0.22,1,0.36,1) 0.06s forwards; opacity:0; animation-fill-mode:forwards; }
  .r-hero-cyan { color:#06B6D4; }
  .r-hero-dim  { color:#CBD5E1; }

  .r-manifesto { font-family:'Space Grotesk',sans-serif; font-size:clamp(15px,1.8vw,20px); font-weight:400; letter-spacing:-0.01em; line-height:1.65; color:#0A0A1A; padding:28px 32px; background:#EFF4FF; border-left:3px solid #1A6AFF; border-radius:0 14px 14px 0; margin-bottom:28px; animation:fadeUp 0.5s ease 0.12s forwards; opacity:0; animation-fill-mode:forwards; }

  .r-grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:12px; margin-bottom:12px; }
  .r-grid-3 { display:grid; grid-template-columns:1fr 1fr 1fr; gap:12px; margin-bottom:12px; }

  .r-card { background:#FFFFFF; border:1px solid #E2E8F0; border-radius:14px; padding:22px; transition:border-color 0.2s, box-shadow 0.2s; box-shadow:0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.04); }
  .r-card:hover { border-color:#CBD5E1; box-shadow:0 2px 6px rgba(0,0,0,0.07), 0 8px 20px rgba(0,0,0,0.05); }
  .r-card-blue { background:#EFF4FF; border-color:#BFDBFE; box-shadow:none; }
  .r-card-blue:hover { border-color:#93C5FD; }
  .r-card-label { font-size:10px; font-weight:600; letter-spacing:0.14em; text-transform:uppercase; color:#6B7280; margin-bottom:10px; }
  .r-card-value { font-family:'Space Grotesk',sans-serif; font-size:18px; font-weight:700; letter-spacing:-0.02em; color:#0A0A1A; margin-bottom:6px; line-height:1.2; word-break:break-word; overflow-wrap:break-word; }
  .r-card-text { font-size:13px; font-weight:400; color:#374151; line-height:1.75; word-break:break-word; overflow-wrap:break-word; }

  /* Whitespace score — #60A5FA provides 3.8:1 on #EFF4FF — passes WCAG AA for large text */
  .r-big-num { font-family:'Space Grotesk',sans-serif; font-size:80px; font-weight:700; letter-spacing:-0.05em; color:#1A6AFF; line-height:1; display:flex; align-items:baseline; gap:4px; }
  .r-big-num-suf { font-size:28px; font-weight:500; color:#60A5FA; }

  /* Name cards — selection is the ONLY action; no inline editing */
  .r-name-card { background:#FFFFFF; border:1.5px solid #E2E8F0; border-radius:16px; padding:26px; margin-bottom:10px; cursor:pointer; transition:all 0.22s cubic-bezier(0.22,1,0.36,1); position:relative; overflow:hidden; box-shadow:0 1px 3px rgba(0,0,0,0.05); }
  .r-name-card:hover { border-color:#93C5FD; transform:translateY(-2px); box-shadow:0 4px 16px rgba(0,0,0,0.08); }
  .r-name-card.selected { border-color:#1A6AFF; box-shadow:0 0 0 3px rgba(26,106,255,0.1), 0 4px 16px rgba(26,106,255,0.12); }
  /* Locked — confirmed name: prominent. Others: clearly receded. */
  .r-name-card.locked-selected { border-color:#1A6AFF; box-shadow:0 0 0 3px rgba(26,106,255,0.12), 0 4px 16px rgba(26,106,255,0.15); cursor:default; background:#FAFCFF; }
  .r-name-card.locked-selected:hover { transform:none; }
  .r-name-card.locked-other { opacity:0.28; cursor:default; filter:grayscale(0.3); pointer-events:none; }
  .r-name-card.locked-other:hover { transform:none; border-color:#E2E8F0; box-shadow:0 1px 3px rgba(0,0,0,0.05); }
  .r-name-top { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:12px; }
  .r-name-word { font-family:'Space Grotesk',sans-serif; font-size:clamp(26px,4vw,40px); font-weight:700; letter-spacing:-0.04em; color:#0A0A1A; line-height:1; }
  .r-name-card.selected .r-name-word { color:#1A6AFF; }
  .r-name-badge { font-size:9px; font-weight:700; letter-spacing:0.1em; text-transform:uppercase; padding:4px 10px; border-radius:100px; display:inline-block; margin-bottom:6px; }
  .r-name-badge-client { background:#EFF4FF; border:1px solid #BFDBFE; color:#1A6AFF; }
  .r-name-badge-user   { background:#F0FDFE; border:1px solid rgba(6,182,212,0.3); color:#06B6D4; }
  .r-badges { display:flex; gap:6px; flex-wrap:wrap; padding-top:4px; }
  .r-badge { font-size:9px; font-weight:500; letter-spacing:0.06em; text-transform:uppercase; padding:4px 10px; border-radius:100px; border:1px solid #E2E8F0; color:#6B7280; background:#F8FAFF; }
  .r-badge-cyan { background:#F0FDFE; border-color:rgba(6,182,212,0.25); color:#06B6D4; }
  .r-name-rationale { font-size:13px; font-weight:400; color:#374151; line-height:1.65; margin-bottom:12px; }
  .r-name-domain { font-size:11px; font-weight:500; color:#9CA3AF; }
  .r-domain-disclaimer { font-size:10px; color:#F59E0B; background:#FFFBEB; border:1px solid #FDE68A; borderRadius:6px; padding:6px 10px; marginBottom:12px; lineHeight:1.5; }
  .r-name-select { position:absolute; bottom:18px; right:18px; height:30px; padding:0 14px; background:transparent; border:1.5px solid #BFDBFE; border-radius:6px; font-family:'Space Grotesk',sans-serif; font-size:10px; font-weight:700; letter-spacing:0.08em; color:#1A6AFF; cursor:pointer; transition:all 0.15s; }
  .r-name-card:hover .r-name-select { background:#1A6AFF; border-color:#1A6AFF; color:#fff; }
  .r-name-card.selected .r-name-select { background:#1A6AFF; border-color:#1A6AFF; color:#fff; }
  .r-name-input-row { display:flex; gap:10px; margin-bottom:18px; }
  .r-name-input { flex:1; height:42px; background:#FFFFFF; border:1.5px solid #E2E8F0; border-radius:8px; padding:0 14px; font-family:'Inter',sans-serif; font-size:14px; color:#0A0A1A; outline:none; transition:border-color 0.2s; caret-color:#1A6AFF; }
  .r-name-input::placeholder { color:#9CA3AF; }
  .r-name-input:focus { border-color:#1A6AFF; box-shadow:0 0 0 3px rgba(26,106,255,0.08); }
  .r-name-input:disabled { opacity:0.4; cursor:not-allowed; background:#F8FAFF; }
  .r-name-input-btn { height:42px; padding:0 18px; background:#1A6AFF; border:none; border-radius:8px; font-family:'Space Grotesk',sans-serif; font-size:13px; font-weight:600; color:#fff; cursor:pointer; transition:all 0.2s; }
  .r-name-input-btn:hover:not(:disabled) { background:#094BCC; }
  .r-name-input-btn:disabled { opacity:0.4; cursor:not-allowed; }

  .r-palette { display:flex; border-radius:12px; overflow:hidden; height:88px; margin-bottom:16px; box-shadow:0 2px 8px rgba(0,0,0,0.08); }
  .r-pal-sw { flex:1; position:relative; cursor:pointer; transition:flex 0.28s ease; }
  .r-pal-sw:hover { flex:2.2; }
  .r-pal-info { position:absolute; bottom:0; left:0; right:0; padding:10px; background:linear-gradient(transparent,rgba(0,0,0,0.7)); opacity:0; transition:opacity 0.2s; }
  .r-pal-sw:hover .r-pal-info { opacity:1; }
  .r-pal-hex  { font-size:10px; font-weight:500; color:rgba(255,255,255,0.9); }
  .r-pal-name { font-size:11px; font-weight:500; color:#fff; }

  .r-trait { display:flex; align-items:center; gap:14px; padding:11px 0; border-bottom:1px solid #F3F4F6; }
  .r-trait:last-child { border-bottom:none; }
  .r-trait-name { font-size:12px; font-weight:400; color:#374151; width:130px; flex-shrink:0; }
  .r-trait-track { flex:1; height:2px; background:#E2E8F0; border-radius:1px; overflow:hidden; }
  .r-trait-fill { height:100%; background:linear-gradient(90deg,#1A6AFF,#06B6D4); border-radius:1px; transition:width 1.2s cubic-bezier(0.22,1,0.36,1); }
  .r-trait-score { font-family:'Space Grotesk',sans-serif; font-size:15px; font-weight:700; color:#CBD5E1; width:34px; text-align:right; flex-shrink:0; }

  .r-tagline { padding:20px 24px; background:#FFFFFF; border:1px solid #E2E8F0; border-radius:12px; margin-bottom:8px; box-shadow:0 1px 3px rgba(0,0,0,0.04); }
  .r-tagline-line { font-family:'Space Grotesk',sans-serif; font-size:clamp(16px,2.2vw,21px); font-weight:600; letter-spacing:-0.02em; color:#0A0A1A; margin-bottom:6px; }
  .r-tagline-why { font-size:12px; font-weight:400; color:#6B7280; line-height:1.55; }

  .r-tones { display:flex; gap:8px; flex-wrap:wrap; margin-bottom:16px; }
  .r-tone { font-family:'Space Grotesk',sans-serif; font-size:13px; font-weight:500; padding:7px 16px; border-radius:100px; background:#EFF4FF; border:1px solid #BFDBFE; color:#1A6AFF; cursor:default; }

  .r-rule { display:flex; gap:10px; align-items:flex-start; padding:9px 0; border-bottom:1px solid #F3F4F6; font-size:13px; font-weight:400; color:#374151; line-height:1.55; }
  .r-rule:last-child { border-bottom:none; }
  .r-rule-do::before   { content:'✓'; color:#06B6D4; font-size:11px; font-weight:700; margin-top:1px; flex-shrink:0; }
  .r-rule-dont::before { content:'✕'; color:#EF4444; font-size:11px; font-weight:700; margin-top:1px; flex-shrink:0; }

  .r-comp { display:grid; grid-template-columns:150px 1fr 1fr; margin-bottom:8px; background:#FFFFFF; border:1px solid #E2E8F0; border-radius:10px; overflow:hidden; box-shadow:0 1px 3px rgba(0,0,0,0.04); }
  .r-comp-name-cell { padding:16px 18px; border-right:1px solid #F3F4F6; display:flex; align-items:center; background:#FAFBFF; }
  .r-comp-name { font-family:'Space Grotesk',sans-serif; font-size:13px; font-weight:600; color:#0A0A1A; }
  .r-comp-cell { padding:16px 18px; border-right:1px solid #F3F4F6; }
  .r-comp-cell:last-child { border-right:none; }
  .r-comp-cell-label { font-size:9px; font-weight:600; letter-spacing:0.12em; text-transform:uppercase; color:#9CA3AF; margin-bottom:5px; }
  .r-comp-cell-text  { font-size:12px; font-weight:400; color:#374151; line-height:1.55; }

  .r-persona { background:#FFFFFF; border:1px solid #E2E8F0; border-radius:16px; padding:22px; margin-bottom:10px; position:relative; overflow:hidden; box-shadow:0 1px 3px rgba(0,0,0,0.04); }
  .r-persona::before { content:''; position:absolute; top:0; left:0; right:0; height:2px; background:linear-gradient(90deg,#1A6AFF,#06B6D4); }
  .r-persona.anti::before { background:linear-gradient(90deg,#EF4444,#F97316); }
  .r-persona-type { font-size:9px; font-weight:600; letter-spacing:0.14em; text-transform:uppercase; color:#06B6D4; margin-bottom:6px; }
  .r-persona.anti .r-persona-type { color:#EF4444; }
  .r-persona-name { font-family:'Space Grotesk',sans-serif; font-size:20px; font-weight:700; letter-spacing:-0.02em; color:#0A0A1A; margin-bottom:14px; }
  .r-persona-grid { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
  .r-persona-item-label { font-size:9px; font-weight:600; letter-spacing:0.12em; text-transform:uppercase; color:#9CA3AF; margin-bottom:3px; }
  .r-persona-item-text  { font-size:12px; font-weight:400; color:#374151; line-height:1.55; }

  .r-swot { display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-bottom:12px; }
  .r-swot-card { background:#FFFFFF; border:1px solid #E2E8F0; border-radius:12px; padding:18px; box-shadow:0 1px 3px rgba(0,0,0,0.04); }
  .r-swot-label { font-size:10px; font-weight:600; letter-spacing:0.14em; text-transform:uppercase; margin-bottom:12px; }
  .r-swot-item { font-size:12px; font-weight:400; color:#374151; padding:6px 0; border-bottom:1px solid #F3F4F6; line-height:1.5; display:flex; gap:8px; }
  .r-swot-item::before { content:''; color:#D1D5DB; flex-shrink:0; }
  .r-swot-item:last-child { border-bottom:none; }

  .r-journey-item { display:grid; grid-template-columns:130px 1fr 1fr; gap:16px; padding:16px 0; border-bottom:1px solid #F3F4F6; align-items:start; }
  .r-journey-item:last-child { border-bottom:none; }
  .r-journey-stage { font-family:'Space Grotesk',sans-serif; font-size:12px; font-weight:600; color:#374151; display:flex; align-items:center; gap:8px; }
  .r-journey-stage::before { content:''; width:5px; height:5px; border-radius:50%; background:#1A6AFF; flex-shrink:0; }
  .r-journey-cell-label { font-size:9px; font-weight:600; letter-spacing:0.12em; text-transform:uppercase; color:#9CA3AF; margin-bottom:4px; }
  .r-journey-cell-text  { font-size:12px; font-weight:400; color:#374151; line-height:1.55; }

  .r-kw-row { display:flex; gap:7px; flex-wrap:wrap; }
  .r-kw { font-size:11px; font-weight:500; padding:5px 12px; border-radius:6px; background:#F8FAFF; border:1px solid #E2E8F0; color:#374151; }

  .r-icp { display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-bottom:16px; }
  .r-icp-card { background:#FFFFFF; border:1px solid #E2E8F0; border-radius:12px; padding:18px; box-shadow:0 1px 3px rgba(0,0,0,0.04); }
  .r-icp-label { font-family:'Space Grotesk',sans-serif; font-size:40px; font-weight:700; letter-spacing:-0.04em; color:#EFF4FF; line-height:1; margin-bottom:6px; }

  .r-hero-preview { background:#FFFFFF; border:1px solid #E2E8F0; border-radius:16px; overflow:hidden; margin-bottom:16px; box-shadow:0 2px 8px rgba(0,0,0,0.05); }
  .r-hero-preview-bar { background:#F8FAFF; padding:10px 16px; display:flex; gap:6px; align-items:center; border-bottom:1px solid #F3F4F6; }
  .r-hero-preview-dot { width:7px; height:7px; border-radius:50%; }
  .r-hero-preview-content { padding:44px 36px; text-align:center; }
  .r-hero-preview-eyebrow  { font-size:9px; font-weight:600; letter-spacing:0.2em; text-transform:uppercase; color:#06B6D4; margin-bottom:12px; }
  .r-hero-preview-headline { font-family:'Space Grotesk',sans-serif; font-size:clamp(18px,2.8vw,30px); font-weight:700; letter-spacing:-0.03em; color:#0A0A1A; line-height:1.15; margin-bottom:10px; }
  .r-hero-preview-sub  { font-size:13px; color:#6B7280; line-height:1.65; margin-bottom:22px; max-width:400px; margin-left:auto; margin-right:auto; }
  .r-hero-preview-ctas { display:flex; gap:10px; justify-content:center; }
  .r-cta-p { padding:10px 22px; background:#1A6AFF; border-radius:8px; font-family:'Space Grotesk',sans-serif; font-size:13px; font-weight:600; color:#fff; }
  .r-cta-s { padding:10px 22px; border:1.5px solid #E2E8F0; border-radius:8px; font-family:'Space Grotesk',sans-serif; font-size:13px; font-weight:500; color:#374151; }

  .r-pitch { font-size:14px; font-weight:300; color:#374151; line-height:1.8; padding:20px 24px; background:#F8FAFF; border-radius:12px; border-left:3px solid #BFDBFE; margin-bottom:10px; }

  .r-sec { font-size:10px; font-weight:600; letter-spacing:0.16em; text-transform:uppercase; color:#6B7280; margin-bottom:12px; }
  .r-divider { height:1px; background:#F3F4F6; margin:22px 0; }
  .r-alert { display:flex; align-items:flex-start; gap:12px; background:#FFFBEB; border:1px solid #FDE68A; border-radius:10px; padding:14px 16px; margin-bottom:20px; font-size:13px; color:#92400E; line-height:1.55; }
  .r-alert::before { content:'⚠'; font-size:14px; flex-shrink:0; margin-top:1px; }

  /* Launch phase components */
  .r-launch-timeline { position:relative; padding-left:28px; margin-bottom:28px; }
  .r-launch-timeline::before { content:''; position:absolute; left:8px; top:6px; bottom:6px; width:1.5px; background:linear-gradient(180deg,#1A6AFF,#06B6D4); border-radius:1px; }
  .r-launch-week { position:relative; margin-bottom:22px; }
  .r-launch-week::before { content:''; position:absolute; left:-24px; top:5px; width:10px; height:10px; border-radius:50%; background:#1A6AFF; border:2px solid #FFFFFF; box-shadow:0 0 0 2px #1A6AFF; }
  .r-launch-week-label { font-size:9px; font-weight:600; letter-spacing:0.16em; text-transform:uppercase; color:#1A6AFF; margin-bottom:4px; }
  .r-launch-week-title { font-family:'Space Grotesk',sans-serif; font-size:15px; font-weight:700; color:#0A0A1A; margin-bottom:6px; }
  .r-launch-week-text  { font-size:13px; color:#374151; line-height:1.7; }
  .r-channel-row { display:grid; grid-template-columns:1fr 1fr 1fr; gap:10px; margin-bottom:16px; }
  .r-channel-item { background:#FFFFFF; border:1px solid #E2E8F0; border-radius:12px; padding:16px; box-shadow:0 1px 3px rgba(0,0,0,0.04); text-align:center; }
  .r-channel-icon { font-size:22px; margin-bottom:8px; }
  .r-channel-name { font-family:'Space Grotesk',sans-serif; font-size:12px; font-weight:600; color:#0A0A1A; margin-bottom:4px; }
  .r-channel-role { font-size:11px; color:#6B7280; line-height:1.4; }
  .r-metric-grid { display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-bottom:16px; }
  .r-metric { background:#EFF4FF; border:1px solid #BFDBFE; border-radius:12px; padding:16px; }
  .r-metric-num   { font-family:'Space Grotesk',sans-serif; font-size:28px; font-weight:700; letter-spacing:-0.04em; color:#1A6AFF; margin-bottom:4px; line-height:1; }
  .r-metric-label { font-size:11px; font-weight:500; color:#374151; margin-bottom:4px; }
  .r-metric-sub   { font-size:11px; color:#6B7280; }

  /* Dock */
  .r-dock { position:fixed; bottom:24px; left:50%; transform:translateX(-50%); z-index:50; display:flex; flex-direction:column; align-items:center; gap:10px; }
  .r-dock-progress { display:flex; gap:5px; align-items:center; background:#FFFFFF; padding:7px 14px; border-radius:100px; border:1px solid #E2E8F0; box-shadow:0 4px 16px rgba(0,0,0,0.08); }
  .r-dock-dot { width:5px; height:5px; border-radius:50%; background:#E2E8F0; transition:all 0.3s ease; }
  .r-dock-dot.done   { background:#06B6D4; }
  .r-dock-dot.active { background:#1A6AFF; width:18px; border-radius:3px; box-shadow:0 0 6px rgba(26,106,255,0.4); }
  .r-dock-dot.future { background:#F3F4F6; }
  .r-dock-actions { display:flex; gap:8px; align-items:center; }
  .r-dock-back { width:40px; height:40px; background:#FFFFFF; border:1px solid #E2E8F0; border-radius:50%; color:#374151; cursor:pointer; transition:all 0.2s; display:flex; align-items:center; justify-content:center; font-size:15px; box-shadow:0 2px 8px rgba(0,0,0,0.07); }
  .r-dock-back:hover { border-color:#CBD5E1; color:#0A0A1A; }
  .r-dock-next { width:40px; height:40px; background:#1A6AFF; border:none; border-radius:50%; color:#fff; cursor:pointer; transition:all 0.2s; display:flex; align-items:center; justify-content:center; font-size:15px; box-shadow:0 4px 14px rgba(26,106,255,0.4); }
  .r-dock-next:hover { background:#094BCC; transform:translateY(-1px); box-shadow:0 8px 24px rgba(26,106,255,0.45); }
  .r-dock-next.gated { background:#E2E8F0; color:#9CA3AF; box-shadow:none; cursor:not-allowed; }
  .r-dock-next.gated:hover { transform:none; background:#E2E8F0; }

  /* ─── MOBILE RESPONSIVE ─────────────────────────────────────────────── */
  @media (max-width:900px) {
    .r-layout { grid-template-columns:1fr; grid-template-rows:52px auto 1fr; min-height:100vh; }
    .r-layout > * { min-width:0; max-width:100%; }
    .r-header { height:52px; padding:0 16px; grid-column:1; grid-row:1; }
    .r-header-plan-pill { display:none; }
    .r-header-dashboard { display:none; }
    .r-header-center { display:none; }
    .r-header-name { max-width:120px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; font-size:11px; }
    .r-header-new { font-size:10px; padding:0 10px; height:26px; }
    .r-wordmark { font-size:10px; gap:8px; flex-shrink:0; }

    /* Phase nav — horizontal scroll with gradient fade edges to hint scrollability */
    .r-left {
      grid-column:1; grid-row:2;
      display:flex; flex-direction:row; flex-wrap:nowrap;
      overflow-x:auto; overflow-y:hidden;
      padding:8px 24px; gap:4px;
      border-right:none; border-bottom:1px solid #E2E8F0;
      background:#FFFFFF; min-width:0;
      -webkit-overflow-scrolling:touch;
      scrollbar-width:none;
      position:sticky; top:52px; z-index:15;
      -webkit-mask-image:linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%);
      mask-image:linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%);
    }
    .r-left::-webkit-scrollbar { display:none; }
    .r-nav-item {
      flex-shrink:0; flex-direction:column; align-items:center;
      padding:6px 10px; min-width:56px; gap:3px; border-radius:8px;
    }
    .r-nav-num { font-size:8px; }
    .r-nav-name { font-size:9px; text-align:center; line-height:1.2; white-space:nowrap; }
    .r-nav-dot { display:none; }

    /* Main content full width */
    .r-main {
      grid-column:1; grid-row:3;
      padding:20px 16px 160px;
      max-height:unset; overflow-y:visible;
      overflow-x:hidden; min-width:0; width:100%;
    }

    /* Right panel hidden on mobile */
    .r-right { display:none; }

    /* Dock — floating, transparent, centered horizontally */
    .r-dock {
      position:fixed;
      bottom:20px;
      left:50%;
      transform:translateX(-50%);
      width:auto;
      padding:0;
      background:transparent;
      backdrop-filter:none;
      -webkit-backdrop-filter:none;
      border:none;
      display:flex;
      flex-direction:column;
      align-items:center;
      gap:10px;
      z-index:50;
    }

    /* Progress dots — frosted pill */
    .r-dock-progress {
      background:rgba(255,255,255,0.88);
      backdrop-filter:blur(16px);
      -webkit-backdrop-filter:blur(16px);
      box-shadow:0 4px 20px rgba(0,0,0,0.1);
    }

    /* Arrow buttons — frosted circles */
    .r-dock-back {
      width:44px; height:44px; border-radius:50%;
      background:rgba(255,255,255,0.85);
      backdrop-filter:blur(16px);
      -webkit-backdrop-filter:blur(16px);
      border:1px solid rgba(0,0,0,0.08);
      box-shadow:0 4px 16px rgba(0,0,0,0.1);
    }
    .r-dock-next {
      width:44px; height:44px; border-radius:50%;
      background:rgba(26,106,255,0.88);
      backdrop-filter:blur(16px);
      -webkit-backdrop-filter:blur(16px);
      box-shadow:0 4px 16px rgba(26,106,255,0.35);
    }
    .r-dock-pills { display:none; }
    .r-progress-num { font-size:28px; }

    /* Content grids stack */
    .r-grid-2 { grid-template-columns:1fr; }
    .r-grid-3 { grid-template-columns:1fr; }
    .r-comp { grid-template-columns:1fr !important; }
    .r-comp-name-cell { border-right:none; border-bottom:1px solid #F3F4F6; }
    .r-comp-cell { border-right:none; border-bottom:1px solid #F3F4F6; }
    .r-persona-grid { grid-template-columns:1fr 1fr; }
    .r-swot { grid-template-columns:1fr 1fr; }
    .r-trait-name { width:100px; }
    .r-comp { grid-template-columns:1fr; }
    .r-comp-name-cell { border-right:none; border-bottom:1px solid #F3F4F6; }
    .r-swot { grid-template-columns:1fr 1fr; }
    .r-persona-grid { grid-template-columns:1fr 1fr; }
    .r-card { padding:16px; }
    .r-hero { font-size:clamp(22px,6vw,36px); margin-bottom:20px; }
    .r-manifesto { padding:18px 20px; font-size:15px; }
  }

  @media (max-width:600px) {
    .r-root * { max-width:100%; box-sizing:border-box; }
    .r-header { padding:0 12px; }
    .r-left { padding:6px 20px; }
    .r-nav-item { min-width:48px; padding:5px 8px; }
    .r-nav-name { font-size:8px; }
    .r-main { padding:16px 12px 180px; overflow-x:hidden; min-width:0; width:100%; }
    .r-card { padding:14px; word-break:break-word; overflow-wrap:break-word; }
    .r-card-value { font-size:16px; word-break:break-word; }
    .r-card-text { word-break:break-word; overflow-wrap:break-word; }
    .r-big-num { font-size:56px; }
    .r-dock-back { width:42px; height:42px; }
    .r-dock-next { width:42px; height:42px; }
    .r-hero { font-size:clamp(20px,5.5vw,30px); }
    .r-manifesto { padding:16px 18px; font-size:14px; }
    .r-grid-2 { grid-template-columns:1fr; }
    .r-grid-3 { grid-template-columns:1fr; }
    .r-comp { grid-template-columns:1fr; }
    .r-comp-name-cell { border-right:none; border-bottom:1px solid #F3F4F6; }
    .r-comp-cell { border-right:none; border-bottom:1px solid #F3F4F6; }
    .r-comp-cell:last-child { border-bottom:none; }
    .r-persona-grid { grid-template-columns:1fr; }
    .r-swot { grid-template-columns:1fr; }
    .r-trait-name { width:90px; font-size:11px; }
    .r-trait-score { width:28px; font-size:13px; }
    .r-palette { height:64px; }
    .r-tagline { padding:14px 16px; }
    .r-tagline-line { font-size:clamp(14px,4vw,18px); }
    .r-name-word { font-size:clamp(22px,6vw,32px); }
    .r-name-card { padding:18px; }
    .r-dialog { padding:24px 20px; max-width:calc(100vw - 32px); }
    .r-dialog-title { font-size:18px; }
    .r-cg-card { min-width:0; width:min(460px,90vw); padding:28px 20px; }
    .r-end { padding:24px 16px; }
    .r-end-inner { max-width:100%; }
    .r-end-actions { width:100%; }
    .r-end-btn-p { font-size:13px; }
    .r-end-btn-s { font-size:12px; }
    .r-tones { gap:6px; }
    .r-tone { font-size:12px; padding:5px 12px; }
  }

  @media (max-width:400px) {
    .r-root * { max-width:100% !important; box-sizing:border-box !important; }
    .r-nav-item { min-width:40px; }
    .r-wordmark-text { display:none; }
    .r-nav-name { display:none; }
    .r-nav-num { font-size:9px; }
    .r-main { padding:12px 10px 170px !important; overflow-x:hidden !important; }
    .r-card { padding:12px !important; }
    .r-hero { font-size:clamp(18px,5vw,26px); }
    .r-trait-name { width:75px !important; font-size:10px; }
    .r-cg-card { padding:20px 14px !important; width:calc(100vw - 24px) !important; }
    .r-dialog { padding:18px 14px !important; max-width:calc(100vw - 24px) !important; }
    .r-manifesto { padding:12px 14px !important; font-size:13px; }
    .r-comp { font-size:12px; }
    .r-persona-name { font-size:17px; }
    .r-name-card { padding:12px !important; }
    .r-name-word { font-size:clamp(20px,5.5vw,28px); }
    .r-grid-2, .r-grid-3 { gap:8px !important; }
    .r-swot { gap:8px !important; }
  }
`;

// ═══════════════════════════════════════════════════════════════════════════
// READ-ONLY WRAPPER
// Applied to all generated content. Signals: read, assess, use Edit & Refine
// to change. NOT applied to: input fields, name selection cards, feedback forms.
// ═══════════════════════════════════════════════════════════════════════════

function ReadOnly({ children }) {
  return (
    <div className="r-readonly-section">
      <div className="r-readonly-badge" aria-hidden="true">
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
          <rect x="3" y="11" width="18" height="11" rx="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
        Generated Content
      </div>
      {children}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CONCIERGE OVERLAY
// ═══════════════════════════════════════════════════════════════════════════

function ConciergeOverlay({ state, phaseName, onDone, error }) {
  return (
    <div className="r-concierge" role="dialog" aria-live="polite">
      <div className="r-cg-card">
        <div className="r-cg-badge">
          <span className="r-cg-badge-dot" aria-hidden="true" />
          Brandostro Assistant
        </div>
        {state === 'regenerating' ? (
          <>
            <div className="r-cg-spinner" aria-hidden="true">
              <div className="r-cg-ring r-cg-ring-1" />
              <div className="r-cg-ring r-cg-ring-2" />
            </div>
            <div className="r-cg-title">Refining your {phaseName}</div>
            <div className="r-cg-sub">
              We are rebuilding this phase with your direction applied.
              The quality of the output demands a moment.
            </div>
            <div className="r-cg-dots" aria-hidden="true"><span /><span /><span /></div>
          </>
        ) : state === 'error' ? (
          <>
            <div style={{ fontSize: '32px' }}>⚠</div>
            <div className="r-cg-title">Something went wrong.</div>
            <div className="r-cg-sub">The refinement could not be completed. Your current version has been preserved.</div>
            {error && <div className="r-cg-error">{error}</div>}
            <button className="r-cg-btn" onClick={onDone}>Return to {phaseName}</button>
          </>
        ) : (
          <>
            <div className="r-cg-done" aria-hidden="true">✓</div>
            <div className="r-cg-title">Your {phaseName} is ready.</div>
            <div className="r-cg-sub">
              The refined version reflects your direction. Review the updated
              output and continue when you are satisfied.
            </div>
            <button className="r-cg-btn" onClick={onDone}>Review {phaseName} →</button>
          </>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// END LIGHTBOX
// ═══════════════════════════════════════════════════════════════════════════

function EndLightbox({ selectedName, brand, brief, normalize, logoUrl, onReset, onClose, onGenerateProfile, isDiscover }) {
  const [view, setView] = useState('main');
  const [rating, setRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSent, setFeedbackSent] = useState(false);

  // Export applies normalization — what you see is what you export
  const exportFull = async () => {
    const b = brand;
    const logo = logoUrl || null;
    if (!window.jspdf) {
      await new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
        s.onload = resolve; s.onerror = reject;
        document.head.appendChild(s);
      });
    }
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const W = 210; const H = 297; const M = 20; const CW = W - 2 * M;
    const date = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

    // Sanitize text — remove special chars that break jsPDF
    const s = (t) => {
      if (!t) return '';
      return String(t)
        .replace(/[\u2018\u2019]/g, "'")
        .replace(/[\u201C\u201D]/g, '"')
        .replace(/\u2014/g, '-')
        .replace(/\u2013/g, '-')
        .replace(/\u2026/g, '...')
        .replace(/[^\x00-\x7F]/g, '');
    };

    const colMap = b?.phase03?.colors || {};
    const hex2rgb = h => {
      if (!h) return [26,106,255];
      const x = String(h).replace('#','');
      const f = x.length===3 ? x.split('').map(c=>c+c).join('') : x;
      return [parseInt(f.slice(0,2),16)||0, parseInt(f.slice(2,4),16)||0, parseInt(f.slice(4,6),16)||0];
    };
    const [pr,pg,pb] = hex2rgb(colMap?.primary?.hex || colMap?.primary || '#1A6AFF');
    const [ar,ag,ab] = hex2rgb(colMap?.accent?.hex || colMap?.accent || colMap?.secondary?.hex || '#06B6D4');

    let y = 0;
    const nl = (n=5.5) => { y += n; };
    const chk = (h, addHeader) => {
      if (y + h > H - 16) { doc.addPage(); y = 28; if (addHeader) addHeader(); }
    };

    const hdr = (phaseNum, title) => {
      doc.setFont('helvetica','bold'); doc.setFontSize(7); doc.setTextColor(pr,pg,pb);
      doc.text(`PHASE ${phaseNum}`, M, 13);
      doc.setFont('helvetica','normal'); doc.setTextColor(130,140,160);
      doc.text(` · ${title.toUpperCase()}`, M+16, 13);
      doc.text(s(selectedName)||'', W-M, 13, { align:'right' });
      doc.setDrawColor(220,228,240); doc.setLineWidth(0.2);
      doc.line(M, 16, W-M, 16);
      doc.setFontSize(7); doc.setTextColor(180,190,205);
      doc.text('Brandostro · brandostro.com', W/2, H-8, { align:'center' });
    };

    const label = (txt) => {
      nl(2);
      doc.setFont('helvetica','bold'); doc.setFontSize(7.5); doc.setTextColor(pr,pg,pb);
      doc.text(s(txt).toUpperCase(), M, y);
      doc.setDrawColor(pr,pg,pb); doc.setLineWidth(0.25);
      doc.line(M, y+1.5, M+9, y+1.5);
      nl(7);
    };

    const body = (txt, indent=0) => {
      if (!txt) return;
      doc.setFont('helvetica','normal'); doc.setFontSize(9.5); doc.setTextColor(55,65,85);
      const lines = doc.splitTextToSize(s(txt), CW-indent);
      lines.forEach(ln => { chk(6); doc.text(ln, M+indent, y); nl(5.5); });
      nl(3);
    };

    const accent = (txt) => {
      if (!txt) return;
      doc.setFont('helvetica','bold'); doc.setFontSize(12); doc.setTextColor(pr,pg,pb);
      const lines = doc.splitTextToSize(s(txt), CW);
      lines.forEach(ln => { chk(8); doc.text(ln, M, y); nl(7); });
      nl(3);
    };

    // ── COVER ──────────────────────────────────────────────────────────────
    doc.setFillColor(5,12,26); doc.rect(0,0,W,H,'F');
    doc.setFillColor(pr,pg,pb); doc.rect(0,0,5,H,'F');
    doc.setFillColor(ar,ag,ab); doc.rect(0,H-2,W,2,'F');
    doc.setFont('helvetica','bold'); doc.setFontSize(8); doc.setTextColor(ar,ag,ab);
    doc.text('BRANDOSTRO', M+2, 30);
    doc.setFontSize(34); doc.setTextColor(255,255,255);
    const nLines = doc.splitTextToSize(s(selectedName)||'Brand Report', CW-10);
    let ny = 80;
    nLines.forEach(ln => { doc.text(ln, M+2, ny); ny+=13; });
    doc.setFont('helvetica','normal'); doc.setFontSize(10); doc.setTextColor(ar,ag,ab);
    doc.text('BRAND INTELLIGENCE REPORT', M+2, ny+8);
    doc.setFontSize(8.5); doc.setTextColor(140,160,190);
    doc.text(date, M+2, ny+18);
    doc.setDrawColor(ar,ag,ab); doc.setLineWidth(0.4);
    doc.line(M+2, ny+24, M+36, ny+24);
    if (brief) {
      doc.setFont('helvetica','normal'); doc.setFontSize(9); doc.setTextColor(180,200,220);
      const bl = doc.splitTextToSize(s(brief), CW-8);
      bl.slice(0,4).forEach((ln,i) => doc.text(ln, M+2, ny+34+(i*6)));
    }


    // ── PHASE 00 — BRAND DNA ───────────────────────────────────────────────
    const p00 = b?.phase00 || {};
    if (p00.manifesto || p00.belief) {
      doc.addPage(); y = 28;
      hdr('00','Brand DNA');
      doc.setFont('helvetica','bold'); doc.setFontSize(20); doc.setTextColor(pr,pg,pb);
      doc.text('Brand DNA', M, y); nl(12);
      if (p00.manifesto) {
        doc.setFillColor(246,248,252); doc.setDrawColor(220,228,240); doc.setLineWidth(0.25);
        doc.setFont('helvetica','bold'); doc.setFontSize(10.5); doc.setTextColor(15,23,42);
        const ml = doc.splitTextToSize(s(p00.manifesto), CW-10);
        const bh = ml.length*6.5+10;
        doc.roundedRect(M, y-3, CW, bh, 1.5, 1.5, 'FD');
        doc.setFillColor(pr,pg,pb); doc.rect(M, y-3, 3, bh, 'F');
        ml.forEach(ln => { doc.text(ln, M+8, y); nl(6.5); });
        nl(14);
      }
      if (p00.belief) { label('The Belief'); body(p00.belief); }
      if (p00.problem) { label('The Problem'); body(p00.problem); }
      if (p00.change) { label('The Change'); body(p00.change); }
    }

    // ── PHASE 01 — COMPANY IDENTITY ───────────────────────────────────────
    const p01 = b?.phase01 || {};
    if (p01.mission || p01.vision) {
      doc.addPage(); y = 28;
      hdr('01','Company Identity');
      doc.setFont('helvetica','bold'); doc.setFontSize(20); doc.setTextColor(pr,pg,pb);
      doc.text('Company Identity', M, y); nl(12);
      if (p01.mission) { label('Mission'); accent(p01.mission); }
      if (p01.vision) { label('Vision'); body(p01.vision); }
      if (p01.offerings) { label('Offerings'); body(p01.offerings); }
      if (Array.isArray(p01.values)) {
        label('Core Values');
        p01.values.forEach((v,i) => {
          chk(18);
          doc.setFont('helvetica','bold'); doc.setFontSize(10); doc.setTextColor(pr,pg,pb);
          doc.text(`${String(i+1).padStart(2,'0')}  ${s(v.name||'')}`, M, y); nl(6);
          doc.setFont('helvetica','normal'); doc.setFontSize(9); doc.setTextColor(71,85,105);
          const vl = doc.splitTextToSize(s(v.definition||''), CW-6);
          vl.forEach(ln => { chk(6); doc.text(ln, M+6, y); nl(5.5); });
          nl(4);
        });
      }
    }

    // ── PHASE 03 — VISUAL ARCHITECTURE ───────────────────────────────────
    const p03 = b?.phase03 || {};
    const cols = Object.values(colMap).filter(c=>c?.hex);
    if (p03.archetype || cols.length) {
      doc.addPage(); y = 28;
      hdr('03','Visual Architecture');
      doc.setFont('helvetica','bold'); doc.setFontSize(20); doc.setTextColor(pr,pg,pb);
      doc.text('Visual Architecture', M, y); nl(12);
      if (p03.archetype) { accent(p03.archetype); body(p03.archetypeDesc); }
      if (cols.length) {
        label('Colour Palette');
        const sw=24, sg=5, perRow=4;
        cols.forEach((c,i) => {
          const [cr,cg,cb] = hex2rgb(c.hex);
          const col=i%perRow, row=Math.floor(i/perRow);
          doc.setFillColor(cr,cg,cb);
          doc.rect(M+(col*(sw+sg)), y+(row*24), sw, 11, 'F');
          doc.setFont('helvetica','normal'); doc.setFontSize(6.5); doc.setTextColor(60,70,90);
          doc.text(s(c.name||c.hex), M+(col*(sw+sg)), y+(row*24)+15);
          doc.setTextColor(130,140,155);
          doc.text(s(c.hex), M+(col*(sw+sg)), y+(row*24)+19);
        });
        nl(24*(Math.ceil(cols.length/perRow))+8);
      }
      if (p03.logoDirection) { label('Logo Direction'); body(p03.logoDirection); }
      if (p03.typography?.display?.font) {
        label('Typography');
        doc.setFont('helvetica','normal'); doc.setFontSize(9.5); doc.setTextColor(55,65,85);
        doc.text(`Display: ${s(p03.typography.display.font)}`, M, y); nl(6);
        if (p03.typography.body?.font) { doc.text(`Body: ${s(p03.typography.body.font)}`, M, y); nl(6); }
        nl(4);
      }
    }

    // ── PHASE 04 — BRAND MESSAGING ────────────────────────────────────────
    const p04 = b?.phase04 || {};
    if (p04.positioning || p04.taglines) {
      doc.addPage(); y = 28;
      hdr('04','Brand Messaging');
      doc.setFont('helvetica','bold'); doc.setFontSize(20); doc.setTextColor(pr,pg,pb);
      doc.text('Brand Messaging', M, y); nl(12);
      if (p04.positioning) { label('Positioning'); accent(p04.positioning); }
      if (Array.isArray(p04.taglines)) {
        label('Taglines');
        p04.taglines.forEach(t => {
          chk(16);
          doc.setFont('helvetica','bold'); doc.setFontSize(11); doc.setTextColor(ar,ag,ab);
          doc.text(`"${s(t.line||t)}"`, M, y); nl(7);
          if (t.rationale) { body(t.rationale, 4); }
        });
      }
      if (p04.toneOfVoice) { label('Tone of Voice'); body(p04.toneOfVoice); }
      if (p04.pitchShort) { label('Elevator Pitch'); body(p04.pitchShort); }
    }

    // ── PHASE 05 — STRATEGIC POSITIONING ─────────────────────────────────
    const p05 = b?.phase05 || {};
    if (p05.winStatement || p05.swot) {
      doc.addPage(); y = 28;
      hdr('05','Strategic Positioning');
      doc.setFont('helvetica','bold'); doc.setFontSize(20); doc.setTextColor(pr,pg,pb);
      doc.text('Strategic Positioning', M, y); nl(12);
      if (p05.winStatement) { label('Win Statement'); accent(p05.winStatement); }
      if (p05.swot) {
        label('SWOT');
        [['Strengths',p05.swot.strengths],['Weaknesses',p05.swot.weaknesses],['Opportunities',p05.swot.opportunities],['Threats',p05.swot.threats]].forEach(([lbl,items]) => {
          if (!Array.isArray(items)||!items.length) return;
          chk(10);
          doc.setFont('helvetica','bold'); doc.setFontSize(8.5); doc.setTextColor(pr,pg,pb);
          doc.text(lbl, M, y); nl(5);
          items.forEach(item => {
            chk(6);
            doc.setFont('helvetica','normal'); doc.setFontSize(9); doc.setTextColor(71,85,105);
            doc.text(`• ${s(item)}`, M+4, y); nl(5.5);
          });
          nl(3);
        });
      }
      if (Array.isArray(p05.competitors)) {
        label('Competitive Landscape');
        p05.competitors.forEach(c => {
          chk(16);
          doc.setFont('helvetica','bold'); doc.setFontSize(10); doc.setTextColor(pr,pg,pb);
          doc.text(s(c.name||''), M, y); nl(6);
          if (c.vulnerability) { doc.setFont('helvetica','normal'); doc.setFontSize(9); doc.setTextColor(71,85,105); doc.text(`Gap: ${s(c.vulnerability)}`, M+4, y); nl(5.5); }
          if (c.ourPosition) { doc.text(`Position: ${s(c.ourPosition)}`, M+4, y); nl(5.5); }
          nl(3);
        });
      }
    }

    // ── PHASE 06 — CUSTOMER EXPERIENCE ───────────────────────────────────
    const p06 = b?.phase06 || {};
    if (p06.signatureMoment || p06.journey) {
      doc.addPage(); y = 28;
      hdr('06','Customer Experience');
      doc.setFont('helvetica','bold'); doc.setFontSize(20); doc.setTextColor(pr,pg,pb);
      doc.text('Customer Experience', M, y); nl(12);
      if (p06.signatureMoment) { label('Signature Moment'); accent(p06.signatureMoment); }
      if (Array.isArray(p06.journey)) {
        label('Customer Journey');
        p06.journey.forEach((j,i) => {
          chk(20);
          doc.setFillColor(246,248,252); doc.setDrawColor(220,228,240); doc.setLineWidth(0.2);
          const jlines = doc.splitTextToSize(s(j.brandBehaviour||''), CW-10);
          doc.roundedRect(M, y-3, CW, 18+(jlines.length*5), 1.5, 1.5, 'FD');
          doc.setFillColor(ar,ag,ab); doc.rect(M, y-3, 3, 18+(jlines.length*5), 'F');
          doc.setFont('helvetica','bold'); doc.setFontSize(9); doc.setTextColor(pr,pg,pb);
          doc.text(`${String(i+1).padStart(2,'0')}  ${s(j.stage||'')}`, M+6, y); nl(6);
          if (j.touchpoint) { doc.setFont('helvetica','normal'); doc.setFontSize(8.5); doc.setTextColor(71,85,105); doc.text(`${s(j.touchpoint)}`, M+6, y); nl(5); }
          nl(10);
        });
        nl(6);
      }
      if (p06.gapAnalysis) { chk(30); nl(6); label('Gap Analysis'); body(p06.gapAnalysis); }
    }

    // ── PHASE 07 — SEO FOUNDATION ─────────────────────────────────────────
    const p07 = b?.phase07 || {};
    if (p07.hero || p07.keywords) {
      doc.addPage(); y = 28;
      hdr('07','SEO Foundation');
      doc.setFont('helvetica','bold'); doc.setFontSize(20); doc.setTextColor(pr,pg,pb);
      doc.text('SEO Foundation', M, y); nl(12);
      if (p07.hero?.headline) { label('Hero Headline'); accent(p07.hero.headline); }
      if (p07.hero?.subheadline) { label('Subheadline'); body(p07.hero.subheadline); }
      if (p07.metaTitle) { label('Meta Title'); body(p07.metaTitle); }
      if (p07.metaDescription) { label('Meta Description'); body(p07.metaDescription); }
      if (Array.isArray(p07.keywords) && p07.keywords.length) {
        label('Keywords');
        doc.setFont('helvetica','normal'); doc.setFontSize(9); doc.setTextColor(55,65,85);
        doc.text(p07.keywords.map(k=>s(k)).join('  ·  '), M, y); nl(8);
      }
    }

    // ── BACK COVER ────────────────────────────────────────────────────────
    doc.addPage();
    doc.setFillColor(5,12,26); doc.rect(0,0,W,H,'F');
    doc.setFillColor(pr,pg,pb); doc.rect(0,0,5,H,'F');
    doc.setFillColor(ar,ag,ab); doc.rect(0,H-2,W,2,'F');
    doc.setFont('helvetica','bold'); doc.setFontSize(26); doc.setTextColor(255,255,255);
    doc.text(s(selectedName)||'Brandostro', W/2, H/2-6, { align:'center' });
    doc.setFont('helvetica','normal'); doc.setFontSize(9); doc.setTextColor(ar,ag,ab);
    doc.text('BRAND INTELLIGENCE REPORT', W/2, H/2+5, { align:'center' });
    doc.setDrawColor(ar,ag,ab); doc.setLineWidth(0.3);
    doc.line(W/2-22, H/2+10, W/2+22, H/2+10);
    doc.setFontSize(7.5); doc.setTextColor(80,100,130);
    doc.text('Powered by Brandostro · brandostro.com', W/2, H/2+18, { align:'center' });

    const safe = (s(selectedName)||'brand').toLowerCase().replace(/\s+/g,'-');
    doc.save(`${safe}-brand-report.pdf`);
  };


  if (view === 'website_cta') return (
    <div className="r-end">
      <div className="r-end-bg" />
      <div className="r-end-wordmark"><img src="/assets/monogram.png" alt="Brandostro" width="22" height="22" style={{ borderRadius:'6px', flexShrink:0, opacity:0.6 }} /> Brandostro</div>
      <div className="r-end-inner">
        <div className="r-end-sub-label">Next Step</div>
        <div className="r-end-sub-title">Your brand is live-ready</div>
        <div className="r-end-sub-text">Your complete brand system is built. Head to Phase 07 to generate your branded website — a complete HTML landing page written, designed, and built from your brand strategy.</div>
        <div className="r-end-actions">
          <button className="r-end-btn-p" onClick={() => { setView('main'); onClose && onClose(); }}>Go to Digital Presence →</button>
          {isDiscover
            ? <a href="https://www.brandostro.com/pricing" target="_blank" rel="noreferrer" className="r-end-btn-s" style={{ display:'block', textAlign:'center', textDecoration:'none', lineHeight:'42px' }}>Upgrade to Download Brand Report →</a>
            : <button className="r-end-btn-s" onClick={exportFull}>Download Brand Report First ↓</button>
          }
        </div>
        <button className="r-end-back" onClick={() => setView('main')}>← Back to options</button>
      </div>
    </div>
  );

  if (view === 'feedback') return (
    <div className="r-end">
      <div className="r-end-bg" />
      <div className="r-end-wordmark"><img src="/assets/monogram.png" alt="Brandostro" width="22" height="22" style={{ borderRadius:'6px', flexShrink:0, opacity:0.6 }} /> Brandostro</div>
      <div className="r-end-inner">
        {feedbackSent ? (
          <div className="r-feedback-sent">
            <div className="r-feedback-sent-icon">✦</div>
            <div className="r-feedback-sent-title">Thank you.</div>
            <div className="r-feedback-sent-sub">Your feedback shapes the platform. Every response is read personally.</div>
            <div className="r-end-actions" style={{ marginTop: '24px' }}>
              <button className="r-end-btn-s" onClick={onClose}>Continue Exploring</button>
              <button className="r-end-btn-s" onClick={onReset}>Build Another Brand</button>
            </div>
          </div>
        ) : (
          <>
            <div className="r-end-sub-label">Your Experience</div>
            <div className="r-end-sub-title">How did Brandostro perform?</div>
            <div className="r-end-sub-text">Rate your experience and tell us what you thought. We read every response personally.</div>
            <div className="r-stars">{[1,2,3,4,5].map(n => <button key={n} className={`r-star ${rating >= n ? 'lit' : ''}`} onClick={() => setRating(n)}>{rating >= n ? '★' : '☆'}</button>)}</div>
            <textarea className="r-dialog-textarea" style={{ marginBottom:'14px', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', color:'#EDF1FF' }} placeholder="What surprised you most? What would you change?" value={feedbackText} onChange={e => setFeedbackText(e.target.value)} rows={3} />
            <div className="r-end-actions">
              <button className="r-end-btn-p" disabled={rating === 0} style={{ opacity: rating === 0 ? 0.4 : 1 }} onClick={async () => {
                try {
                  await fetch('https://brandostro-engine-253902457893.us-central1.run.app/save-support', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      memberId: session?.memberId,
                      token: session?.token,
                      email: session?.email || '',
                      plan: session?.plan || 'discover',
                      name: session?.name || '',
                      message: `FEEDBACK [${rating}/5 stars] — Brand: ${selectedName || 'unknown'}\n\n${feedbackText}`,
                    }),
                  });
                } catch {}
                setFeedbackSent(true);
              }}>Send Feedback</button>
            </div>
            <button className="r-end-back" onClick={() => setView('main')}>← Back to options</button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="r-end">
      <div className="r-end-bg" />
      <div className="r-end-wordmark"><img src="/assets/monogram.png" alt="Brandostro" width="22" height="22" style={{ borderRadius:'6px', flexShrink:0, opacity:0.6 }} /> Brandostro</div>
      <div className="r-end-inner">
        <div className="r-end-fp">
          <div className="r-end-fp-ring r-end-fp-r1" />
          <div className="r-end-fp-ring r-end-fp-r2" />
          <div className="r-end-fp-core" />
        </div>
        <div className="r-end-counter">10 Phases · Brand Complete</div>
        <div className="r-end-headline">Your brand<br />is complete.</div>
        {selectedName && <div className="r-end-name">{selectedName}</div>}
        <div className="r-end-stats">
          <div className="r-end-stat"><div className="r-end-stat-num">10</div><div className="r-end-stat-label">Phases Built</div></div>
          <div className="r-end-sep" />
          <div className="r-end-stat"><div className="r-end-stat-num">200×</div><div className="r-end-stat-label">Agency Value</div></div>
        </div>
        <div className="r-end-actions">
          {isDiscover
            ? <a href="https://www.brandostro.com/pricing" target="_blank" rel="noreferrer" className="r-end-btn-p" style={{ display:'block', textAlign:'center', textDecoration:'none', lineHeight:'50px' }}>Upgrade to Download Brand Report →</a>
            : <button className="r-end-btn-p" onClick={exportFull}>Download Brand Report ↓</button>
          }
          <button className="r-end-btn-s" onClick={() => setView('feedback')}>Share Your Experience</button>
          <button className="r-end-btn-s" onClick={onClose}>Continue Exploring</button>
          <button className="r-end-btn-s" onClick={onReset}>Build Another Brand →</button>
          <button className="r-end-btn-s" onClick={() => { if(navigator.share) { navigator.share({ title: 'Brandostro', text: 'I just built my brand in under 2 minutes with Brandostro', url: 'https://www.brandostro.com' }); } else { navigator.clipboard.writeText('https://www.brandostro.com'); alert('Link copied!'); } }}>Share Brandostro ↗</button>
        </div>
        <div className="r-end-note">Your brand session remains available while this window is open.</div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN RESULTS COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export default function Results({ brand: b, brief, confirmedName: externalConfirmedName, userProvidedName, onReset, onRegenerate, onNameConfirmed, session, savedBrandId, onLogoSave, onSiteHtmlSave, onDashboard, initialLogoGallery, initialLogoRegenCount, initialSiteHtml }) {
  const [idx, setIdx] = useState(0);
  // Plan enforcement
  const plan = session?.plan || 'studio'; // default studio if no session
  const isDiscover = plan === 'discover';
  const GATED_PHASES = ['p06','p07','p08','p10']; // locked for Discover
  const [fadingOut, setFadingOut] = useState(false);
  const [transMsg, setTransMsg] = useState('');
  const [entering, setEntering] = useState(true);
  // ID-based name selection — immune to array index shifts
  const [selectedNameId, setSelectedNameId] = useState(null);
  // Locked immediately if App has already confirmed a name (Step 2 complete)
  const [nameLocked, setNameLocked] = useState(!!externalConfirmedName);
  const [dialog, setDialog] = useState(null);
  const [userNameInput, setUserNameInput] = useState('');
  const [customNames, setCustomNames] = useState([]);
  const [reviseInput, setReviseInput] = useState('');
  const [conciergeState, setConciergeState] = useState(null);
  const [conciergeError, setConciergeError] = useState(null);
  // Ref guard — prevents auto-advance firing more than once
  const autoAdvancedRef = useRef(false);

  // Shared logo URL — set by PhaseVisual, consumed by PhaseDigital and EndLightbox
  const [sharedLogoUrl, setSharedLogoUrl] = useState(
    Array.isArray(initialLogoGallery) && initialLogoGallery.length > 0 ? initialLogoGallery[0] : null
  );
  const [sharedLogoGallery, setSharedLogoGallery] = useState(
    Array.isArray(initialLogoGallery) ? initialLogoGallery : []
  );
  const [sharedActiveIdx, setSharedActiveIdx]     = useState(
    Array.isArray(initialLogoGallery) && initialLogoGallery.length > 0 ? 0 : -1
  );
  const [sharedRegenCount, setSharedRegenCount]   = useState(initialLogoRegenCount || 0);
  const [sharedSiteHtml, setSharedSiteHtml]       = useState(initialSiteHtml || null);
  const [sharedSiteState, setSharedSiteState]     = useState(initialSiteHtml ? 'done' : 'idle');

  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState(null);

  const phase = PHASES[idx];

  // After Step 2 completes, Results remounts with externalConfirmedName already set.
  // 1. Find the matching name card in allNames and set selectedNameId — this makes
  //    Phase 00 show the confirmed name as locked-selected, not locked-other.
  // 2. Lock Phase 00 and advance to Phase 01 (Brand DNA).
  // Ref guard ensures this fires exactly once per session.
  useEffect(() => {
    if (externalConfirmedName && !autoAdvancedRef.current) {
      autoAdvancedRef.current = true;
      setNameLocked(true);
      // Find the matching name in allNames and select it so Phase 00
      // correctly highlights it as locked-selected when user navigates back.
      const engineNames = (b.phase02?.names || []).map((n, i) => ({ ...n, _id: `engine-${i}` }));
      const match = engineNames.find(
        n => n.name && n.name.toLowerCase() === externalConfirmedName.toLowerCase()
      );
      if (match) setSelectedNameId(match._id);
      setTransMsg(TRANSITION_MSGS[1]);
      setIdx(1);
      setTimeout(() => setTransMsg(''), 2000);
    }
  }, []); // fires once on mount when externalConfirmedName is already set

  // Build allNames with stable IDs — prevents index-shift bugs
  const allNames = useMemo(() => {
    const engineNames = (b.phase02?.names || []).map((n, i) => ({ ...n, _id: `engine-${i}` }));
    return [...customNames, ...engineNames];
  }, [customNames, b.phase02]);

  // Resolve selected name from stable ID
  const selectedNameObj = useMemo(
    () => selectedNameId ? allNames.find(n => n._id === selectedNameId) || null : null,
    [selectedNameId, allNames]
  );
  // After Step 2, externalConfirmedName is the source of truth.
  // Before Step 2, use the locally selected name.
  const currentSelectedName = externalConfirmedName || selectedNameObj?.name || null;


  // Name replacer — runs at render time, layer 2 of name consistency
  const nameReplacer = useMemo(
    () => buildReplacer(b, currentSelectedName),
    [b, currentSelectedName]
  );

  // normalize — applies name replacement to any data object before display
  const normalize = useCallback((data) => {
    if (!nameReplacer || !currentSelectedName) return data;
    return fixObj(data, nameReplacer, currentSelectedName);
  }, [nameReplacer, currentSelectedName]);

  // Pitch deck generation — loads PptxGenJS from CDN, generates client-side
  const handleGenerateProfile = async () => {
    if (!currentSelectedName) return;
    setProfileLoading(true);
    setProfileError(null);
    try {
      await generateCompanyProfile(b, currentSelectedName, brief, sharedLogoUrl);
    } catch (err) {
      console.error('Pitch deck error:', err);
      setProfileError('Could not generate company profile. Please try again.');
    } finally {
      setProfileLoading(false);
    }
  };

  // canAdvance uses currentSelectedName — works whether name came from local
  // selection (Step 1) or from externalConfirmedName (Step 2 complete)
  const canAdvance = () => phase.id === 'p02' ? currentSelectedName !== null : true;

  const goNext = () => {
    if (idx >= PHASES.length - 1) { setDialog('end'); return; }
    if (phase.id === 'p02') setNameLocked(true);
    setTransMsg(TRANSITION_MSGS[idx + 1] || '');
    setFadingOut(true);
    setTimeout(() => {
      setIdx(i => i + 1);
      setFadingOut(false);
      setEntering(true);
      // Scroll content to top on phase change
      const mainEl = document.querySelector('.r-main');
      if (mainEl) mainEl.scrollTop = 0;
      setTimeout(() => setTransMsg(''), 1400);
    }, 280);
  };

  const goBack = () => {
    if (idx === 0) return;
    setIdx(i => i - 1);
    setEntering(true);
    const mainEl = document.querySelector('.r-main');
    if (mainEl) mainEl.scrollTop = 0;
  };

  useEffect(() => {
    setEntering(true);
    const t = setTimeout(() => setEntering(false), 500);
    return () => clearTimeout(t);
  }, [idx]);

  const handleNameSelect = (id) => {
    if (nameLocked) { setDialog('name-locked'); return; }
    setSelectedNameId(id);
    setDialog('name-selected');
  };

  const handleAddUserName = () => {
    // Block adding names after lock — would shift display but not break logic
    // (IDs are stable), however it's semantically wrong to add after commit.
    if (nameLocked || !userNameInput.trim()) return;
    const id = `custom-${Date.now()}`;
    setCustomNames(prev => [{
      _id: id,
      name: userNameInput.trim(),
      type: 'Client Selected',
      rationale: 'Name entered directly by the client.',
      domain: userNameInput.trim().toLowerCase().replace(/\s+/g, '') + '.com, verify availability',
      trademark: 'Verify',
      clientSelected: true,
    }, ...prev]);
    setUserNameInput('');
  };

  const handleRefine = async () => {
    if (!reviseInput.trim()) return;
    const direction = reviseInput.trim();
    setReviseInput('');
    setDialog(null);
    setConciergeState('regenerating');
    setConciergeError(null);
    try {
      const result = await onRegenerate(phase.dataKey, direction, currentSelectedName);
      if (result && result.success === false) {
        setConciergeState('error');
        setConciergeError(result.error || 'Unknown error');
      } else {
        setConciergeState('done');
        // Scroll to top of content area so user sees the updated output
        setTimeout(() => {
          const mainEl = document.querySelector('.r-main');
          if (mainEl) mainEl.scrollTop = 0;
        }, 100);
      }
    } catch (err) {
      setConciergeState('error');
      setConciergeError(err.message);
    }
  };

  // Export — applies normalization so what you export matches what you see
  const exportCurrentPhase = () => {
    const raw = b[phase.dataKey];
    const data = normalize(raw);
    const text = [
      `BRANDOSTRO, PHASE ${phase.num}, ${phase.title.toUpperCase()}`,
      '═'.repeat(60), '',
      flattenObj(data)
    ].join('\n');
    triggerDownload(text, `brandostro-${phase.num}-${phase.title.toLowerCase().replace(/\s+/g, '-')}.txt`);
  };

  const handleNextClick = () => {
    if (!canAdvance()) { setDialog('name-required'); return; }
    // After first advance past Phase 00, subsequent visits show a confirmation
    // only on the very first advance (nameLocked is false then true)
    if (phase.id === 'p02' && !nameLocked) { setDialog('name-selected'); return; }
    goNext();
  };

  return (
    <>
      <style>{styles}</style>

      {/* Concierge overlay — all regeneration states */}
      {conciergeState && (
        <ConciergeOverlay
          state={conciergeState}
          phaseName={phase.title}
          error={conciergeError}
          onDone={() => { setConciergeState(null); setConciergeError(null); }}
        />
      )}

      {/* End lightbox — passes normalize for correct export */}
      {dialog === 'end' && (
        <EndLightbox
          selectedName={currentSelectedName}
          brand={b} brief={brief}
          normalize={normalize}
          logoUrl={sharedLogoUrl}
          onReset={() => { setDialog(null); onReset(); }}
          onClose={() => setDialog(null)}
          onGenerateProfile={handleGenerateProfile}
              isDiscover={isDiscover}
        />
      )}

      {/* Name locked — amber info tone, not error */}
      {dialog === 'name-locked' && (
        <div className="r-dialog-overlay" onClick={() => setDialog(null)}>
          <div className="r-dialog r-dialog-info" onClick={e => e.stopPropagation()}>
            <div className="r-dialog-badge" style={{ background:'#FFFBEB', border:'1px solid #FDE68A', color:'#92400E' }}>Brandostro Assistant</div>
            <div className="r-dialog-title">{currentSelectedName} is your confirmed name.</div>
            <div className="r-dialog-sub">
              Your brand name was confirmed when you proceeded to Brand DNA. Every phase has been built around <strong>{currentSelectedName}</strong>.<br /><br />
              To explore a different name, start a new brand session. Your current session is preserved.
            </div>
            <div className="r-dialog-actions">
              <button className="r-dialog-btn-primary" onClick={() => setDialog(null)}>Continue with {currentSelectedName}</button>
              <button className="r-dialog-btn-secondary" onClick={() => { setDialog(null); onReset(); }}>Start a New Brand Session</button>
            </div>
          </div>
        </div>
      )}

      {/* Name selected — first advance confirmation */}
      {dialog === 'name-selected' && (
        <div className="r-dialog-overlay" onClick={() => setDialog(null)}>
          <div className="r-dialog" onClick={e => e.stopPropagation()}>
            <div className="r-dialog-badge">Brandostro Assistant</div>
            <div className="r-dialog-title">{currentSelectedName ? `${currentSelectedName} , an excellent choice.` : 'Name selected.'}</div>
            <div className="r-dialog-sub">
              We will now build your complete brand system with <strong>{currentSelectedName}</strong> as the confirmed name, woven into every phase, every section, every word. This takes approximately 60 seconds.
            </div>
            <div className="r-dialog-actions">
              <button
                className="r-dialog-btn-primary"
                onClick={() => {
                  setDialog(null);
                  setNameLocked(true);
                  // ── STEP 2: Trigger full brand generation with confirmed name ──
                  if (onNameConfirmed && currentSelectedName) {
                    onNameConfirmed(currentSelectedName);
                  } else {
                    goNext();
                  }
                }}
              >
                Build My Brand with {currentSelectedName} →
              </button>
              <button className="r-dialog-btn-secondary" onClick={() => setDialog(null)}>
                Choose a Different Name
              </button>
            </div>
            <div className="r-dialog-note">Additional name generations may incur a nominal charge.</div>
          </div>
        </div>
      )}

      {/* Name required — gated navigation */}
      {dialog === 'name-required' && (
        <div className="r-dialog-overlay" onClick={() => setDialog(null)}>
          <div className="r-dialog r-dialog-info" onClick={e => e.stopPropagation()}>
            <div className="r-dialog-badge" style={{ background:'#FFFBEB', border:'1px solid #FDE68A', color:'#92400E' }}>Brandostro Assistant</div>
            <div className="r-dialog-title">Select a name to continue.</div>
            <div className="r-dialog-sub">Your brand name is the foundation every subsequent phase builds upon. Review the candidates below and select the one that carries your ambition, or add your own using the field above the list.</div>
            <div className="r-dialog-actions">
              <button className="r-dialog-btn-primary" onClick={() => setDialog(null)}>Review the Names →</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit and Refine dialog */}
      {dialog === 'upgrade' && (
        <div style={{ position:'fixed', inset:0, zIndex:400, background:'rgba(5,12,26,0.7)', backdropFilter:'blur(16px)', display:'flex', alignItems:'center', justifyContent:'center', padding:'24px' }} onClick={() => setDialog(null)}>
          <div style={{ background:'#FFFFFF', borderRadius:'20px', padding:'40px', maxWidth:'460px', width:'100%', textAlign:'center' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize:'32px', marginBottom:'16px' }}>🔒</div>
            <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:'22px', fontWeight:'700', letterSpacing:'-0.03em', color:'#0A0A1A', marginBottom:'8px' }}>Unlock the full system</div>
            <div style={{ fontSize:'14px', color:'#6B7280', lineHeight:'1.7', marginBottom:'28px' }}>
              Upgrade to Brand Studio to unlock all 10 phases — Customer Experience, Digital Presence, Go-To-Market and Launch Activation — plus your logo, website and complete brand exports.
            </div>
            <a href="https://www.brandostro.com/pricing" target="_blank" rel="noreferrer" style={{ display:'block', height:'52px', lineHeight:'52px', background:'linear-gradient(135deg,#094BCC,#1A6AFF)', borderRadius:'10px', fontFamily:"'Space Grotesk',sans-serif", fontSize:'15px', fontWeight:'600', color:'#fff', textDecoration:'none', marginBottom:'12px', boxShadow:'0 6px 24px rgba(26,106,255,0.3)' }}>
              Upgrade to Brand Studio →
            </a>
            <button onClick={() => setDialog(null)} style={{ background:'none', border:'none', fontSize:'13px', color:'#9CA3AF', cursor:'pointer' }}>Continue exploring Discover</button>
            <div style={{ marginTop:'12px', fontSize:'11px', color:'#CBD5E1' }}>From $99 · One-time payment · No subscription</div>
          </div>
        </div>
      )}

      {dialog === 'revise' && (
        <div className="r-dialog-overlay" onClick={() => setDialog(null)}>
          <div className="r-dialog" onClick={e => e.stopPropagation()}>
            <div className="r-dialog-badge">Brandostro Assistant</div>
            <div className="r-dialog-title">Edit and Refine {phase.title}</div>
            <div className="r-dialog-sub">
              Describe the direction you'd like this phase to take.
              {currentSelectedName && ` All output will be anchored to ${currentSelectedName}.`}
              {' '}Be specific, the quality of your direction determines the quality of the output.
            </div>
            <div style={{ background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:'8px', padding:'10px 14px', marginBottom:'16px', fontSize:'12px', color:'#92400E', lineHeight:'1.5' }}>
              ⚠ This will regenerate the entire {phase.title} section. Your current version will be replaced. Export this phase first if you want to preserve it.
            </div>
            <textarea
              className="r-dialog-textarea"
              placeholder={`e.g. "More premium positioning, less technical language. Authoritative and confident in tone."${currentSelectedName ? ` The brand name is ${currentSelectedName}.` : ''}`}
              value={reviseInput}
              onChange={e => setReviseInput(e.target.value)}
              autoFocus
            />
            <div className="r-dialog-actions">
              <button className="r-dialog-btn-primary" onClick={handleRefine} disabled={!reviseInput.trim()}>
                Refine this Phase →
              </button>
              <button className="r-dialog-btn-secondary" onClick={() => setDialog(null)}>Keep Current Version</button>
            </div>
            <div className="r-dialog-note">Refinements may incur a nominal charge on some plans.</div>
          </div>
        </div>
      )}

      {/* Main interface */}
      {dialog !== 'end' && (
        <div className="r-root">
          <div className="r-bg" aria-hidden="true">
            <div className="r-bg-orb r-bg-orb-1" />
            <div className="r-bg-orb r-bg-orb-2" />
          </div>

          {/* Concierge transition toast */}
          {transMsg && <div className="r-trans-toast" role="status">{transMsg}</div>}

          <div className="r-layout">
            {/* Header */}
            <header className="r-header">
              <div className="r-wordmark">
                <img src="/assets/monogram.png" alt="Brandostro" width="24" height="24" style={{ borderRadius:'6px', flexShrink:0 }} />
                <span className="r-wordmark-text">Brandostro</span>
                {currentSelectedName && <span className="r-header-name">· {currentSelectedName}</span>}
              </div>
              <div className="r-header-center">
                Phase <strong>{phase.num}</strong> · {phase.tag}
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:'6px', flexShrink:0 }}>
                {session?.plan && (
                  <div className="r-header-plan-pill" style={{ fontSize:'9px', fontWeight:'700', letterSpacing:'0.1em', textTransform:'uppercase', color: session.plan==='pro' ? '#06B6D4' : session.plan==='studio' ? '#1A6AFF' : '#8B5CF6', padding:'4px 10px', border:`1px solid ${session.plan==='pro' ? 'rgba(6,182,212,0.3)' : session.plan==='studio' ? 'rgba(26,106,255,0.3)' : 'rgba(139,92,246,0.3)'}`, borderRadius:'20px', whiteSpace:'nowrap' }}>
                    {session.plan==='pro' ? 'Pro' : session.plan==='studio' ? 'Studio' : 'Discover'}
                  </div>
                )}
                {onDashboard && (
                  <button className="r-header-new r-header-dashboard" onClick={onDashboard} style={{ border:'1px solid rgba(255,255,255,0.15)', color:'rgba(255,255,255,0.7)' }}>Dashboard</button>
                )}
                <button className="r-header-new" onClick={onReset}>+ New</button>
              </div>
            </header>

            {/* Left nav */}
            <nav className="r-left" aria-label="Phase navigation">
              {PHASES.map((p, i) => (
                <div
                  key={p.id}
                  className={`r-nav-item ${i === idx ? 'active' : ''} ${i < idx ? 'done' : ''} ${i > idx ? 'future' : ''} ${isDiscover && GATED_PHASES.includes(p.id) ? 'gated' : ''}`}
                  onClick={() => {
                    if (isDiscover && GATED_PHASES.includes(p.id)) { setDialog('upgrade'); return; }
                    if (i <= idx) { setIdx(i); setEntering(true); }
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      if (isDiscover && GATED_PHASES.includes(p.id)) { setDialog('upgrade'); return; }
                      if (i <= idx) { setIdx(i); setEntering(true); }
                    }
                  }}
                >
                  <span className="r-nav-num">{p.num}</span>
                  <span className="r-nav-name">{p.title}</span>
                  {isDiscover && GATED_PHASES.includes(p.id)
                    ? <span style={{ fontSize:'10px', opacity:0.5 }}>🔒</span>
                    : <span className="r-nav-dot" aria-hidden="true" />
                  }
                </div>
              ))}
            </nav>

            {/* Content */}
            <main className={`r-main${fadingOut ? ' fading-out' : ''}${entering ? ' entering' : ''}`}>
              {phase.id === 'p02' && (
                <PhaseNames
                  names={allNames}
                  selectedNameId={selectedNameId}
                  nameLocked={nameLocked}
                  onSelect={handleNameSelect}
                  userNameInput={userNameInput}
                  setUserNameInput={setUserNameInput}
                  onAddUserName={handleAddUserName}
                  userProvidedName={userProvidedName}
                />
              )}
              {phase.id === 'p00' && <PhaseDNA       data={normalize(b.phase00)} selectedName={currentSelectedName} />}
              {phase.id === 'p01' && <PhaseIdentity  data={normalize(b.phase01)} />}
              {phase.id === 'p03' && (isDiscover ? <DiscoverLogoGate /> : <PhaseVisual data={normalize(b.phase03)} selectedName={currentSelectedName} brief={brief} onLogoGenerated={setSharedLogoUrl} existingLogoUrl={sharedLogoUrl} session={session} onLogoSave={onLogoSave} savedBrandId={savedBrandId} extGallery={sharedLogoGallery} setExtGallery={setSharedLogoGallery} extActiveIdx={sharedActiveIdx} setExtActiveIdx={setSharedActiveIdx} extRegenCount={sharedRegenCount} setExtRegenCount={setSharedRegenCount} />)}              {phase.id === 'p04' && <PhaseMessaging data={normalize(b.phase04)} selectedName={currentSelectedName} />}
              {phase.id === 'p05' && <PhasePositioning data={normalize(b.phase05)} selectedName={currentSelectedName} />}
              {phase.id === 'p06' && (isDiscover
                ? <BlurGate phase="Customer Experience"><PhaseExperience data={normalize(b.phase06)} /></BlurGate>
                : <PhaseExperience data={normalize(b.phase06)} />)}
              {phase.id === 'p07' && (isDiscover
                ? <BlurGate phase="Digital Presence"><PhaseDigital data={normalize(b.phase07)} selectedName={currentSelectedName} brief={brief} brandData={b} logoUrl={sharedLogoUrl} session={session} onSiteHtmlSave={onSiteHtmlSave} savedBrandId={savedBrandId} extSiteHtml={sharedSiteHtml} setExtSiteHtml={setSharedSiteHtml} extSiteState={sharedSiteState} setExtSiteState={setSharedSiteState} /></BlurGate>
                : <PhaseDigital data={normalize(b.phase07)} selectedName={currentSelectedName} brief={brief} brandData={b} logoUrl={sharedLogoUrl} session={session} onSiteHtmlSave={onSiteHtmlSave} savedBrandId={savedBrandId} extSiteHtml={sharedSiteHtml} setExtSiteHtml={setSharedSiteHtml} extSiteState={sharedSiteState} setExtSiteState={setSharedSiteState} />)}
              {phase.id === 'p08' && (isDiscover
                ? <BlurGate phase="Go-To-Market"><PhaseGTM data={normalize(b.phase08)} /></BlurGate>
                : <PhaseGTM data={normalize(b.phase08)} />)}
              {phase.id === 'p10' && (isDiscover
                ? <BlurGate phase="Launch and Activation"><PhaseLaunch data={normalize(b.phase10)} selectedName={currentSelectedName} brief={brief} onEnd={() => setDialog('end')} /></BlurGate>
                : <PhaseLaunch data={normalize(b.phase10)} selectedName={currentSelectedName} brief={brief} onEnd={() => setDialog('end')} />)}
            </main>

            {/* Right panel */}
            <aside className="r-right">
              <div>
                <div className="r-right-label">Your Brief</div>
                <div className="r-brief-box">
                  <div className="r-brief-text">{brief && brief.length > 120 ? brief.substring(0, 120) + '...' : brief}</div>
                </div>
              </div>

              {/* Selected name — shown only after a name is chosen */}
              {currentSelectedName && (
                <div>
                  <div className="r-right-label">Selected Name</div>
                  <div className="r-selected-name-val">{currentSelectedName}</div>
                </div>
              )}

              <div className="r-right-divider" />

              {!isDiscover && (
                <div>
                  <div className="r-right-label">Edit and Refine</div>
                  <button
                    className="r-refine-btn"
                    onClick={() => setDialog('revise')}
                    disabled={!!conciergeState || (phase.id === 'p02' && nameLocked)}
                    title={phase.id === 'p02' && nameLocked ? 'Name is confirmed. Start a new session to explore different names.' : 'Open the Edit and Refine dialog to give direction for this phase'}
                  >
                    ✦ Edit and Refine
                  </button>
                  {phase.id === 'p02' && nameLocked && (
                    <div style={{ fontSize:'10px', color:'#9CA3AF', lineHeight:'1.5', marginTop:'4px' }}>
                      Name confirmed. Use + New Brand to explore alternatives.
                    </div>
                  )}
                </div>
              )}

              {!isDiscover && (
                <div>
                  <div className="r-right-label">Export</div>
                  <button className="r-export-btn" onClick={exportCurrentPhase} title="Download this phase as a text file" style={{ marginBottom:'6px' }}>
                    ↓ This Phase
                  </button>
                </div>
              )}

              {isDiscover && (
                <div style={{ background:'linear-gradient(135deg,rgba(26,106,255,0.08),rgba(6,182,212,0.04))', border:'1px solid rgba(26,106,255,0.2)', borderRadius:'10px', padding:'14px 12px', textAlign:'center' }}>
                  <div style={{ fontSize:'11px', fontWeight:'700', color:'#1A6AFF', marginBottom:'4px', letterSpacing:'0.04em' }}>Upgrade to Brand Studio</div>
                  <div style={{ fontSize:'10px', color:'#6B7280', lineHeight:'1.5', marginBottom:'10px' }}>Unlock all 10 phases, logo, website, exports and more.</div>
                  <a href="https://www.brandostro.com/pricing" target="_blank" rel="noreferrer" style={{ display:'block', height:'28px', lineHeight:'28px', background:'#1A6AFF', borderRadius:'6px', fontSize:'10px', fontWeight:'700', color:'#fff', textDecoration:'none', letterSpacing:'0.06em', textTransform:'uppercase' }}>
                    Upgrade Now →
                  </a>
                </div>
              )}

              <div className="r-right-divider" />

              <div className="r-progress-arc">
                <div className="r-right-label">Progress</div>
                <div className="r-progress-num">{String(parseInt(phase.num) + 1).padStart(2, '0')}</div>
                <div className="r-progress-of">of {PHASES.length} phases</div>
              </div>
            </aside>
          </div>

          {/* Dock */}
          <div className="r-dock" role="navigation" aria-label="Phase progress">
            <div className="r-dock-progress">
              {PHASES.map((p, i) => (
                <div
                  key={p.id}
                  className={`r-dock-dot ${i < idx ? 'done' : i === idx ? 'active' : 'future'}`}
                  title={p.title}
                  aria-label={`${p.title}: ${i < idx ? 'complete' : i === idx ? 'current' : 'upcoming'}`}
                />
              ))}
            </div>
            <div className="r-dock-actions">
              {idx > 0 && (
                <button className="r-dock-back" onClick={goBack} aria-label="Previous phase">←</button>
              )}
              <button
                className={`r-dock-next ${!canAdvance() ? 'gated' : ''}`}
                onClick={handleNextClick}
                aria-label={canAdvance() ? 'Next phase' : 'Select a name to continue'}
                title={canAdvance() ? undefined : 'Select a name to continue'}
              >
                →
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PHASE SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

function PL({ num, title }) {
  return <div className="r-phase-label" aria-label={`Phase ${num}: ${title}`}>Phase {num} · {title}</div>;
}

function Card({ label, children, blue, style }) {
  return (
    <div className={`r-card ${blue ? 'r-card-blue' : ''}`} style={style}>
      {label && <div className="r-card-label">{label}</div>}
      {children}
    </div>
  );
}

function NameScoreBar({ label, score }) {
  const color = score >= 75 ? '#06B6D4' : score >= 50 ? '#1A6AFF' : '#EF4444';
  return (
    <div style={{ display:'flex', alignItems:'center', gap:'10px', padding:'6px 0', borderBottom:'1px solid #F3F4F6' }}>
      <span style={{ fontSize:'11px', color:'#6B7280', width:'140px', flexShrink:0 }}>{label}</span>
      <div style={{ flex:1, height:'2px', background:'#E2E8F0', borderRadius:'1px', overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${score}%`, background:color, borderRadius:'1px', transition:'width 1.2s cubic-bezier(0.22,1,0.36,1)' }} />
      </div>
      <span style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:'13px', fontWeight:'700', color:'#CBD5E1', width:'28px', textAlign:'right' }}>{score}</span>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// BLUR GATE — Option A: blurred preview with upgrade overlay
// ═══════════════════════════════════════════════════════════════════════════
function BlurGate({ phase, children }) {
  return (
    <div style={{ position:'relative', minHeight:'60vh' }}>
      {/* Blurred actual content — teases the value */}
      <div style={{ filter:'blur(6px)', pointerEvents:'none', userSelect:'none', opacity:0.5, overflow:'hidden', maxHeight:'70vh' }}>
        {children}
      </div>
      {/* Upgrade overlay */}
      <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(242,245,255,0.75)', backdropFilter:'blur(4px)', zIndex:10 }}>
        <div style={{ textAlign:'center', padding:'40px', maxWidth:'460px' }}>
          <div style={{ width:'56px', height:'56px', borderRadius:'50%', background:'linear-gradient(135deg,rgba(26,106,255,0.12),rgba(6,182,212,0.06))', border:'1px solid rgba(26,106,255,0.2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'22px', margin:'0 auto 20px' }}>🔒</div>
          <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:'clamp(20px,2.5vw,28px)', fontWeight:'700', letterSpacing:'-0.03em', color:'#0A0A1A', marginBottom:'10px', lineHeight:'1.2' }}>
            {phase} is locked
          </div>
          <div style={{ fontSize:'14px', color:'#6B7280', lineHeight:'1.7', marginBottom:'28px' }}>
            Upgrade to Brand Studio to unlock Customer Experience, Digital Presence, Go-To-Market and Launch Activation — plus your logo, website and complete brand export.
          </div>
          <a href="https://www.brandostro.com/pricing" target="_blank" rel="noreferrer"
            style={{ display:'inline-flex', alignItems:'center', height:'50px', padding:'0 32px', background:'linear-gradient(135deg,#094BCC,#1A6AFF)', borderRadius:'10px', fontFamily:"'Space Grotesk',sans-serif", fontSize:'14px', fontWeight:'600', color:'#fff', textDecoration:'none', boxShadow:'0 6px 24px rgba(26,106,255,0.3)', letterSpacing:'0.02em' }}>
            Upgrade to Brand Studio →
          </a>
          <div style={{ marginTop:'14px', fontSize:'12px', color:'#9CA3AF' }}>
            From $99 · One-time payment · No subscription
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// DISCOVER LOGO GATE — shown instead of logo generator for Discover users
// ═══════════════════════════════════════════════════════════════════════════
function DiscoverLogoGate() {
  return (
    <div style={{ background:'#FFFFFF', border:'1px solid #E2E8F0', borderRadius:'16px', overflow:'hidden', boxShadow:'0 1px 3px rgba(0,0,0,0.05)' }}>
      <div style={{ background:'#FAFBFF', padding:'60px 40px', display:'flex', flexDirection:'column', alignItems:'center', gap:'16px', textAlign:'center' }}>
        <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:'13px', fontWeight:'600', color:'#9CA3AF', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:'4px' }}>Brand Logo</div>
        <div style={{ width:'56px', height:'56px', borderRadius:'50%', background:'rgba(26,106,255,0.08)', border:'1px solid rgba(26,106,255,0.15)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'22px' }}>🔒</div>
        <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:'18px', fontWeight:'700', color:'#0A0A1A', marginBottom:'4px' }}>Logo generation is locked</div>
        <div style={{ fontSize:'13px', color:'#9CA3AF', maxWidth:'320px', lineHeight:'1.6' }}>
          Upgrade to Brand Studio to generate your bespoke AI logo — 3 creative directions, PNG export at 2048px and 4096px.
        </div>
        <a href="https://www.brandostro.com/pricing" target="_blank" rel="noreferrer"
          style={{ height:'44px', padding:'0 28px', background:'linear-gradient(135deg,#094BCC,#1A6AFF)', border:'none', borderRadius:'10px', fontFamily:"'Space Grotesk',sans-serif", fontSize:'13px', fontWeight:'600', color:'#fff', cursor:'pointer', letterSpacing:'0.02em', boxShadow:'0 4px 16px rgba(26,106,255,0.25)', display:'inline-flex', alignItems:'center', textDecoration:'none', marginTop:'8px' }}>
          Upgrade to Brand Studio →
        </a>
        <div style={{ fontSize:'11px', color:'#CBD5E1' }}>From $99 · One-time payment</div>
      </div>
    </div>
  );
}


function UpgradeGate({ phase }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'60vh', textAlign:'center', padding:'40px' }}>
      <div style={{ width:'64px', height:'64px', borderRadius:'50%', background:'linear-gradient(135deg,rgba(26,106,255,0.1),rgba(6,182,212,0.05))', border:'1px solid rgba(26,106,255,0.2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'24px', marginBottom:'24px' }}>🔒</div>
      <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:'clamp(22px,2.5vw,32px)', fontWeight:'700', letterSpacing:'-0.03em', color:'#0A0A1A', marginBottom:'12px', lineHeight:'1.2' }}>
        {phase} is locked
      </div>
      <div style={{ fontSize:'15px', color:'#6B7280', lineHeight:'1.7', maxWidth:'420px', marginBottom:'32px' }}>
        Upgrade to Brand Studio to unlock Customer Experience, Digital Presence, Go-To-Market and Launch Activation — plus your logo, website and complete brand export.
      </div>
      <div style={{ display:'flex', gap:'12px', flexWrap:'wrap', justifyContent:'center' }}>
        <a
          href="https://www.brandostro.com/pricing"
          target="_blank"
          rel="noreferrer"
          style={{ height:'52px', padding:'0 36px', background:'linear-gradient(135deg,#094BCC,#1A6AFF)', border:'none', borderRadius:'10px', fontFamily:"'Space Grotesk',sans-serif", fontSize:'15px', fontWeight:'600', color:'#fff', cursor:'pointer', letterSpacing:'0.02em', boxShadow:'0 6px 24px rgba(26,106,255,0.3)', display:'flex', alignItems:'center', textDecoration:'none' }}
        >
          Upgrade to Brand Studio →
        </a>
      </div>
      <div style={{ marginTop:'16px', fontSize:'12px', color:'#9CA3AF' }}>
        From $99 · One-time payment · No subscription
      </div>
    </div>
  );
}

function PhaseNames({ names, selectedNameId, nameLocked, onSelect, userNameInput, setUserNameInput, onAddUserName }) {
  const assessedName = names.find(n => n.userProvided);
  const alternatives = names.filter(n => n.isAlternative);
  const regularNames = names.filter(n => !n.isAlternative && !n.userProvided);
  const isAssessmentMode = !!assessedName;

  return (
    <>
      <PL num="00" title="Nomenclature" />

      {/* Domain availability disclaimer */}
      <div style={{ background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:'8px', padding:'8px 12px', marginBottom:'16px', display:'flex', alignItems:'flex-start', gap:'8px' }}>
        <span style={{ fontSize:'13px', flexShrink:0 }}>⚠️</span>
        <div style={{ fontSize:'11px', color:'#92400E', lineHeight:'1.5' }}>
          Domain availability shown is AI-estimated, not a live registry check. Always verify at a domain registrar such as GoDaddy, Namecheap or Google Domains before registering.
        </div>
      </div>

      {isAssessmentMode ? (
        <>
          <h1 className="r-hero">{assessedName.name}.<br /><span className="r-hero-dim">Professional assessment.</span></h1>
          {nameLocked && (
            <div style={{ background:'#EFF4FF', border:'1px solid #BFDBFE', borderRadius:'10px', padding:'12px 16px', marginBottom:'20px', fontSize:'13px', color:'#1A6AFF', fontWeight:'500' }}>
              ✓ {assessedName.name} is your confirmed name. This phase is now read-only.
            </div>
          )}
          {assessedName.assessmentScore !== undefined && (
            <div className="r-card r-card-blue" style={{ marginBottom:'20px' }}>
              <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:'16px' }}>
                <div>
                  <div className="r-card-label">Name Assessment Score</div>
                  <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:'56px', fontWeight:'700', letterSpacing:'-0.05em', color: assessedName.assessmentScore >= 75 ? '#1A6AFF' : assessedName.assessmentScore >= 50 ? '#F59E0B' : '#EF4444', lineHeight:'1' }}>
                    {assessedName.assessmentScore}<span style={{ fontSize:'22px', fontWeight:'400', color:'#60A5FA' }}>/100</span>
                  </div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize:'11px', fontWeight:'600', letterSpacing:'0.1em', textTransform:'uppercase', color: assessedName.assessmentScore >= 75 ? '#06B6D4' : assessedName.assessmentScore >= 50 ? '#F59E0B' : '#EF4444', marginBottom:'4px' }}>
                    {assessedName.assessmentScore >= 75 ? 'Strong Name' : assessedName.assessmentScore >= 50 ? 'Acceptable' : 'Needs Review'}
                  </div>
                  <div style={{ fontSize:'12px', color:'#6B7280', maxWidth:'180px', lineHeight:'1.5' }}>{assessedName.assessmentVerdict}</div>
                </div>
              </div>
              {assessedName.clarity !== undefined && (
                <>
                  <NameScoreBar label="Clarity" score={assessedName.clarity} />
                  <NameScoreBar label="Cultural Resonance" score={assessedName.culturalResonance} />
                  <NameScoreBar label="Trademark Safety" score={assessedName.trademarkRisk} />
                  <NameScoreBar label="Domain Availability" score={assessedName.domainAvailability} />
                  <NameScoreBar label="Memorability" score={assessedName.memorability} />
                  <NameScoreBar label="Scalability" score={assessedName.scalability} />
                </>
              )}
            </div>
          )}
          <div
            className={`r-name-card ${nameLocked ? 'locked-selected' : selectedNameId === assessedName._id ? 'selected' : ''}`}
            onClick={() => !nameLocked && onSelect(assessedName._id)}
          >
            <div className="r-name-badge r-name-badge-client">Your Name</div>
            <div className="r-name-top">
              <div className="r-name-word">{assessedName.name}</div>
              <div className="r-badges">{assessedName.trademark && <span className="r-badge r-badge-cyan">TM: {assessedName.trademark}</span>}</div>
            </div>
            <div className="r-name-rationale">{assessedName.rationale}</div>
            <div className="r-name-domain">{assessedName.domain}</div>
            {nameLocked ? (
              <div style={{ position:'absolute', bottom:'18px', right:'18px', height:'30px', padding:'0 14px', background:'#1A6AFF', borderRadius:'6px', display:'flex', alignItems:'center', fontSize:'10px', fontWeight:'700', color:'#fff' }}>CONFIRMED ✓</div>
            ) : (
              <button className="r-name-select" onClick={e => { e.stopPropagation(); onSelect(assessedName._id); }}>{selectedNameId === assessedName._id ? 'SELECTED ✓' : 'KEEP THIS NAME'}</button>
            )}
          </div>
          {alternatives.length > 0 && (
            <>
              <div style={{ margin:'24px 0 14px', paddingTop:'20px', borderTop:'1px solid #F3F4F6' }}>
                <div style={{ fontSize:'10px', fontWeight:'600', letterSpacing:'0.16em', textTransform:'uppercase', color:'#EF4444', marginBottom:'4px' }}>Superior Alternatives</div>
                <div style={{ fontSize:'13px', color:'#6B7280' }}>Our analysis identified stronger options. Each scores higher across professional brand criteria.</div>
              </div>
              {alternatives.map(n => (
                <div key={n._id} className={`r-name-card ${nameLocked ? 'locked-other' : selectedNameId === n._id ? 'selected' : ''}`} onClick={() => !nameLocked && onSelect(n._id)}>
                  <div className="r-name-badge" style={{ background:'#FEF2F2', border:'1px solid #FECACA', color:'#EF4444' }}>Superior Alternative{n.assessmentScore ? ` · ${n.assessmentScore}/100` : ''}</div>
                  <div className="r-name-top"><div className="r-name-word">{n.name}</div><div className="r-badges">{n.type && <span className="r-badge">{n.type}</span>}{n.trademark && <span className="r-badge r-badge-cyan">TM: {n.trademark}</span>}</div></div>
                  <div className="r-name-rationale">{n.superiorReason || n.rationale}</div>
                  <div className="r-name-domain">{n.domain}</div>
                  {!nameLocked && <button className="r-name-select" onClick={e => { e.stopPropagation(); onSelect(n._id); }}>{selectedNameId === n._id ? 'SELECTED ✓' : 'SELECT'}</button>}
                </div>
              ))}
            </>
          )}
        </>
      ) : (
        <>
          <h1 className="r-hero">Five names.<br /><span className="r-hero-dim">One carries your future.</span></h1>
          {!nameLocked && <div className="r-alert">Select a name below to proceed. This decision anchors every phase, choose the name your brand will carry into the world.</div>}
          {nameLocked && <div style={{ background:'#EFF4FF', border:'1px solid #BFDBFE', borderRadius:'10px', padding:'12px 16px', marginBottom:'20px', fontSize:'13px', color:'#1A6AFF', fontWeight:'500' }}>✓ Your name is confirmed. This phase is now read-only.</div>}
          {!nameLocked && (
            <div className="r-name-input-row">
              <input className="r-name-input" placeholder="Have a name in mind? Enter it here..." value={userNameInput} onChange={e => setUserNameInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && onAddUserName()} />
              <button className="r-name-input-btn" onClick={onAddUserName}>Add</button>
            </div>
          )}
          {regularNames.map(n => {
            const isSelected = selectedNameId === n._id;
            const cardClass = nameLocked ? (isSelected ? 'locked-selected' : 'locked-other') : (isSelected ? 'selected' : '');
            return (
              <div key={n._id} className={`r-name-card ${cardClass}`} onClick={() => !nameLocked && onSelect(n._id)} role="button" tabIndex={nameLocked ? -1 : 0} onKeyDown={e => !nameLocked && e.key === 'Enter' && onSelect(n._id)} aria-selected={isSelected}>
                {n.clientSelected && <div className="r-name-badge r-name-badge-client">Client Selected</div>}
                {n.userDefined && !n.clientSelected && <div className="r-name-badge r-name-badge-user">Your Entry</div>}
                <div className="r-name-top"><div className="r-name-word">{n.name}</div><div className="r-badges">{!n.clientSelected && !n.userDefined && <span className="r-badge">{n.type}</span>}{n.trademark && <span className="r-badge r-badge-cyan">TM: {n.trademark}</span>}</div></div>
                <div className="r-name-rationale">{n.rationale}</div>
                <div className="r-name-domain">{n.domain}</div>
                {nameLocked && isSelected && <div style={{ position:'absolute', bottom:'18px', right:'18px', height:'30px', padding:'0 14px', background:'#1A6AFF', borderRadius:'6px', display:'flex', alignItems:'center', fontSize:'10px', fontWeight:'700', color:'#fff' }}>CONFIRMED ✓</div>}
                {!nameLocked && <button className="r-name-select" onClick={e => { e.stopPropagation(); onSelect(n._id); }}>{isSelected ? 'SELECTED ✓' : 'SELECT'}</button>}
              </div>
            );
          })}
        </>
      )}
    </>
  );
}


function PhaseDNA({ data, selectedName }) {
  return (
    <>
      <PL num="01" title="Brand DNA" />
      <h1 className="r-hero">The truth at the<br /><span className="r-hero-cyan">heart of your brand.</span></h1>
      {selectedName && (
        <div style={{ display:'inline-flex', alignItems:'center', gap:'8px', background:'#EFF4FF', border:'1px solid #BFDBFE', borderRadius:'100px', padding:'6px 14px', marginBottom:'20px', fontSize:'11px', fontWeight:'600', color:'#1A6AFF' }}>
          ✦ Building the DNA of <span style={{ color:'#06B6D4', marginLeft:'4px' }}>{selectedName}</span>
        </div>
      )}
      <ReadOnly>
        <div className="r-manifesto">{data?.manifesto}</div>
        <div className="r-grid-2">
          <Card label="The Problem We Solve"><div className="r-card-text">{data?.problem}</div></Card>
          <Card label="What We Believe"><div className="r-card-text">{data?.belief}</div></Card>
        </div>
        <div className="r-grid-2">
          <Card label="The Change We Create"><div className="r-card-text">{data?.change}</div></Card>
          <Card label="Where We Come From"><div className="r-card-text">{data?.origin}</div></Card>
        </div>
      </ReadOnly>
    </>
  );
}

function PhaseIdentity({ data }) {
  return (
    <>
      <PL num="02" title="Company Identity" />
      <h1 className="r-hero">Who you are.<br /><span className="r-hero-dim">Why it matters.</span></h1>
      <ReadOnly>
        <div className="r-grid-2" style={{ marginBottom:'12px' }}>
          <Card label="Mission, Why You Exist"><div className="r-card-text">{data?.mission}</div></Card>
          <Card label="Vision, Where You're Going"><div className="r-card-text">{data?.vision}</div></Card>
        </div>
        <Card label="What You Offer" blue style={{ marginBottom:'16px' }}>
          <div className="r-card-text">{data?.offerings}</div>
        </Card>
        <div className="r-sec">Core Values</div>
        <div className="r-grid-3">
          {(data?.values || []).map((v, i) => (
            <Card key={i}><div className="r-card-label">{v.name}</div><div className="r-card-text">{v.definition}</div></Card>
          ))}
        </div>
      </ReadOnly>
    </>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// COLLATERAL LIGHTBOX — transparent frosted overlay
// Used for business card and letterhead full-size preview.
// ═══════════════════════════════════════════════════════════════════════════

function CollateralLightbox({ onClose, title, children }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      style={{ position:'fixed', inset:0, zIndex:500, background:'rgba(5,12,26,0.88)', backdropFilter:'blur(20px)', WebkitBackdropFilter:'blur(20px)', display:'flex', alignItems:'center', justifyContent:'center', padding:'24px' }}
      onClick={onClose}
    >
      <div
        style={{ background:'#FFFFFF', borderRadius:'20px', padding:'32px', maxWidth:'860px', width:'100%', maxHeight:'90vh', overflow:'auto', position:'relative' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'24px' }}>
          <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:'18px', fontWeight:'700', letterSpacing:'-0.02em', color:'#0A0A1A' }}>{title}</div>
          <button onClick={onClose} style={{ width:'32px', height:'32px', borderRadius:'50%', border:'1.5px solid #E2E8F0', background:'#F8FAFF', cursor:'pointer', fontSize:'14px', color:'#6B7280', display:'flex', alignItems:'center', justifyContent:'center' }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function PhaseVisual({ data, selectedName, brief, onLogoGenerated, existingLogoUrl, session, onLogoSave, savedBrandId, extGallery, setExtGallery, extActiveIdx, setExtActiveIdx, extRegenCount, setExtRegenCount }) {
  const colors = Object.values(data?.colors || {});
  const primaryColor  = colors[0]?.hex || '#1A6AFF';
  const secondaryColor = colors[1]?.hex || '#06B6D4';
  const safeName = (selectedName || 'brand').toLowerCase().replace(/\s+/g, '');

  // ── Logo gallery — array of all generated logos ──
  const [_localGallery, _setLocalGallery]       = useState(existingLogoUrl ? [existingLogoUrl] : []);
  const [_localActiveIdx, _setLocalActiveIdx]   = useState(existingLogoUrl ? 0 : -1);
  const logoGallery  = setExtGallery  ? extGallery  : _localGallery;
  const setLogoGallery = setExtGallery || _setLocalGallery;
  const activeIdx    = setExtActiveIdx ? extActiveIdx : _localActiveIdx;
  const setActiveIdx = setExtActiveIdx || _setLocalActiveIdx;
  const [logoState, setLogoState]       = useState(existingLogoUrl ? 'done' : 'idle');
  const [_localRegenCount, _setLocalRegenCount] = useState(0);
  const regenCount   = setExtRegenCount ? extRegenCount : _localRegenCount;
  const setRegenCount = setExtRegenCount || _setLocalRegenCount;
  const [logoZoom, setLogoZoom]         = useState(1);
  const [showLogoLightbox, setShowLogoLightbox] = useState(false);
  const timeoutRef   = useRef(null);
  const MAX_REGEN    = 3;

  // Active logo URL derived from gallery
  const logoUrl = activeIdx >= 0 && logoGallery[activeIdx] ? logoGallery[activeIdx] : null;

  // ── Gemini card mockup state ──
  const [cardMockupState, setCardMockupState] = useState('idle'); // idle|loading|done|error
  const [cardMockupUrl, setCardMockupUrl]     = useState(null);

  // ── Business card editable fields ──
  const [cardData, setCardData] = useState({
    name:    'Your Full Name',
    title:   'Founder & CEO',
    email:   `hello@${safeName}.com`,
    phone:   '+1 000 000 0000',
    website: `${safeName}.com`,
  });
  const updateCard = (field, val) => setCardData(prev => ({ ...prev, [field]: val }));

  // ── Lightbox state ──
  const [lightbox, setLightbox] = useState(null); // null | 'card' | 'letterhead'
  const [cardSide, setCardSide] = useState('front');

  const ENGINE_URL = 'https://brandostro-engine-253902457893.us-central1.run.app';

  // ── Logo generation ──
  const doLogoGeneration = () => {
    setLogoState('loading');
    // Timeout after 40 seconds — Brandostro Assistant steps in
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setLogoState('timeout');
    }, 40000);

    fetch(`${ENGINE_URL}/logo`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: selectedName,
        archetype: data?.archetype,
        colors: data?.colors,
        brief: brief || '',
        tagline: data?.taglines?.[0]?.line || '',
        visualDirection: data?.logoDirection || data?.visualRationale || '',
        variation: regenCount + 1,
        memberId: session?.memberId,
        token: session?.token,
        brandId: savedBrandId || null,
      }),
    })
      .then(r => r.json())
      .then(d => {
        clearTimeout(timeoutRef.current);
        if (d.success && d.imageUrl) {
          setLogoGallery(prev => {
            const updated = [...prev, d.imageUrl];
            setActiveIdx(updated.length - 1);
            if (onLogoSave) onLogoSave(d.imageUrl, updated, regenCount + 1);
            return updated;
          });
          setLogoState('done');
          if (onLogoGenerated) onLogoGenerated(d.imageUrl);
        } else throw new Error(d.error || 'No image');
      })
      .catch(() => {
        clearTimeout(timeoutRef.current);
        setLogoState('error');
      });
  };

  useEffect(() => {
    // No auto-generation — user triggers intentionally via button
    return () => clearTimeout(timeoutRef.current);
  }, []);

  // ── Prompted regeneration state ──
  const [showRegenDialog, setShowRegenDialog] = useState(false);
  const [regenPrompt, setRegenPrompt]         = useState('');

  const handleRegenerate = () => {
    if (regenCount >= MAX_REGEN - 1) {
      setLogoState('limit');
      return;
    }
    setShowRegenDialog(true);
  };

  const confirmRegen = () => {
    setShowRegenDialog(false);
    setRegenCount(prev => prev + 1);
    doLogoGenerationWithPrompt(regenPrompt);
    setRegenPrompt('');
  };

  const doLogoGenerationWithPrompt = (userPrompt) => {
    setLogoState('loading');
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setLogoState('timeout'), 40000);

    fetch(`${ENGINE_URL}/logo`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: selectedName,
        archetype: data?.archetype,
        colors: data?.colors,
        brief: brief || '',
        tagline: data?.taglines?.[0]?.line || '',
        visualDirection: data?.logoDirection || data?.visualRationale || '',
        variation: regenCount + 2,
        userDirection: userPrompt || '',
      }),
    })
      .then(r => r.json())
      .then(d => {
        clearTimeout(timeoutRef.current);
        if (d.success && d.imageUrl) {
          setLogoGallery(prev => {
            const updated = [...prev, d.imageUrl];
            setActiveIdx(updated.length - 1);
            if (onLogoSave) onLogoSave(d.imageUrl, updated, regenCount + 1);
            return updated;
          });
          setLogoState('done');
          if (onLogoGenerated) onLogoGenerated(d.imageUrl);
        } else throw new Error(d.error || 'No image');
      })
      .catch(() => {
        clearTimeout(timeoutRef.current);
        setLogoState('error');
      });
  };

  // ── Download PNG (high resolution via canvas) ──
  const handleDownloadPNG = (size = 2048) => {
    if (!logoUrl) return;
    const canvas = document.createElement('canvas');
    canvas.width = size; canvas.height = size;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, size, size);
      // Centre the logo with padding
      const pad = size * 0.1;
      const drawSize = size - pad * 2;
      const ar = img.width / img.height;
      let dw = drawSize, dh = drawSize;
      if (ar > 1) dh = drawSize / ar;
      else dw = drawSize * ar;
      ctx.drawImage(img, (size - dw) / 2, (size - dh) / 2, dw, dh);
      const a = document.createElement('a');
      a.download = `${safeName}-logo-${size}px.png`;
      a.href = canvas.toDataURL('image/png');
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    };
    img.onerror = () => {
      // Fallback: download the data URL directly
      const a = document.createElement('a');
      a.download = `${safeName}-logo.png`;
      a.href = logoUrl;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    };
    img.src = logoUrl;
  };

  // ── Generate Gemini card mockup ──
  const generateCardMockup = async () => {
    setCardMockupState('loading');
    setCardMockupUrl(null);
    try {
      const res = await fetch(`${ENGINE_URL}/card-mockup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: selectedName,
          primary: primaryColor,
          secondary: secondaryColor,
          tagline: data?.taglines?.[0]?.line || '',
          archetype: data?.archetype || '',
          displayFont: data?.typography?.display?.font || '',
          cardData,
        }),
      });
      const d = await res.json();
      if (d.success && d.imageUrl) {
        setCardMockupUrl(d.imageUrl);
        setCardMockupState('done');
      } else throw new Error(d.error || 'No image');
    } catch (err) {
      console.error('Card mockup error:', err);
      setCardMockupState('error');
    }
  };

  // ── Print business card ──
  const printCard = () => {
    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8">
<title>${selectedName || 'Brand'} Business Card</title>
<style>
  @page { size: 94.9mm 56.8mm; margin: 0; }
  *{ box-sizing:border-box; margin:0; padding:0; font-family:Arial,sans-serif; }
  body{ width:94.9mm; height:56.8mm; }
  .wrap{ width:94.9mm; height:56.8mm; padding:3mm; }
  .card-f{ width:88.9mm; height:50.8mm; background:${primaryColor}; display:flex; flex-direction:column; justify-content:space-between; padding:7mm 8mm; position:relative; }
  .brand{ font-size:18pt; font-weight:700; color:#fff; letter-spacing:-0.5pt; }
  .tagline{ font-size:7pt; color:rgba(255,255,255,0.6); letter-spacing:1pt; text-transform:uppercase; margin-bottom:3mm; }
  .rule{ width:12mm; height:0.8pt; background:rgba(255,255,255,0.4); }
  .logo-img{ position:absolute; top:6mm; right:6mm; width:14mm; height:14mm; object-fit:contain; opacity:0.9; }
  @media print{ body{ -webkit-print-color-adjust:exact; print-color-adjust:exact; } }
</style>
</head><body>
<div class="wrap">
  <div class="card-f">
    ${logoUrl ? `<img class="logo-img" src="${logoUrl}" alt="logo" />` : ''}
    <div class="brand">${selectedName || 'Brand'}</div>
    <div>
      <div class="tagline">${cardData.name} &nbsp;·&nbsp; ${cardData.title}</div>
      <div class="rule"></div>
      <div style="margin-top:2mm;font-size:6pt;color:rgba(255,255,255,0.55);">${cardData.email} &nbsp;·&nbsp; ${cardData.website}</div>
    </div>
  </div>
</div>
</body></html>`);
    win.document.close();
    setTimeout(() => { win.focus(); win.print(); }, 600);
  };

  // ── Export letterhead as Word-compatible HTML doc ──
  const exportLetterheadDoc = () => {
    const logoTag = logoUrl ? `<img src="${logoUrl}" style="width:32px;height:32px;object-fit:contain;margin-bottom:6px;display:block;" alt="logo" />` : '';
    const doc = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="UTF-8"><title>${selectedName} Letterhead</title>
<style>
  body{ font-family:Arial,sans-serif; margin:0; padding:0; color:#0A0A1A; }
  .header{ padding:28px 40px 20px; border-bottom:3px solid ${primaryColor}; display:flex; justify-content:space-between; align-items:flex-end; }
  .brand{ font-size:22pt; font-weight:700; color:#0A0A1A; letter-spacing:-0.5pt; }
  .contact{ font-size:9pt; color:#6B7280; text-align:right; line-height:1.6; }
  .accent{ display:inline-block; width:32px; height:3px; background:${primaryColor}; margin-top:8px; }
  .body{ padding:44px 40px; min-height:200mm; }
  .footer{ border-top:1px solid #E2E8F0; padding:16px 40px; font-size:8pt; color:#9CA3AF; display:flex; justify-content:space-between; }
</style></head>
<body>
<div class="header">
  <div>
    ${logoTag}
    <div class="brand">${selectedName}</div>
    <div class="accent"></div>
  </div>
  <div class="contact">
    ${cardData.email}<br>${cardData.website}<br>${cardData.phone}
  </div>
</div>
<div class="body">
  <p style="margin-bottom:24px;color:#374151;line-height:1.8;">&nbsp;</p>
  <p style="margin-bottom:16px;color:#374151;">Dear [Recipient],</p>
  <p style="margin-bottom:16px;color:#374151;line-height:1.8;">&nbsp;</p>
  <p style="color:#374151;">Yours sincerely,</p>
  <p style="margin-top:32px;color:#374151;font-weight:700;">${cardData.name}</p>
  <p style="color:#6B7280;font-size:10pt;">${cardData.title}, ${selectedName}</p>
</div>
<div class="footer">
  <span>${selectedName}</span><span>${cardData.website}</span>
</div>
</body></html>`;
    const blob = new Blob([doc], { type: 'application/msword' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `${safeName}-letterhead.doc`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  // ── Shared card preview components ──
  const CardFront = ({ scale = 1 }) => (
    <div style={{ width:`${340*scale}px`, height:`${194*scale}px`, background:primaryColor, borderRadius:`${8*scale}px`, display:'flex', flexDirection:'column', justifyContent:'space-between', padding:`${20*scale}px ${24*scale}px`, flexShrink:0, position:'relative', overflow:'hidden' }}>
      {logoUrl && <img src={logoUrl} alt="logo" style={{ position:'absolute', top:`${14*scale}px`, right:`${14*scale}px`, width:`${32*scale}px`, height:`${32*scale}px`, objectFit:'contain', opacity:0.85 }} />}
      <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:`${20*scale}px`, fontWeight:'700', letterSpacing:`${-0.5*scale}px`, color:'#FFFFFF', lineHeight:'1.1' }}>{selectedName}</div>
      <div>
        {data?.taglines?.[0]?.line && <div style={{ fontSize:`${8*scale}px`, color:'rgba(255,255,255,0.55)', letterSpacing:`${1*scale}px`, textTransform:'uppercase', marginBottom:`${6*scale}px`, fontFamily:"'Inter',sans-serif" }}>{data.taglines[0].line}</div>}
        <div style={{ width:`${28*scale}px`, height:`${1.5*scale}px`, background:'rgba(255,255,255,0.4)', borderRadius:'1px' }} />
      </div>
    </div>
  );

  const CardBack = ({ scale = 1 }) => (
    <div style={{ width:`${340*scale}px`, height:`${194*scale}px`, background:'#FFFFFF', border:'1.5px solid #E2E8F0', borderRadius:`${8*scale}px`, display:'flex', flexDirection:'column', justifyContent:'space-between', padding:`${20*scale}px ${24*scale}px`, flexShrink:0 }}>
      <div>
        <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:`${13*scale}px`, fontWeight:'600', color:'#0A0A1A', marginBottom:`${2*scale}px` }}>{cardData.name}</div>
        <div style={{ fontSize:`${9*scale}px`, color:'#6B7280', textTransform:'uppercase', letterSpacing:`${0.8*scale}px`, fontFamily:"'Inter',sans-serif" }}>{cardData.title}</div>
      </div>
      <div>
        <div style={{ fontSize:`${9*scale}px`, color:'#9CA3AF', marginBottom:`${3*scale}px`, fontFamily:"'Inter',sans-serif" }}>{cardData.email}</div>
        <div style={{ fontSize:`${9*scale}px`, color:'#9CA3AF', marginBottom:`${3*scale}px`, fontFamily:"'Inter',sans-serif" }}>{cardData.phone}</div>
        <div style={{ fontSize:`${9*scale}px`, color:'#9CA3AF', fontFamily:"'Inter',sans-serif" }}>{cardData.website}</div>
      </div>
      <div style={{ width:`${20*scale}px`, height:`${3*scale}px`, background:primaryColor, borderRadius:'2px' }} />
    </div>
  );

  const inputStyle = { width:'100%', height:'34px', background:'#F8FAFF', border:'1.5px solid #E2E8F0', borderRadius:'7px', padding:'0 12px', fontFamily:"'Inter',sans-serif", fontSize:'12px', color:'#0A0A1A', outline:'none', transition:'border-color 0.2s' };
  const labelStyle = { fontSize:'9px', fontWeight:'600', letterSpacing:'0.12em', textTransform:'uppercase', color:'#9CA3AF', marginBottom:'4px', display:'block' };

  return (
    <>
      <PL num="03" title="Visual Architecture" />
      <h1 className="r-hero">The visual world<br /><span className="r-hero-cyan">your brand deserves.</span></h1>

      {/* ── REGENERATION PROMPT DIALOG ── */}
      {showRegenDialog && (
        <div style={{ position:'fixed', inset:0, zIndex:600, background:'rgba(5,12,26,0.7)', backdropFilter:'blur(16px)', display:'flex', alignItems:'center', justifyContent:'center', padding:'24px' }} onClick={() => setShowRegenDialog(false)}>
          <div style={{ background:'#FFFFFF', borderRadius:'20px', padding:'32px', maxWidth:'480px', width:'100%' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:'18px', fontWeight:'700', color:'#0A0A1A', marginBottom:'8px' }}>Direct the next logo</div>
            <div style={{ fontSize:'13px', color:'#6B7280', marginBottom:'20px', lineHeight:'1.6' }}>
              Tell Brandostro what direction to take. Be specific — style, mood, elements, what to avoid.
            </div>
            <div style={{ fontSize:'11px', color:'#9CA3AF', marginBottom:'6px', fontWeight:'600', letterSpacing:'0.08em', textTransform:'uppercase' }}>Your direction</div>
            <textarea
              value={regenPrompt}
              onChange={e => setRegenPrompt(e.target.value)}
              placeholder="e.g. More minimal, no circular frame. Use the primary color more boldly. Try a lettermark instead of a symbol."
              style={{ width:'100%', minHeight:'100px', padding:'12px', border:'1.5px solid #E2E8F0', borderRadius:'10px', fontFamily:"'Inter',sans-serif", fontSize:'13px', color:'#0A0A1A', resize:'vertical', outline:'none', lineHeight:'1.6', marginBottom:'16px' }}
              autoFocus
            />
            <div style={{ fontSize:'11px', color:'#9CA3AF', marginBottom:'20px' }}>
              Generation {regenCount + 2} of {MAX_REGEN} · Leave blank to let Brandostro choose a different direction automatically.
            </div>
            <div style={{ display:'flex', gap:'10px' }}>
              <button onClick={confirmRegen} style={{ flex:1, height:'44px', background:'linear-gradient(135deg,#094BCC,#1A6AFF)', border:'none', borderRadius:'10px', fontFamily:"'Space Grotesk',sans-serif", fontSize:'14px', fontWeight:'600', color:'#fff', cursor:'pointer' }}>
                Generate →
              </button>
              <button onClick={() => { setShowRegenDialog(false); setRegenPrompt(''); }} style={{ height:'44px', padding:'0 20px', background:'transparent', border:'1.5px solid #E2E8F0', borderRadius:'10px', fontFamily:"'Space Grotesk',sans-serif", fontSize:'13px', fontWeight:'500', color:'#374151', cursor:'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── LOGO LIGHTBOX ── */}
      {showLogoLightbox && logoUrl && (
        <CollateralLightbox title={`${selectedName} — Brand Logo`} onClose={() => { setShowLogoLightbox(false); setLogoZoom(1); }}>
          <div style={{ display:'flex', justifyContent:'center', gap:'8px', marginBottom:'16px' }}>
            {[1, 1.5, 2].map(z => (
              <button key={z} onClick={() => setLogoZoom(z)} style={{ height:'30px', padding:'0 16px', background: logoZoom===z ? '#1A6AFF' : '#F8FAFF', border: logoZoom===z ? 'none' : '1.5px solid #E2E8F0', borderRadius:'7px', fontFamily:"'Space Grotesk',sans-serif", fontSize:'11px', fontWeight:'600', color: logoZoom===z ? '#fff' : '#374151', cursor:'pointer' }}>{z===1?'100%':z===1.5?'150%':'200%'}</button>
            ))}
          </div>
          {/* Light — main view */}
          <div style={{ background:'#FAFBFF', borderRadius:'12px', padding:'52px 32px', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:'10px', overflow:'hidden', minHeight:'300px', cursor:'zoom-in' }} onClick={() => setLogoZoom(z => Math.min(z + 0.5, 2))}>
            <img src={logoUrl} alt={`${selectedName} logo`} style={{ maxWidth:`${480*logoZoom}px`, maxHeight:`${360*logoZoom}px`, objectFit:'contain', transition:'all 0.3s ease' }} />
          </div>
          {/* Dark — reversed */}
          <div style={{ background:'#050C1A', borderRadius:'12px', padding:'40px 32px', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:'20px', overflow:'hidden', minHeight:'220px' }}>
            <img src={logoUrl} alt={`${selectedName} logo on dark`} style={{ maxWidth:`${380*logoZoom}px`, maxHeight:`${280*logoZoom}px`, objectFit:'contain', transition:'all 0.3s ease' }} />
          </div>
          <div style={{ display:'flex', gap:'10px', justifyContent:'center' }}>
            <button onClick={() => handleDownloadPNG(2048)} style={{ height:'44px', padding:'0 28px', background:'#1A6AFF', border:'none', borderRadius:'10px', fontFamily:"'Space Grotesk',sans-serif", fontSize:'14px', fontWeight:'600', color:'#fff', cursor:'pointer' }}>↓ PNG 2048px</button>
            <button onClick={() => handleDownloadPNG(4096)} style={{ height:'44px', padding:'0 24px', background:'#EFF4FF', border:'1px solid #BFDBFE', borderRadius:'10px', fontFamily:"'Space Grotesk',sans-serif", fontSize:'14px', fontWeight:'600', color:'#1A6AFF', cursor:'pointer' }}>↓ PNG 4096px</button>
            <button onClick={() => { setShowLogoLightbox(false); setLogoZoom(1); }} style={{ height:'44px', padding:'0 24px', background:'transparent', border:'1.5px solid #E2E8F0', borderRadius:'10px', fontFamily:"'Space Grotesk',sans-serif", fontSize:'13px', fontWeight:'500', color:'#374151', cursor:'pointer' }}>Close</button>
          </div>
          <div style={{ textAlign:'center', marginTop:'10px', fontSize:'11px', color:'#9CA3AF' }}>Click image to zoom in. High resolution PNG ready for print and digital.</div>
        </CollateralLightbox>
      )}

      {/* ── LOGO SECTION ── */}
      <div style={{ marginBottom:'28px' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'14px' }}>
          <div className="r-card-label">Brand Logo</div>
          {logoState === 'done' && <div style={{ fontSize:'10px', color:'#9CA3AF' }}>{MAX_REGEN - regenCount} generation{MAX_REGEN - regenCount !== 1 ? 's' : ''} remaining</div>}
        </div>

        {/* Loading */}
        {logoState === 'loading' && (
          <div style={{ background:'#FFFFFF', border:'1px solid #E2E8F0', borderRadius:'16px', padding:'48px 24px', display:'flex', flexDirection:'column', alignItems:'center', gap:'16px', boxShadow:'0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ position:'relative', width:'44px', height:'44px' }}>
              <div style={{ position:'absolute', inset:0, borderRadius:'50%', border:'2px solid rgba(26,106,255,0.15)', borderTopColor:'#1A6AFF', animation:'cg-spin 1.4s linear infinite' }} />
              <div style={{ position:'absolute', inset:'8px', borderRadius:'50%', border:'2px solid rgba(6,182,212,0.15)', borderRightColor:'#06B6D4', animation:'cg-spin 0.9s linear infinite reverse' }} />
            </div>
            <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:'15px', fontWeight:'600', color:'#0A0A1A' }}>Generating your logo</div>
            <div style={{ fontSize:'12px', color:'#9CA3AF', textAlign:'center', maxWidth:'300px', lineHeight:'1.6' }}>Brandostro is building a mark for <strong style={{ color:'#0A0A1A' }}>{selectedName}</strong>. About 20 seconds.</div>
          </div>
        )}

        {/* Timeout — Brandostro Assistant steps in */}
        {logoState === 'timeout' && (
          <div style={{ background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:'16px', padding:'32px 28px', display:'flex', flexDirection:'column', alignItems:'center', gap:'14px', textAlign:'center' }}>
            <div style={{ width:'44px', height:'44px', borderRadius:'50%', background:'#FEF3C7', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'20px' }}>⏳</div>
            <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:'16px', fontWeight:'700', color:'#92400E' }}>Brandostro Assistant</div>
            <div style={{ fontSize:'13px', color:'#92400E', lineHeight:'1.7', maxWidth:'360px' }}>We sincerely apologise for the delay. Our logo generation service is experiencing high demand at this moment. Please try again or continue exploring your brand — the rest of your system is complete and ready.</div>
            <div style={{ display:'flex', gap:'10px' }}>
              <button onClick={() => doLogoGeneration()} style={{ height:'40px', padding:'0 22px', background:'#F59E0B', border:'none', borderRadius:'8px', fontFamily:"'Space Grotesk',sans-serif", fontSize:'13px', fontWeight:'600', color:'#fff', cursor:'pointer' }}>Try Again</button>
              <button onClick={() => setLogoState('error')} style={{ height:'40px', padding:'0 22px', background:'transparent', border:'1.5px solid #FDE68A', borderRadius:'8px', fontFamily:"'Space Grotesk',sans-serif", fontSize:'13px', fontWeight:'600', color:'#92400E', cursor:'pointer' }}>Continue Without Logo</button>
            </div>
          </div>
        )}

        {/* Limit reached */}
        {logoState === 'limit' && (
          <div style={{ background:'#EFF4FF', border:'1px solid #BFDBFE', borderRadius:'16px', padding:'28px', textAlign:'center' }}>
            <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:'15px', fontWeight:'700', color:'#1A6AFF', marginBottom:'10px' }}>Your logo is complete.</div>
            <div style={{ fontSize:'13px', color:'#374151', lineHeight:'1.65', marginBottom:'16px', maxWidth:'360px', margin:'0 auto 16px' }}>Brandostro provides 3 logo generations — equivalent to the 3 creative concepts a world-class agency presents. This ensures each direction receives genuine creative investment and maintains the integrity of your brand development process. To explore a new creative direction, begin a new brand session.</div>
            {logoUrl && <button onClick={() => setShowLogoLightbox(true)} style={{ height:'38px', padding:'0 22px', background:'#1A6AFF', border:'none', borderRadius:'8px', fontFamily:"'Space Grotesk',sans-serif", fontSize:'13px', fontWeight:'600', color:'#fff', cursor:'pointer' }}>⤢ View Your Logo</button>}
          </div>
        )}

        {/* Done */}
        {logoState === 'done' && logoUrl && (
          <div style={{ background:'#FFFFFF', border:'1px solid #E2E8F0', borderRadius:'16px', overflow:'hidden', boxShadow:'0 2px 12px rgba(0,0,0,0.06)' }}>
            {/* Active logo — large centered display */}
            <div
              style={{ background:'#FAFBFF', padding:'60px 40px', display:'flex', alignItems:'center', justifyContent:'center', minHeight:'320px', cursor:'zoom-in', position:'relative' }}
              onClick={() => setShowLogoLightbox(true)}
              title="Click to preview full size"
            >
              <img src={logoUrl} alt={`${selectedName} logo`} style={{ maxWidth:'480px', maxHeight:'320px', objectFit:'contain', display:'block' }} />
              <div style={{ position:'absolute', top:'14px', right:'14px', background:'rgba(26,106,255,0.08)', border:'1px solid rgba(26,106,255,0.18)', borderRadius:'6px', padding:'5px 12px', fontSize:'10px', fontWeight:'600', color:'#1A6AFF', letterSpacing:'0.08em', cursor:'pointer' }}>⤢ PREVIEW</div>
            </div>

            {/* Gallery strip — shows all generated logos */}
            {logoGallery.length > 1 && (
              <div style={{ padding:'12px 20px', borderTop:'1px solid #F3F4F6', background:'#F8FAFF' }}>
                <div style={{ fontSize:'9px', fontWeight:'600', color:'#9CA3AF', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:'8px' }}>
                  All generations — click to select
                </div>
                <div style={{ display:'flex', gap:'8px', overflowX:'auto', scrollbarWidth:'none' }}>
                  {logoGallery.map((url, i) => (
                    <div
                      key={i}
                      onClick={() => { setActiveIdx(i); if(onLogoGenerated) onLogoGenerated(url); }}
                      style={{ flexShrink:0, width:'72px', height:'72px', borderRadius:'10px', border: i === activeIdx ? '2px solid #1A6AFF' : '2px solid transparent', background:'#FFFFFF', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', transition:'all 0.2s', boxShadow: i === activeIdx ? '0 0 0 3px rgba(26,106,255,0.15)' : '0 1px 3px rgba(0,0,0,0.06)', padding:'6px', position:'relative' }}
                    >
                      <img src={url} alt={`Generation ${i+1}`} style={{ maxWidth:'100%', maxHeight:'100%', objectFit:'contain' }} />
                      <div style={{ position:'absolute', bottom:'2px', right:'4px', fontSize:'8px', fontWeight:'700', color: i === activeIdx ? '#1A6AFF' : '#CBD5E1' }}>{i+1}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div style={{ padding:'14px 20px', display:'flex', alignItems:'center', justifyContent:'space-between', borderTop:'1px solid #F3F4F6' }}>
              <div style={{ fontSize:'11px', color:'#9CA3AF', lineHeight:'1.5' }}>
                Powered by Brandostro · Generation {activeIdx + 1} of {logoGallery.length} · {MAX_REGEN - regenCount} remaining
              </div>
              <div style={{ display:'flex', gap:'8px', flexShrink:0 }}>
                <button onClick={() => setShowLogoLightbox(true)} style={{ height:'32px', padding:'0 14px', background:'#EFF4FF', border:'1px solid #BFDBFE', borderRadius:'7px', fontFamily:"'Space Grotesk',sans-serif", fontSize:'11px', fontWeight:'600', color:'#1A6AFF', cursor:'pointer' }}>⤢ Preview</button>
                <button onClick={() => handleDownloadPNG(2048)} style={{ height:'32px', padding:'0 14px', background:'#1A6AFF', border:'none', borderRadius:'7px', fontFamily:"'Space Grotesk',sans-serif", fontSize:'11px', fontWeight:'600', color:'#fff', cursor:'pointer' }}>↓ PNG</button>
                {regenCount < MAX_REGEN - 1 ? (
                  <button onClick={handleRegenerate} style={{ height:'32px', padding:'0 14px', background:'transparent', border:'1.5px solid #E2E8F0', borderRadius:'7px', fontFamily:"'Space Grotesk',sans-serif", fontSize:'11px', fontWeight:'600', color:'#6B7280', cursor:'pointer' }}>↺ Regenerate</button>
                ) : (
                  <button onClick={() => setLogoState('limit')} style={{ height:'32px', padding:'0 14px', background:'#FEF2F2', border:'1.5px solid #FECACA', borderRadius:'7px', fontFamily:"'Space Grotesk',sans-serif", fontSize:'11px', fontWeight:'600', color:'#DC2626', cursor:'pointer' }}>↺ Last Generation</button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {logoState === 'error' && (
          <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:'12px', padding:'18px 24px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div style={{ fontSize:'13px', color:'#DC2626' }}>Logo generation encountered a difficulty. Your visual system is complete below.</div>
            <button onClick={() => doLogoGeneration()} style={{ height:'30px', padding:'0 14px', background:'transparent', border:'1.5px solid #FECACA', borderRadius:'7px', fontSize:'11px', fontWeight:'600', color:'#DC2626', cursor:'pointer', flexShrink:0, marginLeft:'12px' }}>Try Again</button>
          </div>
        )}

        {/* Idle — show generate button */}
        {logoState === 'idle' && (
          <div style={{ background:'#FFFFFF', border:'1px solid #E2E8F0', borderRadius:'16px', overflow:'hidden', boxShadow:'0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ background:'#FAFBFF', padding:'60px 40px', display:'flex', flexDirection:'column', alignItems:'center', gap:'16px', textAlign:'center' }}>
              <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:'13px', fontWeight:'600', color:'#9CA3AF', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:'4px' }}>Brand Logo</div>
              <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:'24px', fontWeight:'700', letterSpacing:'-0.02em', color:'#CBD5E1', marginBottom:'4px' }}>{selectedName}</div>
              <div style={{ fontSize:'13px', color:'#9CA3AF', maxWidth:'320px', lineHeight:'1.6' }}>
                Brandostro will generate a bespoke logo mark for {selectedName} using your brand archetype, palette and positioning.
              </div>
              <button
                onClick={doLogoGeneration}
                disabled={!selectedName}
                style={{ height:'48px', padding:'0 36px', background:'linear-gradient(135deg,#094BCC,#1A6AFF)', border:'none', borderRadius:'10px', fontFamily:"'Space Grotesk',sans-serif", fontSize:'14px', fontWeight:'600', color:'#fff', cursor: selectedName ? 'pointer' : 'not-allowed', letterSpacing:'0.02em', boxShadow:'0 6px 24px rgba(26,106,255,0.25)', marginTop:'8px', opacity: selectedName ? 1 : 0.5 }}
              >
                ✦ Generate Logo
              </button>
              <div style={{ fontSize:'11px', color:'#CBD5E1' }}>Takes about 20 seconds · 3 generations available</div>
            </div>
          </div>
        )}
      </div>

      <ReadOnly>
        <div className="r-grid-2" style={{ marginBottom:'16px' }}>
          <Card label="Brand Archetype"><div className="r-card-value">{data?.archetype}</div><div className="r-card-text">{data?.archetypeDesc}</div></Card>
          <Card label="Visual Direction"><div className="r-card-text">{data?.visualRationale}</div></Card>
        </div>
        <div className="r-card-label" style={{ marginBottom:'10px' }}>Colour Palette, hover to explore</div>
        <div className="r-palette">
          {colors.map((c, i) => (
            <div key={i} className="r-pal-sw" style={{ background: c.hex }}>
              <div className="r-pal-info"><div className="r-pal-hex">{c.hex}</div><div className="r-pal-name">{c.name}</div></div>
            </div>
          ))}
        </div>
        <div className="r-grid-2" style={{ marginBottom:'16px' }}>
          <Card label="Display Typeface">
            <div style={{ fontFamily:`'${data?.typography?.display?.font}', 'Playfair Display', Georgia, serif`, fontSize:'32px', fontWeight:'700', letterSpacing:'-0.02em', color:'#0A0A1A', marginBottom:'4px', lineHeight:'1.1' }}>{selectedName}</div>
            <div style={{ fontFamily:`'${data?.typography?.display?.font}', serif`, fontSize:'11px', fontWeight:'600', color:'#1A6AFF', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:'10px' }}>{data?.typography?.display?.font}</div>
            <div className="r-card-text">{data?.typography?.display?.rationale}</div>
          </Card>
          <Card label="Body Typeface">
            <div style={{ fontFamily:`'${data?.typography?.body?.font}', 'Merriweather', Georgia, serif`, fontSize:'14px', fontWeight:'400', color:'#374151', marginBottom:'4px', lineHeight:'1.8', fontStyle:'italic' }}>"{data?.taglines?.[0]?.line || 'The quick brown fox jumps over the lazy dog.'}"</div>
            <div style={{ fontFamily:`'${data?.typography?.body?.font}', serif`, fontSize:'11px', fontWeight:'600', color:'#1A6AFF', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:'10px' }}>{data?.typography?.body?.font}</div>
            <div className="r-card-text">{data?.typography?.body?.rationale}</div>
          </Card>
        </div>
        <Card label="Brand Personality Spectrum">
          {(data?.traits || []).map((t, i) => (
            <div key={i} className="r-trait">
              <span className="r-trait-name">{t.name}</span>
              <div className="r-trait-track"><div className="r-trait-fill" style={{ width:`${t.score}%` }} /></div>
              <span className="r-trait-score">{t.score}</span>
            </div>
          ))}
        </Card>
      </ReadOnly>

      {/* ── BRAND COLLATERALS ── */}
      {selectedName && (
        <div style={{ marginTop:'36px' }}>
          <div className="r-sec">Brand Collaterals</div>
          <div style={{ fontSize:'13px', color:'#6B7280', marginBottom:'24px', lineHeight:'1.6' }}>
            Visual references built from your confirmed brand identity. Each one is unique to your brand.
          </div>

          {/* ── BUSINESS CARD — Coming Soon ── */}
          <div style={{ background:'#FFFFFF', border:'1px solid #E2E8F0', borderRadius:'16px', overflow:'hidden', marginBottom:'16px', boxShadow:'0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ padding:'24px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div>
                <div className="r-card-label" style={{ marginBottom:'2px' }}>Business Card</div>
                <div style={{ fontSize:'10px', color:'#9CA3AF' }}>88.9 × 50.8 mm · Standard landscape</div>
              </div>
              <div style={{ background:'#FFF7ED', border:'1px solid #FED7AA', borderRadius:'20px', padding:'4px 12px', fontSize:'10px', fontWeight:'700', color:'#EA580C', letterSpacing:'0.08em', textTransform:'uppercase' }}>
                Coming Soon
              </div>
            </div>

            {/* Preview showing brand colors with brand name */}
            <div style={{ margin:'0 24px 24px', borderRadius:'12px', overflow:'hidden', boxShadow:'0 4px 20px rgba(0,0,0,0.08)' }}>
              {/* Card front preview — pure CSS, real brand colors */}
              <div style={{ background: primaryColor, padding:'28px 28px 24px', display:'flex', flexDirection:'column', justifyContent:'space-between', minHeight:'130px', position:'relative' }}>
                <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:'20px', fontWeight:'700', letterSpacing:'-0.02em', color:'#FFFFFF', lineHeight:'1.1' }}>{selectedName}</div>
                {data?.taglines?.[0]?.line && (
                  <div style={{ fontSize:'8px', color:`rgba(255,255,255,0.55)`, letterSpacing:'0.12em', textTransform:'uppercase', fontFamily:"'Inter',sans-serif", marginTop:'auto', paddingTop:'12px' }}>
                    {data.taglines[0].line}
                  </div>
                )}
                <div style={{ width:'24px', height:'1.5px', background: secondaryColor, borderRadius:'1px', marginTop:'8px' }} />
                {/* Color palette swatches */}
                <div style={{ position:'absolute', bottom:'16px', right:'16px', display:'flex', gap:'4px' }}>
                  {colors.slice(0,3).map((c, i) => (
                    <div key={i} style={{ width:'12px', height:'12px', borderRadius:'50%', background: c.hex, border:'1.5px solid rgba(255,255,255,0.3)' }} />
                  ))}
                </div>
              </div>
              {/* Card back preview */}
              <div style={{ background:'#FAFAFA', padding:'20px 28px', display:'flex', alignItems:'center', justifyContent:'space-between', borderTop:'1px solid #F3F4F6' }}>
                <div>
                  <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:'13px', fontWeight:'700', color: primaryColor, letterSpacing:'0.04em', textTransform:'uppercase' }}>{selectedName}</div>
                  <div style={{ fontSize:'9px', color:'#9CA3AF', marginTop:'2px', fontFamily:"'Inter',sans-serif" }}>{(selectedName || 'brand').toLowerCase().replace(/\s+/g,'')}.com</div>
                </div>
                <div style={{ width:'20px', height:'2px', background: primaryColor, borderRadius:'1px' }} />
              </div>
            </div>

            <div style={{ padding:'0 24px 20px', fontSize:'11px', color:'#9CA3AF', lineHeight:'1.6' }}>
              Professional print-ready business cards are coming soon — with your actual logo, brand colors and typography precisely placed. We are working on a partnership with a leading print platform to deliver this seamlessly.
            </div>
          </div>

        </div>
      )}

      {/* ── LIGHTBOXES — card lightbox removed, coming soon ── */}
    </>
  );
}



function PhaseMessaging({ data, selectedName }) {
  return (
    <>
      <PL num="04" title="Brand Messaging" />
      <h1 className="r-hero">The words that make<br /><span className="r-hero-cyan">your market stop.</span></h1>
      <ReadOnly>
        <div className="r-sec">Taglines</div>
        {(data?.taglines || []).map((t, i) => (
          <div key={i} className="r-tagline"><div className="r-tagline-line">"{t.line}"</div><div className="r-tagline-why">{t.rationale}</div></div>
        ))}
        <div className="r-divider" />
        <div className="r-card-label" style={{ marginBottom:'12px' }}>Brand Voice</div>
        <div className="r-tones">{(data?.toneAdjectives || []).map((a, i) => <span key={i} className="r-tone">{a}</span>)}</div>
        <div className="r-card-text" style={{ marginBottom:'20px', fontSize:'14px', lineHeight:'1.8' }}>{data?.voiceDescription}</div>
        <div className="r-grid-2" style={{ marginBottom:'16px' }}>
          <Card label="Always Do">{(data?.doList || []).map((d, i) => <div key={i} className="r-rule r-rule-do">{d}</div>)}</Card>
          <Card label="Never Do">{(data?.dontList || []).map((d, i) => <div key={i} className="r-rule r-rule-dont">{d}</div>)}</Card>
        </div>
        <Card blue label="Positioning Statement" style={{ marginBottom:'16px' }}>
          <div className="r-card-text" style={{ fontSize:'15px', lineHeight:'1.75' }}>{data?.positioning}</div>
        </Card>
        <div className="r-sec">Elevator Pitches</div>
        <div className="r-pitch"><strong style={{ display:'block', marginBottom:'6px', fontSize:'9px', fontWeight:'600', letterSpacing:'0.16em', textTransform:'uppercase', color:'#9CA3AF' }}>30 Seconds</strong>{data?.pitchShort}</div>
        <div className="r-pitch"><strong style={{ display:'block', marginBottom:'6px', fontSize:'9px', fontWeight:'600', letterSpacing:'0.16em', textTransform:'uppercase', color:'#9CA3AF' }}>2 Minutes</strong>{data?.pitchLong}</div>
      </ReadOnly>
    </>
  );
}

function PositioningMap({ map, selectedName }) {
  const W = 480, H = 360, PAD = 48;
  const plotW = W - PAD * 2, plotH = H - PAD * 2;

  const toX = v => PAD + (v / 100) * plotW;
  const toY = v => PAD + ((100 - v) / 100) * plotH;

  const brand = map?.brandPosition || { x: 70, y: 75 };
  const competitors = map?.competitorPositions || [];
  const xAxis = map?.xAxis || { low: 'Traditional', high: 'Modern' };
  const yAxis = map?.yAxis || { low: 'Mass Market', high: 'Premium' };

  return (
    <div style={{ background:'#FFFFFF', border:'1px solid #E2E8F0', borderRadius:'16px', padding:'24px', marginBottom:'24px', boxShadow:'0 1px 3px rgba(0,0,0,0.05)' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'16px' }}>
        <div className="r-card-label">Brand Positioning Map</div>
        <div style={{ fontSize:'10px', color:'#9CA3AF', fontWeight:'500', letterSpacing:'0.06em', textTransform:'uppercase' }}>Perceptual Map</div>
      </div>
      <div style={{ overflowX:'auto' }}>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxWidth:`${W}px`, display:'block', margin:'0 auto' }}>
          {/* Grid lines */}
          {[25,50,75].map(v => (
            <g key={v}>
              <line x1={toX(v)} y1={PAD} x2={toX(v)} y2={H-PAD} stroke="#F3F4F6" strokeWidth="1" strokeDasharray="4,4" />
              <line x1={PAD} y1={toY(v)} x2={W-PAD} y2={toY(v)} stroke="#F3F4F6" strokeWidth="1" strokeDasharray="4,4" />
            </g>
          ))}

          {/* Axes */}
          <line x1={PAD} y1={H/2} x2={W-PAD} y2={H/2} stroke="#E2E8F0" strokeWidth="1.5" />
          <line x1={W/2} y1={PAD} x2={W/2} y2={H-PAD} stroke="#E2E8F0" strokeWidth="1.5" />

          {/* Axis labels */}
          <text x={PAD} y={H/2+18} textAnchor="middle" fontSize="9" fill="#9CA3AF" fontFamily="Inter,sans-serif" fontWeight="500">{xAxis.low}</text>
          <text x={W-PAD} y={H/2+18} textAnchor="middle" fontSize="9" fill="#9CA3AF" fontFamily="Inter,sans-serif" fontWeight="500">{xAxis.high}</text>
          <text x={W/2} y={PAD-8} textAnchor="middle" fontSize="9" fill="#9CA3AF" fontFamily="Inter,sans-serif" fontWeight="500">{yAxis.high}</text>
          <text x={W/2} y={H-PAD+16} textAnchor="middle" fontSize="9" fill="#9CA3AF" fontFamily="Inter,sans-serif" fontWeight="500">{yAxis.low}</text>

          {/* Whitespace zone — subtle glow around brand position */}
          <circle cx={toX(brand.x)} cy={toY(brand.y)} r="40" fill="rgba(26,106,255,0.05)" />
          <circle cx={toX(brand.x)} cy={toY(brand.y)} r="28" fill="rgba(26,106,255,0.08)" />

          {/* Competitor dots */}
          {competitors.map((c, i) => (
            <g key={i}>
              <circle cx={toX(c.x)} cy={toY(c.y)} r="7" fill="#F3F4F6" stroke="#CBD5E1" strokeWidth="1.5" />
              <text x={toX(c.x)} y={toY(c.y) - 12} textAnchor="middle" fontSize="9" fill="#6B7280" fontFamily="Inter,sans-serif" fontWeight="500">{c.name}</text>
            </g>
          ))}

          {/* Brand dot */}
          <circle cx={toX(brand.x)} cy={toY(brand.y)} r="10" fill="#1A6AFF" stroke="#FFFFFF" strokeWidth="2.5" filter="url(#glow)" />
          <text x={toX(brand.x)} y={toY(brand.y) - 16} textAnchor="middle" fontSize="10" fill="#1A6AFF" fontFamily="Space Grotesk,sans-serif" fontWeight="700">{selectedName || 'Your Brand'}</text>

          {/* Glow filter */}
          <defs>
            <filter id="glow">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          </defs>
        </svg>
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:'20px', marginTop:'12px', paddingTop:'12px', borderTop:'1px solid #F3F4F6' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
          <div style={{ width:'10px', height:'10px', borderRadius:'50%', background:'#1A6AFF' }} />
          <span style={{ fontSize:'11px', color:'#374151', fontWeight:'500' }}>{selectedName || 'Your Brand'} — your position</span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
          <div style={{ width:'10px', height:'10px', borderRadius:'50%', background:'#F3F4F6', border:'1.5px solid #CBD5E1' }} />
          <span style={{ fontSize:'11px', color:'#6B7280' }}>Competitors</span>
        </div>
      </div>
    </div>
  );
}

function PhasePositioning({ data, selectedName }) {
  const raw = data?.whitespaceScore;
  const scoreNum = typeof raw === 'number' ? raw : parseInt(String(raw || '0').replace(/[^0-9]/g, ''), 10) || 0;
  return (
    <>
      <PL num="05" title="Strategic Positioning" />
      <h1 className="r-hero">Your place<br /><span className="r-hero-cyan">in the market.</span></h1>
      <ReadOnly>
        <div className="r-grid-2" style={{ marginBottom:'24px' }}>
          <Card blue>
            <div className="r-card-label">Market Whitespace Score</div>
            <div className="r-big-num" aria-label={`${scoreNum} out of 100`}>{scoreNum}<span className="r-big-num-suf">/100</span></div>
            <div className="r-card-text" style={{ marginTop:'10px' }}>{data?.whitespaceText}</div>
          </Card>
          <Card>
            <div className="r-card-label">How We Win</div>
            <div className="r-card-text" style={{ fontSize:'14px', lineHeight:'1.7' }}>{data?.winStatement}</div>
          </Card>
        </div>

        {/* Positioning Map */}
        {data?.positioningMap && (
          <PositioningMap map={data.positioningMap} selectedName={selectedName} />
        )}

        <div className="r-sec">Competitive Landscape</div>
        {(data?.competitors || []).map((c, i) => (
          <div key={i} className="r-comp" style={{ marginBottom:'8px' }}>
            <div className="r-comp-name-cell"><div className="r-comp-name">{c.name}</div></div>
            <div className="r-comp-cell"><div className="r-comp-cell-label">Vulnerability</div><div className="r-comp-cell-text">{c.vulnerability}</div></div>
            <div className="r-comp-cell"><div className="r-comp-cell-label">Our Advantage</div><div className="r-comp-cell-text">{c.ourPosition}</div></div>
          </div>
        ))}
        <div className="r-divider" />
        <div className="r-sec">Customer Personas</div>
        {(data?.personas || []).map((p, i) => (
          <div key={i} className={`r-persona ${p.type === 'Anti-Persona' ? 'anti' : ''}`}>
            <div className="r-persona-type">{p.type}</div>
            <div className="r-persona-name">{p.name}</div>
            <div className="r-persona-grid">
              {['who','need','fear','trigger'].map(k => (
                <div key={k}><div className="r-persona-item-label">{k}</div><div className="r-persona-item-text">{p[k]}</div></div>
              ))}
            </div>
          </div>
        ))}
        <div className="r-divider" />
        <div className="r-sec">SWOT Analysis</div>
        <div className="r-swot">
          {[{key:'strengths',label:'Strengths',color:'#06B6D4'},{key:'weaknesses',label:'Weaknesses',color:'#6B7280'},{key:'opportunities',label:'Opportunities',color:'#1A6AFF'},{key:'threats',label:'Threats',color:'#EF4444'}].map(({key,label,color}) => (
            <div key={key} className="r-swot-card">
              <div className="r-swot-label" style={{ color }}>{label}</div>
              {(data?.swot?.[key] || []).map((item, i) => <div key={i} className="r-swot-item">{item}</div>)}
            </div>
          ))}
        </div>
      </ReadOnly>
    </>
  );
}

function PhaseExperience({ data }) {
  return (
    <>
      <PL num="06" title="Customer Experience" />
      <h1 className="r-hero">Every moment<br /><span className="r-hero-cyan">your customer feels you.</span></h1>
      <ReadOnly>
        <Card blue style={{ marginBottom:'20px' }}>
          <div className="r-card-label">Signature Brand Moment</div>
          <div className="r-card-text" style={{ fontSize:'15px', lineHeight:'1.75' }}>{data?.signatureMoment}</div>
        </Card>
        <div className="r-sec">The Customer Journey</div>
        <Card style={{ marginBottom:'16px' }}>
          {(data?.journey || []).map((j, i) => (
            <div key={i} className="r-journey-item">
              <div className="r-journey-stage">{j.stage}</div>
              <div><div className="r-journey-cell-label">Touchpoint</div><div className="r-journey-cell-text">{j.touchpoint}</div></div>
              <div><div className="r-journey-cell-label">How We Show Up</div><div className="r-journey-cell-text">{j.brandBehaviour}</div></div>
            </div>
          ))}
        </Card>
        <div className="r-sec">Critical Moments</div>
        <div className="r-grid-3" style={{ marginBottom:'16px' }}>
          {(data?.criticalMoments || []).map((m, i) => (
            <Card key={i}><div className="r-card-label">Moment {i + 1}</div><div className="r-card-text">{m}</div></Card>
          ))}
        </div>
        <Card><div className="r-card-label">Where The Promise Could Break</div><div className="r-card-text">{data?.gapAnalysis}</div></Card>
      </ReadOnly>
    </>
  );
}

function PhaseDigital({ data, selectedName, brief, brandData, logoUrl, session, onSiteHtmlSave, savedBrandId, extSiteHtml, setExtSiteHtml, extSiteState, setExtSiteState }) {
  const [activeTab, setActiveTab]         = useState('website'); // 'website' | 'seo'
  const [_localSiteState, _setLocalSiteState] = useState('idle');
  const [_localSiteHtml,  _setLocalSiteHtml]  = useState(null);
  const siteState   = setExtSiteState ? extSiteState : _localSiteState;
  const setSiteState = setExtSiteState || _setLocalSiteState;
  const siteHtml    = setExtSiteHtml  ? extSiteHtml  : _localSiteHtml;
  const setSiteHtml  = setExtSiteHtml  || _setLocalSiteHtml;
  const [showSitePreview, setShowSitePreview] = useState(false);

  const ENGINE_URL = 'https://brandostro-engine-253902457893.us-central1.run.app';

  const generateWebsite = async () => {
    if (siteState === 'done' && siteHtml) return;
    setSiteState('loading');
    const abortCtrl = new AbortController();
    const abortTimer = setTimeout(() => abortCtrl.abort(), 120000);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 120000); // 120 second timeout
    try {
      const res = await fetch(`${ENGINE_URL}/website`, { signal: abortCtrl.signal,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          name: selectedName,
          brief: brief || '',
          phase00: brandData?.phase00,
          phase01: brandData?.phase01,
          phase03: brandData?.phase03,
          phase04: brandData?.phase04,
          phase05: brandData?.phase05,
          phase07: brandData?.phase07,
          memberId: session?.memberId,
          token: session?.token,
          brandId: savedBrandId || null,
        }),
      });
      clearTimeout(timeout);
      if (res.status === 403) {
        const d = await res.json();
        if (d.existingHtml) {
          clearTimeout(abortTimer);
          setSiteHtml(d.existingHtml);
          setSiteState('done');
          return;
        }
        throw new Error(d.error || 'Site generation not permitted');
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d = await res.json();
      if (!d.success || !d.html) throw new Error(d.error || 'No HTML returned');
      // Inject logo into generated HTML client-side
      // This avoids passing multi-MB base64 to the engine
      let finalHtml = d.html;
      if (logoUrl && finalHtml.includes('</nav>')) {
        // Replace text logo placeholder with actual image in nav
        const logoImg = `<img src="${logoUrl}" alt="${selectedName}" style="height:36px;width:auto;object-fit:contain;display:block;" />`;
        // Inject after first nav brand span if it exists
        finalHtml = finalHtml.replace(
          /(<span[^>]*font-family[^>]*>[^<]*<\/span>)/,
          logoImg
        );
      }
      clearTimeout(abortTimer);
      setSiteHtml(finalHtml);
      setSiteState('done');
      if (onSiteHtmlSave) onSiteHtmlSave(finalHtml);
    } catch (err) {
      clearTimeout(timeout);
      console.error('Website generation error:', err);
      setSiteState('error');
    }
  };

  const downloadHtml = () => {
    if (!siteHtml) return;
    const blob = new Blob([siteHtml], { type: 'text/html; charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `${(selectedName || 'brand').toLowerCase().replace(/\s+/g,'-')}-landing-page.html`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const tabBtn = (id, label) => (
    <button
      onClick={() => setActiveTab(id)}
      style={{
        height:'38px', padding:'0 24px',
        background: activeTab === id ? '#0A0A1A' : 'transparent',
        border: activeTab === id ? 'none' : '1.5px solid #E2E8F0',
        borderRadius:'8px',
        fontFamily:"'Space Grotesk',sans-serif",
        fontSize:'13px', fontWeight:'600',
        color: activeTab === id ? '#FFFFFF' : '#6B7280',
        cursor:'pointer', transition:'all 0.2s',
      }}
    >{label}</button>
  );

  return (
    <>
      <PL num="07" title="Digital Presence" />
      <h1 className="r-hero">Your presence<br /><span className="r-hero-cyan">in the digital world.</span></h1>

      {/* ── TABS ── */}
      <div style={{ display:'flex', gap:'8px', marginBottom:'24px' }}>
        {tabBtn('website', '🌐 Landing Page')}
        {tabBtn('seo', '📈 SEO Foundation')}
      </div>

      {/* ── TAB A: LANDING PAGE ── */}
      {activeTab === 'website' && (
        <div>
          {/* Website preview lightbox */}
          {showSitePreview && siteHtml && (
            <CollateralLightbox title={`${selectedName} — Landing Page Preview`} onClose={() => setShowSitePreview(false)}>
              <div style={{ borderRadius:'12px', overflow:'hidden', border:'1px solid #E2E8F0', boxShadow:'0 4px 20px rgba(0,0,0,0.1)' }}>
                {/* Browser chrome */}
                <div style={{ background:'#F3F4F6', padding:'10px 16px', display:'flex', alignItems:'center', gap:'8px', borderBottom:'1px solid #E2E8F0' }}>
                  <div style={{ display:'flex', gap:'5px' }}>
                    <div style={{ width:'10px', height:'10px', borderRadius:'50%', background:'#EF4444' }} />
                    <div style={{ width:'10px', height:'10px', borderRadius:'50%', background:'#F59E0B' }} />
                    <div style={{ width:'10px', height:'10px', borderRadius:'50%', background:'#22C55E' }} />
                  </div>
                  <div style={{ flex:1, background:'#FFFFFF', borderRadius:'6px', padding:'5px 14px', fontSize:'12px', color:'#6B7280', border:'1px solid #E2E8F0', textAlign:'center' }}>
                    {(selectedName || 'brand').toLowerCase().replace(/\s+/g,'')}.com
                  </div>
                </div>
                {/* Live iframe */}
                <iframe
                  srcDoc={siteHtml}
                  title={`${selectedName} Landing Page`}
                  style={{ width:'100%', height:'520px', border:'none', display:'block' }}
                  sandbox="allow-same-origin allow-scripts allow-popups"
                />
              </div>
              <div style={{ display:'flex', gap:'10px', justifyContent:'center', marginTop:'20px' }}>
                <button onClick={downloadHtml} style={{ height:'44px', padding:'0 28px', background:'#1A6AFF', border:'none', borderRadius:'10px', fontFamily:"'Space Grotesk',sans-serif", fontSize:'14px', fontWeight:'600', color:'#fff', cursor:'pointer' }}>↓ Download HTML</button>
                <button onClick={() => setShowSitePreview(false)} style={{ height:'44px', padding:'0 24px', background:'transparent', border:'1.5px solid #E2E8F0', borderRadius:'10px', fontFamily:"'Space Grotesk',sans-serif", fontSize:'13px', fontWeight:'500', color:'#374151', cursor:'pointer' }}>Close</button>
              </div>
              <div style={{ textAlign:'center', marginTop:'10px', fontSize:'11px', color:'#9CA3AF' }}>
                Clean HTML · No dependencies · Open in any browser · Ready to deploy
              </div>
            </CollateralLightbox>
          )}

          {/* Idle — not generated yet */}
          {siteState === 'idle' && (
            <div style={{ background:'#FFFFFF', border:'1px solid #E2E8F0', borderRadius:'16px', overflow:'hidden', boxShadow:'0 1px 3px rgba(0,0,0,0.05)' }}>
              {/* Browser chrome mockup */}
              <div style={{ background:'#F3F4F6', padding:'10px 16px', display:'flex', alignItems:'center', gap:'8px', borderBottom:'1px solid #E2E8F0' }}>
                <div style={{ display:'flex', gap:'5px' }}>
                  <div style={{ width:'10px', height:'10px', borderRadius:'50%', background:'#EF4444' }} />
                  <div style={{ width:'10px', height:'10px', borderRadius:'50%', background:'#F59E0B' }} />
                  <div style={{ width:'10px', height:'10px', borderRadius:'50%', background:'#22C55E' }} />
                </div>
                <div style={{ flex:1, background:'#FFFFFF', borderRadius:'6px', padding:'5px 14px', fontSize:'12px', color:'#9CA3AF', border:'1px solid #E2E8F0', textAlign:'center' }}>
                  {(selectedName || 'brand').toLowerCase().replace(/\s+/g,'')}.com
                </div>
              </div>
              {/* Placeholder content */}
              <div style={{ padding:'60px 40px', textAlign:'center', background:'#FAFBFF' }}>
                <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:'13px', fontWeight:'600', color:'#9CA3AF', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:'16px' }}>Your Landing Page</div>
                <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:'clamp(24px,3vw,40px)', fontWeight:'700', letterSpacing:'-0.03em', color:'#CBD5E1', marginBottom:'12px', lineHeight:'1.1' }}>{data?.hero?.headline || `${selectedName}`}</div>
                <div style={{ fontSize:'14px', color:'#CBD5E1', marginBottom:'32px', maxWidth:'400px', margin:'0 auto 32px' }}>{data?.hero?.subheadline || 'Brandostro will generate a fully branded landing page.'}</div>
                <button
                  onClick={generateWebsite}
                  style={{ height:'52px', padding:'0 40px', background:'linear-gradient(135deg,#094BCC,#1A6AFF)', border:'none', borderRadius:'10px', fontFamily:"'Space Grotesk',sans-serif", fontSize:'15px', fontWeight:'600', color:'#fff', cursor:'pointer', letterSpacing:'0.02em', boxShadow:'0 6px 24px rgba(26,106,255,0.3)' }}
                >
                  ✦ Generate Landing Page
                </button>
                <div style={{ marginTop:'12px', fontSize:'11px', color:'#9CA3AF' }}>
                  Brandostro builds a bespoke page from your brand strategy · About 15 seconds
                </div>
              </div>
            </div>
          )}

          {/* Loading */}
          {siteState === 'loading' && (
            <div style={{ background:'#FFFFFF', border:'1px solid #E2E8F0', borderRadius:'16px', padding:'64px 40px', display:'flex', flexDirection:'column', alignItems:'center', gap:'18px', boxShadow:'0 1px 3px rgba(0,0,0,0.05)' }}>
              <div style={{ position:'relative', width:'52px', height:'52px' }}>
                <div style={{ position:'absolute', inset:0, borderRadius:'50%', border:'2px solid rgba(26,106,255,0.15)', borderTopColor:'#1A6AFF', animation:'cg-spin 1.4s linear infinite' }} />
                <div style={{ position:'absolute', inset:'9px', borderRadius:'50%', border:'2px solid rgba(6,182,212,0.15)', borderRightColor:'#06B6D4', animation:'cg-spin 0.9s linear infinite reverse' }} />
              </div>
              <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:'17px', fontWeight:'700', color:'#0A0A1A' }}>Building your landing page</div>
              <div style={{ fontSize:'13px', color:'#9CA3AF', textAlign:'center', maxWidth:'360px', lineHeight:'1.7' }}>
                Brandostro is reading your brand strategy, positioning, messaging and visual identity, then writing a bespoke landing page that reflects every dimension of {selectedName}.
              </div>
              <div style={{ fontSize:'11px', color:'#CBD5E1' }}>About 15 seconds</div>
            </div>
          )}

          {/* Done */}
          {siteState === 'done' && siteHtml && (
            <div style={{ background:'#FFFFFF', border:'1px solid #E2E8F0', borderRadius:'16px', overflow:'hidden', boxShadow:'0 2px 12px rgba(0,0,0,0.06)' }}>
              {/* Browser chrome */}
              <div style={{ background:'#F3F4F6', padding:'10px 16px', display:'flex', alignItems:'center', gap:'8px', borderBottom:'1px solid #E2E8F0' }}>
                <div style={{ display:'flex', gap:'5px' }}>
                  <div style={{ width:'10px', height:'10px', borderRadius:'50%', background:'#EF4444' }} />
                  <div style={{ width:'10px', height:'10px', borderRadius:'50%', background:'#F59E0B' }} />
                  <div style={{ width:'10px', height:'10px', borderRadius:'50%', background:'#22C55E' }} />
                </div>
                <div style={{ flex:1, background:'#FFFFFF', borderRadius:'6px', padding:'5px 14px', fontSize:'12px', color:'#6B7280', border:'1px solid #E2E8F0', textAlign:'center' }}>
                  {(selectedName || 'brand').toLowerCase().replace(/\s+/g,'')}.com
                </div>
                <button onClick={() => setShowSitePreview(true)} style={{ height:'28px', padding:'0 14px', background:'#1A6AFF', border:'none', borderRadius:'6px', fontFamily:"'Space Grotesk',sans-serif", fontSize:'11px', fontWeight:'600', color:'#fff', cursor:'pointer', flexShrink:0 }}>⤢ Preview</button>
              </div>
              {/* Live preview iframe — scrollable */}
              <div style={{ position:'relative' }}>
                <iframe
                  srcDoc={siteHtml}
                  title={`${selectedName} Landing Page`}
                  style={{ width:'100%', height:'420px', border:'none', display:'block' }}
                  sandbox="allow-same-origin allow-scripts allow-popups"
                />
                {/* Click overlay to open lightbox */}
                <div
                  onClick={() => setShowSitePreview(true)}
                  style={{ position:'absolute', inset:0, cursor:'pointer', background:'transparent' }}
                  title="Click to preview full size"
                />
              </div>
              {/* Actions */}
              <div style={{ padding:'14px 20px', display:'flex', alignItems:'center', justifyContent:'space-between', borderTop:'1px solid #F3F4F6' }}>
                <div style={{ fontSize:'11px', color:'#9CA3AF' }}>Powered by Brandostro</div>
                <div style={{ display:'flex', gap:'8px' }}>
                  <button onClick={() => setShowSitePreview(true)} style={{ height:'32px', padding:'0 14px', background:'#EFF4FF', border:'1px solid #BFDBFE', borderRadius:'7px', fontFamily:"'Space Grotesk',sans-serif", fontSize:'11px', fontWeight:'600', color:'#1A6AFF', cursor:'pointer' }}>⤢ Full Preview</button>
                  <button onClick={downloadHtml} style={{ height:'32px', padding:'0 14px', background:'#1A6AFF', border:'none', borderRadius:'7px', fontFamily:"'Space Grotesk',sans-serif", fontSize:'11px', fontWeight:'600', color:'#fff', cursor:'pointer' }}>↓ Download HTML</button>
{/* Website regeneration disabled — one generation per session */}
                </div>
              </div>
              {/* Website CTA */}
              <div style={{ padding:'14px 20px', background:'#F8FAFF', borderTop:'1px solid #F3F4F6', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div style={{ fontSize:'12px', color:'#6B7280' }}>Your HTML website is ready. Download it and deploy anywhere.</div>
                <button onClick={() => setView('website')} style={{ height:'30px', padding:'0 16px', background:'transparent', border:'1.5px solid #CBD5E1', borderRadius:'7px', fontFamily:"'Space Grotesk',sans-serif", fontSize:'11px', fontWeight:'600', color:'#374151', cursor:'pointer', flexShrink:0, marginLeft:'16px' }}>Generate Website →</button>
              </div>
            </div>
          )}

          {/* Error */}
          {siteState === 'error' && (
            <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:'12px', padding:'24px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div style={{ fontSize:'13px', color:'#DC2626' }}>Website generation encountered a difficulty. Please try again.</div>
              <button onClick={generateWebsite} style={{ height:'36px', padding:'0 18px', background:'transparent', border:'1.5px solid #FECACA', borderRadius:'7px', fontSize:'12px', fontWeight:'600', color:'#DC2626', cursor:'pointer', flexShrink:0, marginLeft:'16px' }}>Try Again</button>
            </div>
          )}
        </div>
      )}

      {/* ── TAB B: SEO FOUNDATION ── */}
      {activeTab === 'seo' && (
        <ReadOnly>
          <Card blue style={{ marginBottom:'16px' }}>
            <div className="r-card-label">Website Strategy</div>
            <div className="r-card-text" style={{ fontSize:'15px', lineHeight:'1.8' }}>{data?.websiteStrategy}</div>
          </Card>
          <div className="r-grid-2" style={{ marginBottom:'12px' }}>
            <Card label="Meta Title"><div className="r-card-text">{data?.metaTitle}</div></Card>
            <Card label="Meta Description"><div className="r-card-text">{data?.metaDescription}</div></Card>
          </div>
          <Card label="Target Keywords" style={{ marginBottom:'12px' }}>
            <div className="r-kw-row">{(data?.keywords || []).map((k, i) => <span key={i} className="r-kw">{k}</span>)}</div>
          </Card>
          <Card label="Recommended Site Structure">
            <div className="r-card-text">{data?.siteStructure}</div>
          </Card>
        </ReadOnly>
      )}
    </>
  );
}

function PhaseGTM({ data }) {
  return (
    <>
      <PL num="08" title="Go-To-Market" />
      <h1 className="r-hero">The strategy that<br /><span className="r-hero-cyan">takes you to market.</span></h1>
      <ReadOnly>
        <div className="r-icp">
          {['who','why','when','where'].map(k => (
            <div key={k} className="r-icp-card">
              <div className="r-icp-label">{k.toUpperCase()}</div>
              <div className="r-card-label" style={{ marginBottom:'6px' }}>{k==='who'?'Your Customer':k==='why'?'Why They Buy':k==='when'?'When They Buy':'Where To Find Them'}</div>
              <div className="r-card-text">{data?.icp?.[k]}</div>
            </div>
          ))}
        </div>
        <div className="r-grid-2">
          <Card label="Channel Strategy"><div className="r-card-text">{data?.channels}</div></Card>
          <Card blue label="Pricing Strategy"><div className="r-card-text">{data?.pricingStrategy}</div></Card>
        </div>
      </ReadOnly>
    </>
  );
}

function PhaseLaunch({ data, selectedName, brief, onEnd }) {
  const name = selectedName || 'Your Brand';

  // Use engine-generated channels — specific to this brand's market
  // Fall back to empty array gracefully if not yet generated
  const launchChannels = data?.launchChannels || [];
  const successBenchmarks = data?.successBenchmarks || [];

  const launchWeeks = [
    { label:'Days 1–7', title:'Soft Launch and Signal Testing', text:`Activate your inner circle. Share ${name} exclusively with 50–100 trusted contacts, advisors, potential customers, and strategic partners. Collect qualitative feedback. This is your last moment to refine before the world watches.` },
    { label:'Days 8–30', title:'Public Entrance', text:`Execute your public launch sequence across the channels that matter most for ${name}'s market. Every piece of content should position ${name} as a category creator. Lead with the transformation you deliver.` },
    { label:'Days 31–90', title:'Community and Compounding', text:`Convert early adopters into advocates. Begin your content engine — one flagship piece per week demonstrating ${name}'s unique value. Track your benchmarks weekly and adjust with precision.` },
  ];

  return (
    <>
      <PL num="09" title="Launch and Activation" />
      <h1 className="r-hero">{name}'s<br /><span className="r-hero-cyan">entrance into the world.</span></h1>
      <ReadOnly>
        <Card blue style={{ marginBottom:'24px' }}>
          <div className="r-card-label">The Launch Moment</div>
          <div className="r-card-text" style={{ fontSize:'15px', lineHeight:'1.8' }}>{data?.launchMoment}</div>
        </Card>
        <div className="r-grid-2" style={{ marginBottom:'28px' }}>
          <Card label="Founder Narrative"><div className="r-card-text">{data?.founderStory}</div></Card>
          <Card label="Press and Media Angle"><div className="r-card-text">{data?.prNarrative}</div></Card>
        </div>
      </ReadOnly>

      <div className="r-sec">90-Day Launch Sequence</div>
      <div className="r-launch-timeline">
        {launchWeeks.map((w, i) => (
          <div key={i} className="r-launch-week">
            <div className="r-launch-week-label">{w.label}</div>
            <div className="r-launch-week-title">{w.title}</div>
            <div className="r-launch-week-text">{w.text}</div>
          </div>
        ))}
      </div>

      {/* Launch channels — from engine, specific to this brand */}
      {launchChannels.length > 0 && (
        <>
          <div className="r-sec">Launch Channel Strategy</div>
          <div className="r-channel-row">
            {launchChannels.map((c, i) => (
              <div key={i} className="r-channel-item">
                <div className="r-channel-icon" aria-hidden="true">{c.icon}</div>
                <div className="r-channel-name">{c.name}</div>
                <div className="r-channel-role">{c.role}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Success benchmarks — from engine, specific to this brand */}
      {successBenchmarks.length > 0 && (
        <>
          <div className="r-sec">Success Benchmarks, First 90 Days</div>
          <div className="r-metric-grid" style={{ marginBottom:'44px' }}>
            {successBenchmarks.map((m, i) => (
              <div key={i} className="r-metric">
                <div className="r-metric-num">{m.target}</div>
                <div className="r-metric-label">{m.metric}</div>
                <div className="r-metric-sub">{m.timeframe} · {m.context}</div>
              </div>
            ))}
          </div>
        </>
      )}

      <div style={{ textAlign:'center', padding:'44px 0', borderTop:'1px solid #F3F4F6' }}>
        <div style={{ fontFamily:'Space Grotesk,sans-serif', fontSize:'clamp(24px,3vw,36px)', fontWeight:'700', letterSpacing:'-0.03em', color:'#0A0A1A', marginBottom:'10px', lineHeight:'1.2' }}>
          {name} is ready for the world.
        </div>
        <div style={{ fontSize:'14px', color:'#6B7280', lineHeight:'1.6', maxWidth:'420px', margin:'0 auto 32px' }}>
          Your complete brand system — 10 phases of strategy, identity, and activation — is built and ready to deploy.
        </div>
        <button
          onClick={onEnd}
          style={{ height:'52px', padding:'0 40px', background:'linear-gradient(135deg,#094BCC,#1A6AFF)', border:'none', borderRadius:'10px', fontFamily:'Space Grotesk,sans-serif', fontSize:'15px', fontWeight:'600', color:'#fff', cursor:'pointer', letterSpacing:'0.02em', boxShadow:'0 6px 24px rgba(26,106,255,0.3)', transition:'all 0.2s' }}
        >
          Complete Your Brand →
        </button>
        <div style={{ fontSize:'12px', color:'#9CA3AF', marginTop:'16px' }}>Download your website and deploy it anywhere — no platform lock-in.</div>
      </div>
    </>
  );
}
